import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { RAMP_MODE } from './Auth';
import { WHITELABEL_COMPANY } from './Whitelabel';
import { NETWORKS } from './Crypto';
import { ACCOUNT_CURRENCY } from './User';
import { IDataWidget } from './WidgetUser';

/**
 * Si se quiere AGREGAR NUEVO CAMPO se deberá:
 * 1. Agregar su identificador en FIELD
 * 2. Agregarlo en OnboardingFields.fields
 * 3. Especificar a que step pertenece en OnboardingFields.fieldsBySteps
 */

export enum FIELD {
  //ID
  DNI = 'DNI', // ARG
  RUT = 'RUT', // CHL
  CC = 'CC', // COL
  CPF = 'CPF', // BRA
  CUI = 'CUI', // GTM
  RUC = 'RUC', // PAN
  CI = 'CI', // CRI
  CURP = 'CURP', // MEX
  PCN = 'PCN', // PHL
  EMAIL = 'EMAIL',
  PHONE = 'PHONE',
  TYPE_DOCUMENT = 'TYPE_DOCUMENT',
  TYPE_NATIONALITY = 'TYPE_NATIONALITY',
  BIRTH_DATE_PPT = 'BIRTH_DATE_PPT',
  ISSUE_DATE = 'ISSUE_DATE',

  //PERSONAL DATA
  NAME = 'NAME',
  SURNAME = 'SURNAME',
  COMPANY_NAME = 'COMPANY_NAME',
  ADDRESS_STREET = 'ADDRESS_STREET',
  ADDRESS_NUMBER = 'ADDRESS_NUMBER',
  ADDRESS_FLOOR = 'ADDRESS_FLOOR',
  POSTAL_CODE = 'POSTAL_CODE',
  PROVINCE = 'PROVINCE',
  PROVINCE_OC = 'PROVINCE_OC', // Other countries: CRI y PAN
  DISTRICT_OR_CANTON = 'DISTRICT_OR_CANTON', // CRI y PAN
  CITY = 'CITY',
  MARITAL_STATE = 'MARITAL_STATE',
  REGION = 'REGION',
  DEPARTMENT = 'DEPARTMENT',
  STATES = 'STATES',
  MUNICIPALITY = 'MUNICIPALITY',
  NATIONALITY = 'NATIONALITY',
  COMMUNE = 'COMMUNE',
  BIRTH_DATE = 'BIRTH_DATE',
  ACTIVITY_DATA = 'ACTIVITY_DATA',
  SEX = 'SEX',
  TYC = 'TYC',
  FACTA = 'FACTA',
  UIF = 'UIF',
  PEP = 'PEP',

  //BANKS
  BANK_ACCOUNT_CODE = 'BANK_ACCOUNT_CODE',
  BANK_ACCOUNT_CODE_ARG = 'BANK_ACCOUNT_CODE_ARG',
  BANK_ACCOUNT_NAME = 'BANK_ACCOUNT_NAME',
  BANK_CODE = 'BANK_CODE',
  BANK_ACCOUNT_TYPE = 'BANK_ACCOUNT_TYPE',
}

export enum FIELD_DESKTOP {
  //ID
  DNI = 'DNI',
  RUT = 'RUT',
  EMAIL = 'EMAIL',
  PHONE = 'PHONE',

  //PERSONAL DATA
  NAME = 'NAME',
  SURNAME = 'SURNAME',
  COMPANY_NAME = 'COMPANY_NAME',
  SEX = 'SEX',
  NATIONALITY = 'NATIONALITY',
  ADDRESS_STREET = 'ADDRESS_STREET',
  ADDRESS_NUMBER = 'ADDRESS_NUMBER',
  ADDRESS_FLOOR = 'ADDRESS_FLOOR',
  POSTAL_CODE = 'POSTAL_CODE',
  MARITAL_STATE = 'MARITAL_STATE',
  PROVINCE = 'PROVINCE',
  REGION = 'REGION',
  CITY = 'CITY',
  COMMUNE = 'COMMUNE',
  BIRTH_DATE = 'BIRTH_DATE',
  TYC = 'TYC',
  FACTA = 'FACTA',
  UIF = 'UIF',
  PEP = 'PEP',

  //BANKS
  BANK_ACCOUNT_CODE = 'BANK_ACCOUNT_CODE',
  BANK_ACCOUNT_NAME = 'BANK_ACCOUNT_NAME',
  BANK_CODE = 'BANK_CODE',
  BANK_ACCOUNT_TYPE = 'BANK_ACCOUNT_TYPE',
}

export enum ONBOARDING_STEP {
  LEGAL_ID,
  DATA_CONFIRM,
  BANK_INFO,
}

export enum ONBOARDING_FLOW {
  WIDGET,
  TC_FLOW,
}

export interface IRequiredFlowFields {
  [ONBOARDING_FLOW.WIDGET]: FIELD[];
  [ONBOARDING_FLOW.TC_FLOW]: FIELD[];
}

export enum EXCHANGE_COUNTRY {
  ARG = 'ARG',
  CHL = 'CHL',
  COL = 'COL',
  BRA = 'BRA',
  GTM = 'GTM',
  PAN = 'PAN',
  CRI = 'CRI',
  MEX = 'MEX',
  PHL = 'PHL',
}

export enum EXCHANGE {
  ARGENTINA = 'ARGENTINA',
  CHILE = 'CHILE',
  COLOMBIA = 'COLOMBIA',
  BRAZIL = 'BRAZIL',
  GUATEMALA = 'GUATEMALA',
  COSTA_RICA = 'COSTA_RICA',
  PANAMA = 'PANAMA',
  MEXICO = 'MEXICO',
  PHILIPPINES = 'PHILIPPINES',
}

export const exchangeToExchangeCountry: Record<EXCHANGE, EXCHANGE_COUNTRY> = {
  [EXCHANGE.ARGENTINA]: EXCHANGE_COUNTRY.ARG,
  [EXCHANGE.CHILE]: EXCHANGE_COUNTRY.CHL,
  [EXCHANGE.COLOMBIA]: EXCHANGE_COUNTRY.COL,
  [EXCHANGE.BRAZIL]: EXCHANGE_COUNTRY.BRA,
  [EXCHANGE.GUATEMALA]: EXCHANGE_COUNTRY.GTM,
  [EXCHANGE.PANAMA]: EXCHANGE_COUNTRY.PAN,
  [EXCHANGE.COSTA_RICA]: EXCHANGE_COUNTRY.CRI,
  [EXCHANGE.MEXICO]: EXCHANGE_COUNTRY.MEX,
  [EXCHANGE.PHILIPPINES]: EXCHANGE_COUNTRY.PHL,
};

export enum PAYMENT_METHOD {
  TRANSFIYA = 'TRANSFIYA',
  NEQUI = 'NEQUI',
  INSTAPAY = 'INSTAPAY',
}

export interface OnboardingField {
  validators: ValidatorFn[];
  isPersonal: boolean;
  isCompany: boolean;
  defaultValue: any;

  /** Si no está seteado. El Field le pertenecerá a todos los paises */
  supportedCountries?: EXCHANGE_COUNTRY[];
}

export interface IdataUser {
  cuit: string;
  phoneNumber: string;
  email: string;
  exchangeCountry?: EXCHANGE_COUNTRY;
  typeDocument?: 'CC' | 'CE' | 'PPT';
  isPPT?: boolean;
  firstName?: string;
  lastName?: string;
  birthDate?: string;
  checkId?: string;
  previewCbu?: string;
}

export interface IBank {
  value: string;
  type: string;
  label: string;
  checking: string;
  savings: string;
}

export class OnboardingFields {
  static fields: { [field: string]: OnboardingField } = {
    //ID
    [FIELD.DNI]: {
      validators: [Validators.maxLength(8), this.minLengthDni, this.dniValidator],
      isCompany: true,
      isPersonal: true,
      defaultValue: '',
      supportedCountries: [EXCHANGE_COUNTRY.ARG],
    },
    [FIELD.RUT]: {
      validators: [Validators.required, this.validateRUT],
      isCompany: false,
      isPersonal: true,
      defaultValue: '',
      supportedCountries: [EXCHANGE_COUNTRY.CHL],
    },
    [FIELD.CC]: {
      validators: [Validators.required, this.minLengthCC],
      isCompany: false,
      isPersonal: true,
      defaultValue: '',
      supportedCountries: [EXCHANGE_COUNTRY.COL],
    },
    [FIELD.CPF]: {
      validators: [Validators.required, this.minLengthCPF],
      isCompany: false,
      isPersonal: true,
      defaultValue: '',
      supportedCountries: [EXCHANGE_COUNTRY.BRA],
    },
    [FIELD.CUI]: {
      validators: [Validators.required, this.validateCui],
      isCompany: false,
      isPersonal: true,
      defaultValue: '',
      supportedCountries: [EXCHANGE_COUNTRY.GTM],
    },
    [FIELD.RUC]: {
      validators: [Validators.required, this.minLengthRuc],
      isCompany: false,
      isPersonal: true,
      defaultValue: '',
      supportedCountries: [EXCHANGE_COUNTRY.PAN],
    },
    [FIELD.CI]: {
      validators: [Validators.required, this.minLengthCI],
      isCompany: false,
      isPersonal: true,
      defaultValue: '',
      supportedCountries: [EXCHANGE_COUNTRY.CRI],
    },
    [FIELD.CURP]: {
      validators: [Validators.required, this.validateCurp],
      isCompany: false,
      isPersonal: true,
      defaultValue: '',
      supportedCountries: [EXCHANGE_COUNTRY.MEX],
    },
    [FIELD.PCN]: {
      validators: [Validators.required],
      isCompany: false,
      isPersonal: true,
      defaultValue: '',
      supportedCountries: [EXCHANGE_COUNTRY.PHL],
    },
    [FIELD.PHONE]: {
      validators: [Validators.required, this.phoneLengthValidator, Validators.maxLength(10)],
      isCompany: false,
      isPersonal: true,
      defaultValue: '',
    },
    [FIELD.EMAIL]: {
      validators: [
        Validators.required,
        Validators.email,
        this.emailValidator,
        Validators.maxLength(50),
      ],
      isCompany: false,
      isPersonal: true,
      defaultValue: '',
    },
    [FIELD.TYPE_DOCUMENT]: {
      validators: [Validators.required],
      isCompany: false,
      isPersonal: true,
      defaultValue: '',
      supportedCountries: [EXCHANGE_COUNTRY.COL],
    },
    [FIELD.TYPE_NATIONALITY]: {
      validators: [],
      isCompany: false,
      isPersonal: true,
      defaultValue: '',
      supportedCountries: [EXCHANGE_COUNTRY.PAN, EXCHANGE_COUNTRY.CRI],
    },

    //PERSONAL DATA
    [FIELD.TYC]: {
      validators: [Validators.requiredTrue],
      isCompany: true,
      isPersonal: true,
      defaultValue: false,
    },

    [FIELD.NAME]: {
      validators: [Validators.required, Validators.maxLength(40), this.invalidCharactersValidator],
      isCompany: false,
      isPersonal: true,
      defaultValue: '',
    },
    [FIELD.SURNAME]: {
      validators: [Validators.required, Validators.maxLength(40), this.invalidCharactersValidator],
      isCompany: false,
      isPersonal: true,
      defaultValue: '',
    },
    [FIELD.COMPANY_NAME]: {
      validators: [Validators.required],
      isCompany: true,
      isPersonal: false,
      defaultValue: '',
    },
    [FIELD.ADDRESS_STREET]: {
      validators: [Validators.required, this.minLengthStreet, Validators.maxLength(40)],
      isCompany: true,
      isPersonal: true,
      defaultValue: '',
    },
    [FIELD.ADDRESS_NUMBER]: {
      validators: [Validators.required],
      isCompany: true,
      isPersonal: true,
      defaultValue: '',
      supportedCountries: [
        EXCHANGE_COUNTRY.CHL,
        EXCHANGE_COUNTRY.ARG,
        EXCHANGE_COUNTRY.BRA,
        EXCHANGE_COUNTRY.MEX,
        EXCHANGE_COUNTRY.PHL,
      ],
    },
    [FIELD.ADDRESS_FLOOR]: {
      validators: [Validators.maxLength(20)],
      isCompany: true,
      isPersonal: true,
      defaultValue: '',
      supportedCountries: [EXCHANGE_COUNTRY.CHL, EXCHANGE_COUNTRY.ARG, EXCHANGE_COUNTRY.BRA],
    },
    [FIELD.POSTAL_CODE]: {
      validators: [Validators.required],
      isCompany: true,
      isPersonal: true,
      defaultValue: '',
      supportedCountries: [
        EXCHANGE_COUNTRY.CHL,
        EXCHANGE_COUNTRY.ARG,
        EXCHANGE_COUNTRY.BRA,
        EXCHANGE_COUNTRY.GTM,
        EXCHANGE_COUNTRY.PAN,
        EXCHANGE_COUNTRY.CRI,
        EXCHANGE_COUNTRY.MEX,
        EXCHANGE_COUNTRY.PHL,
      ],
    },
    [FIELD.BIRTH_DATE]: {
      validators: [Validators.required, this.requiredDate],
      isCompany: false,
      isPersonal: true,
      defaultValue: null,
    },
    [FIELD.ACTIVITY_DATA]: {
      validators: [Validators.required],
      isCompany: false,
      isPersonal: true,
      defaultValue: null,
      supportedCountries: [
        EXCHANGE_COUNTRY.COL,
        EXCHANGE_COUNTRY.BRA,
        EXCHANGE_COUNTRY.GTM,
        EXCHANGE_COUNTRY.PAN,
        EXCHANGE_COUNTRY.CRI,
        EXCHANGE_COUNTRY.CHL,
        EXCHANGE_COUNTRY.MEX,
        EXCHANGE_COUNTRY.PHL,
      ],
    },
    [FIELD.SEX]: {
      validators: [Validators.required],
      isCompany: false,
      isPersonal: true,
      defaultValue: undefined,
    },
    [FIELD.MARITAL_STATE]: {
      validators: [Validators.required],
      isCompany: false,
      isPersonal: true,
      defaultValue: undefined,
    },
    [FIELD.PROVINCE]: {
      validators: [Validators.required],
      isCompany: true,
      isPersonal: true,
      defaultValue: undefined,
      supportedCountries: [EXCHANGE_COUNTRY.ARG],
    },
    [FIELD.PROVINCE_OC]: {
      validators: [Validators.required],
      isCompany: false,
      isPersonal: true,
      defaultValue: undefined,
      supportedCountries: [EXCHANGE_COUNTRY.PAN, EXCHANGE_COUNTRY.CRI],
    },
    [FIELD.REGION]: {
      validators: [Validators.required],
      isCompany: false,
      isPersonal: true,
      defaultValue: '',
      supportedCountries: [EXCHANGE_COUNTRY.CHL],
    },
    [FIELD.DEPARTMENT]: {
      validators: [Validators.required],
      isCompany: false,
      isPersonal: true,
      defaultValue: '',
      supportedCountries: [EXCHANGE_COUNTRY.COL, EXCHANGE_COUNTRY.GTM],
    },
    [FIELD.STATES]: {
      validators: [Validators.required],
      isCompany: false,
      isPersonal: true,
      defaultValue: '',
      supportedCountries: [EXCHANGE_COUNTRY.BRA, EXCHANGE_COUNTRY.MEX],
    },
    [FIELD.NATIONALITY]: {
      validators: [Validators.required],
      isCompany: true,
      isPersonal: true,
      defaultValue: undefined,
    },
    [FIELD.CITY]: {
      validators: [Validators.required],
      isCompany: true,
      isPersonal: true,
      defaultValue: '',
      supportedCountries: [EXCHANGE_COUNTRY.ARG],
    },
    [FIELD.MUNICIPALITY]: {
      validators: [Validators.required],
      isCompany: false,
      isPersonal: true,
      defaultValue: '',
      supportedCountries: [
        EXCHANGE_COUNTRY.COL,
        EXCHANGE_COUNTRY.BRA,
        EXCHANGE_COUNTRY.GTM,
        EXCHANGE_COUNTRY.MEX,
      ],
    },
    [FIELD.COMMUNE]: {
      validators: [Validators.required],
      isCompany: false,
      isPersonal: true,
      defaultValue: '',
      supportedCountries: [EXCHANGE_COUNTRY.CHL],
    },
    [FIELD.DISTRICT_OR_CANTON]: {
      validators: [Validators.required],
      isCompany: false,
      isPersonal: true,
      defaultValue: '',
      supportedCountries: [EXCHANGE_COUNTRY.PAN, EXCHANGE_COUNTRY.CRI],
    },
    [FIELD.ISSUE_DATE]: {
      validators: [],
      isCompany: false,
      isPersonal: true,
      defaultValue: null,
      supportedCountries: [EXCHANGE_COUNTRY.COL],
    },
    [FIELD.BIRTH_DATE_PPT]: {
      validators: [],
      isCompany: false,
      isPersonal: true,
      defaultValue: null,
      supportedCountries: [EXCHANGE_COUNTRY.COL],
    },
    [FIELD.FACTA]: {
      validators: [Validators.requiredTrue],
      isCompany: false,
      isPersonal: true,
      defaultValue: false,
    },
    [FIELD.UIF]: {
      validators: [Validators.required],
      isCompany: false,
      isPersonal: true,
      defaultValue: undefined,
    },
    [FIELD.PEP]: {
      validators: [Validators.required],
      isCompany: false,
      isPersonal: true,
      defaultValue: undefined,
    },

    //BANKS
    [FIELD.BANK_ACCOUNT_CODE]: {
      validators: [Validators.required],
      isCompany: false,
      isPersonal: true,
      defaultValue: '',
    },
    [FIELD.BANK_ACCOUNT_CODE_ARG]: {
      validators: [Validators.required],
      isCompany: false,
      isPersonal: true,
      defaultValue: undefined,
      supportedCountries: [EXCHANGE_COUNTRY.ARG],
    },
    [FIELD.BANK_ACCOUNT_NAME]: {
      validators: [
        Validators.required,
        Validators.minLength(3),
        this.invalidCharactersValidator,
        Validators.maxLength(50),
      ],
      isCompany: false,
      isPersonal: true,
      defaultValue: undefined,
    },
    [FIELD.BANK_ACCOUNT_TYPE]: {
      validators: [Validators.required],
      isCompany: true,
      isPersonal: true,
      defaultValue: undefined,
      supportedCountries: [
        EXCHANGE_COUNTRY.CHL,
        EXCHANGE_COUNTRY.COL,
        EXCHANGE_COUNTRY.GTM,
        EXCHANGE_COUNTRY.PAN,
        EXCHANGE_COUNTRY.CRI,
        EXCHANGE_COUNTRY.MEX,
        EXCHANGE_COUNTRY.PHL,
      ],
    },
    [FIELD.BANK_CODE]: {
      validators: [Validators.required],
      isCompany: true,
      isPersonal: true,
      defaultValue: undefined,
      supportedCountries: [
        EXCHANGE_COUNTRY.CHL,
        EXCHANGE_COUNTRY.COL,
        EXCHANGE_COUNTRY.GTM,
        EXCHANGE_COUNTRY.PAN,
        EXCHANGE_COUNTRY.CRI,
        EXCHANGE_COUNTRY.MEX,
        EXCHANGE_COUNTRY.PHL,
      ],
    },
  };

  static fieldsBySteps: { [step: number]: IRequiredFlowFields } = {
    [ONBOARDING_STEP.LEGAL_ID]: {
      [ONBOARDING_FLOW.WIDGET]: [
        FIELD.DNI,
        FIELD.RUT,
        FIELD.CC,
        FIELD.CPF,
        FIELD.CUI,
        FIELD.RUC,
        FIELD.CI,
        FIELD.CURP,
        FIELD.PCN,
        FIELD.EMAIL,
        FIELD.TYPE_DOCUMENT,
        FIELD.TYPE_NATIONALITY,
        FIELD.BIRTH_DATE_PPT,
        FIELD.ISSUE_DATE,
        FIELD.BANK_ACCOUNT_CODE_ARG,
        FIELD.NAME,
        FIELD.SURNAME,
      ],
      [ONBOARDING_FLOW.TC_FLOW]: [FIELD.DNI, FIELD.PHONE, FIELD.BANK_ACCOUNT_CODE_ARG],
    },
    [ONBOARDING_STEP.DATA_CONFIRM]: {
      [ONBOARDING_FLOW.WIDGET]: [
        FIELD.NAME,
        FIELD.SURNAME,
        FIELD.COMPANY_NAME,
        FIELD.ADDRESS_STREET,
        FIELD.ADDRESS_NUMBER,
        FIELD.ADDRESS_FLOOR,

        FIELD.BIRTH_DATE,
        FIELD.ACTIVITY_DATA,
        FIELD.POSTAL_CODE,
        FIELD.SEX,
        FIELD.MARITAL_STATE,
        FIELD.PROVINCE,
        FIELD.PROVINCE_OC,
        FIELD.REGION,
        FIELD.DEPARTMENT,
        FIELD.STATES,
        FIELD.NATIONALITY,
        FIELD.CITY,
        FIELD.MUNICIPALITY,
        FIELD.COMMUNE,
        FIELD.DISTRICT_OR_CANTON,
        FIELD.TYC,
        FIELD.FACTA,
        FIELD.UIF,
        FIELD.PEP,
        FIELD.PHONE,
      ],
      [ONBOARDING_FLOW.TC_FLOW]: [
        FIELD.NAME,
        FIELD.SURNAME,
        FIELD.COMPANY_NAME,
        FIELD.ADDRESS_STREET,
        FIELD.ADDRESS_NUMBER,
        FIELD.ADDRESS_FLOOR,
        FIELD.CITY,
        FIELD.BIRTH_DATE,
        FIELD.POSTAL_CODE,
        FIELD.SEX,
        FIELD.MARITAL_STATE,
        FIELD.PROVINCE,
        FIELD.NATIONALITY,
        FIELD.TYC,
        FIELD.FACTA,
        FIELD.UIF,
        FIELD.PEP,
      ],
    },
    [ONBOARDING_STEP.BANK_INFO]: {
      [ONBOARDING_FLOW.WIDGET]: [
        FIELD.BANK_ACCOUNT_CODE,
        FIELD.BANK_ACCOUNT_TYPE,
        FIELD.BANK_CODE,
        FIELD.BANK_ACCOUNT_NAME,
      ],
      [ONBOARDING_FLOW.TC_FLOW]: [FIELD.BANK_ACCOUNT_CODE, FIELD.BANK_ACCOUNT_NAME],
    },
  };

  static getByStep(
    step: ONBOARDING_STEP,
    flow: ONBOARDING_FLOW,
    requiredCountry?: EXCHANGE_COUNTRY,
  ): FIELD[] {
    const stepFieldNames = this.fieldsBySteps[step];
    const flowFieldNames = stepFieldNames ? stepFieldNames[flow] : [];
    return flowFieldNames.filter((fieldName) => {
      const field = this.fields[fieldName];

      if (field.supportedCountries?.length && !field.supportedCountries.includes(requiredCountry))
        return false;

      return field.isPersonal;
    });
  }

  static loadControlsToFormGroup(
    formGroup: FormGroup,
    step: ONBOARDING_STEP,
    flow: ONBOARDING_FLOW,
    country?: EXCHANGE_COUNTRY,
    widgetUser?: IDataWidget,
  ) {
    OnboardingFields.getByStep(step, flow, country).forEach((fieldName) => {
      const field = OnboardingFields.fields[fieldName];
      const control = new FormControl(field.defaultValue, field.validators);
      if (fieldName === FIELD.BANK_ACCOUNT_CODE) {
        if (country === EXCHANGE_COUNTRY.CHL) {
          control.addValidators(this.CBUAliasValidatorChl);
        } else if (country === EXCHANGE_COUNTRY.COL) {
          control.addValidators(this.CBUAliasValidatorCol);
        } else if (country === EXCHANGE_COUNTRY.BRA) {
          control.addValidators(this.CBUAliasValidatorBra);
        } else if (country === EXCHANGE_COUNTRY.CRI) {
          control.addValidators(this.CBUAliasValidatorCri);
        } else if (country === EXCHANGE_COUNTRY.PAN) {
          control.addValidators(this.CBUAliasValidatorPan);
        } else if (country === EXCHANGE_COUNTRY.ARG) {
          control.addValidators(this.CBUAliasValidator);
        }
        control.updateValueAndValidity();
      }

      if (fieldName === FIELD.BANK_ACCOUNT_NAME) {
        if (country === EXCHANGE_COUNTRY.CHL || country === EXCHANGE_COUNTRY.COL) {
          control.clearValidators();
          control.updateValueAndValidity();
        }
      }
      if (fieldName === FIELD.BANK_CODE) {
        if (country === EXCHANGE_COUNTRY.COL) {
          control.addValidators(this.bankCodeValidator);
          control.updateValueAndValidity();
        }
      }

      if (fieldName === FIELD.POSTAL_CODE) {
        if (country === EXCHANGE_COUNTRY.CHL) {
          control.addValidators(this.minLengthPostalCodeChl);
        } else if (country === EXCHANGE_COUNTRY.COL) {
          control.addValidators(this.minLengthPostalCodeCol);
        } else if (country === EXCHANGE_COUNTRY.BRA) {
          control.addValidators(this.minLengthPostalCodeBra);
        } else if (country === EXCHANGE_COUNTRY.GTM) {
          control.addValidators(this.minLengthPostalCodeGTM);
        } else if (country === EXCHANGE_COUNTRY.MEX) {
          control.addValidators(this.minLengthPostalCodeMEX);
        } else {
          control.addValidators(this.minLengthPostalCode);
        }
        control.updateValueAndValidity();
      }

      if (country === EXCHANGE_COUNTRY.CHL) {
        if (fieldName === FIELD.NAME || fieldName === FIELD.SURNAME) {
          control.addValidators(this.minLengthName);
          control.updateValueAndValidity();
        }
      }

      if ([FIELD.UIF, FIELD.PEP].includes(fieldName) && flow !== 1) {
        control.addValidators(this.requiredFalse);
        control.updateValueAndValidity();
      }

      if (fieldName === FIELD.ADDRESS_STREET && country === EXCHANGE_COUNTRY.COL) {
        control.addValidators(this.addressValidatorCol);
        control.updateValueAndValidity();
      }

      if (fieldName === FIELD.PHONE) {
        if (widgetUser) {
          control.clearValidators();
          const maxLenght = country === EXCHANGE_COUNTRY.BRA ? 11 : 10;
          if (country === EXCHANGE_COUNTRY.BRA) {
            control.addValidators([
              Validators.required,
              this.phoneLengthValidatorBra,
              Validators.maxLength(maxLenght),
            ]);
          } else {
            control.addValidators([
              Validators.required,
              this.phoneLengthValidator,
              Validators.maxLength(maxLenght),
            ]);
          }
        } else {
          control.clearValidators();
        }
        control.updateValueAndValidity();
      }
      if (
        step === ONBOARDING_STEP.LEGAL_ID &&
        (fieldName === FIELD.NAME || fieldName === FIELD.SURNAME)
      ) {
        control.clearValidators();
      }

      formGroup.addControl(fieldName, control);
    });
  }

  static minLengthDni(control: AbstractControl): { [key: string]: boolean } | null {
    return control.value && control.value.length !== 8 ? { badDNI: true } : null;
  }

  static minLengthCuit(control: AbstractControl): { [key: string]: boolean } | null {
    return control.value && control.value.length !== 11 ? { cuilMin: true } : null;
  }

  static minLengthCC(control: AbstractControl): { [key: string]: boolean } | null {
    return control.value && control.value.length < 6 ? { ccMin: true } : null;
  }

  static minLengthCPF(control: AbstractControl): { [key: string]: boolean } | null {
    return control.value && control.value.length < 11 ? { cpfMin: true } : null;
  }

  static minLengthCI(control: AbstractControl): { [key: string]: boolean } | null {
    return control.value && control.value.length < 9 ? { ciMin: true } : null;
  }

  static minLengthRuc(control: AbstractControl): { [key: string]: boolean } | null {
    return control.value && control.value.length < 7 ? { rucMin: true } : null;
  }

  static dniValidator(control: AbstractControl): { [key: string]: boolean } | null {
    const value = control.value?.toString();
    if (value && (value.length !== 8 || isNaN(value))) {
      return { badDNI: true };
    }
    return null;
  }

  static getCuilSufix(control: AbstractControl): { [key: string]: boolean } | null {
    const cuit = control.value?.toString();
    if (!cuit) return null;
    let acumulado = 0;
    const digitos = cuit.split('');
    const digito = Number(digitos.pop());

    if (cuit.startsWith('99')) return null;

    const prefixes = ['20', '23', '24', '27', '30', '33', '34'];

    const prefix = cuit.slice(0, 2);
    if (!prefixes.includes(prefix)) return { badCUIT: true };

    const restOfDigits = cuit.slice(2, 10);
    if (/^0+$/.test(restOfDigits)) return { badCUIT: true };

    for (let i = 0; i < digitos.length; i++) {
      acumulado += parseInt(digitos[9 - i], 10) * (2 + (i % 6));
    }

    let verif = 11 - (acumulado % 11);
    if (verif === 11) {
      verif = 0;
    }
    if (verif !== digito) {
      return { badCUIT: true };
    }
    return null;
  }

  static validateRUT(control: AbstractControl): { [key: string]: boolean } | null {
    let RUT = control.value;
    const mutipliers = [2, 3, 4, 5, 6, 7];
    let productsSum = 0;

    if (![8, 9].includes(RUT.length)) return { badRUTLength: true };

    let digits = RUT.toString().toUpperCase().split('');
    let lastDigit = digits.pop().toString();

    digits.reverse().forEach((digit, index) => {
      const multiplier = mutipliers[index >= 6 ? index - 6 : index];
      productsSum += multiplier * digit;
    });

    let verifier = (11 - (productsSum - 11 * Math.trunc(productsSum / 11))).toString();

    if (verifier === '11') verifier = '0';
    else if (verifier === '10') verifier = 'K';

    if (lastDigit !== verifier) return { badRUT: true };

    return null;
  }

  static validateCui(control: AbstractControl): { [key: string]: boolean } | null {
    const value = control.value;

    if (value && value.length === 13) {
      const lastFourDigits = value.slice(-4);

      if (validMunicipalityCodes.includes(lastFourDigits)) {
        return null;
      } else {
        return { invalidMunicipalityCode: true };
      }
    }

    return null;
  }

  static validateCurp(control: AbstractControl): { [key: string]: boolean } | null {
    const curpPattern = /^[A-Z]{1}[AEIOU]{1}[A-Z]{2}\d{6}[HM]{1}[A-Z]{2}[A-Z]{3}[A-Z\d]{1}\d{1}$/i;

    if (!control.value) {
      return null;
    }

    return curpPattern.test(control.value) ? null : { invalidCurp: true };
  }

  static validPhilSysId(control: AbstractControl): { [key: string]: boolean } | null {
    const value: string = control.value;
    if (!value) return null;

    const validFormats = [/^[ndfp]\d{10}$/i, /^[a-z]\d{7}[a-z]$/i, /^\d{13,16}$/];

    const isValid = validFormats.some((regex) => regex.test(value));

    return isValid ? null : { invalidNationalId: true };
  }

  static phoneLengthValidator(control: AbstractControl): { [key: string]: boolean } | null {
    return control.value && control.value.toString().length < 10 ? { phoneShort: true } : null;
  }

  static phoneLengthValidatorBra(control: AbstractControl): { [key: string]: boolean } | null {
    return control.value && control.value.toString().length < 11 ? { phoneShortBra: true } : null;
  }

  static phoneValidatorPHL(control: AbstractControl): { [key: string]: boolean } | null {
    const value = control.value?.trim();
    const localFormatRegex = /^09\d{9}$/;

    return value && !localFormatRegex.test(value) ? { invalidPhonePHL: true } : null;
  }

  static invalidCharactersValidator(control: AbstractControl): { [key: string]: boolean } | null {
    const invalidCharsRegex =
      /^([ 'A-Za-z\u00AA\u00B5\u00BA\u00C0-\u00D6\u2018\u00D8-\u00F6\u00F8-\u02C1\u02C6-\u02D1\u02E0-\u02E4\u02EC\u02EE\u0370-\u0374\u0376\u0377\u037A-\u037D\u037F\u0386\u0388-\u038A\u038C\u038E-\u03A1\u03A3-\u03F5\u03F7-\u0481\u048A-\u052F\u0531-\u0556\u0559\u0561-\u0587\u05D0-\u05EA\u05F0-\u05F2\u0620-\u064A\u066E\u066F\u0671-\u06D3\u06D5\u06E5\u06E6\u06EE\u06EF\u06FA-\u06FC\u06FF\u0710\u0712-\u072F\u074D-\u07A5\u07B1\u07CA-\u07EA\u07F4\u07F5\u07FA\u0800-\u0815\u081A\u0824\u0828\u0840-\u0858\u08A0-\u08B4\u0904-\u0939\u093D\u0950\u0958-\u0961\u0971-\u0980\u0985-\u098C\u098F\u0990\u0993-\u09A8\u09AA-\u09B0\u09B2\u09B6-\u09B9\u09BD\u09CE\u09DC\u09DD\u09DF-\u09E1\u09F0\u09F1\u0A05-\u0A0A\u0A0F\u0A10\u0A13-\u0A28\u0A2A-\u0A30\u0A32\u0A33\u0A35\u0A36\u0A38\u0A39\u0A59-\u0A5C\u0A5E\u0A72-\u0A74\u0A85-\u0A8D\u0A8F-\u0A91\u0A93-\u0AA8\u0AAA-\u0AB0\u0AB2\u0AB3\u0AB5-\u0AB9\u0ABD\u0AD0\u0AE0\u0AE1\u0AF9\u0B05-\u0B0C\u0B0F\u0B10\u0B13-\u0B28\u0B2A-\u0B30\u0B32\u0B33\u0B35-\u0B39\u0B3D\u0B5C\u0B5D\u0B5F-\u0B61\u0B71\u0B83\u0B85-\u0B8A\u0B8E-\u0B90\u0B92-\u0B95\u0B99\u0B9A\u0B9C\u0B9E\u0B9F\u0BA3\u0BA4\u0BA8-\u0BAA\u0BAE-\u0BB9\u0BD0\u0C05-\u0C0C\u0C0E-\u0C10\u0C12-\u0C28\u0C2A-\u0C39\u0C3D\u0C58-\u0C5A\u0C60\u0C61\u0C85-\u0C8C\u0C8E-\u0C90\u0C92-\u0CA8\u0CAA-\u0CB3\u0CB5-\u0CB9\u0CBD\u0CDE\u0CE0\u0CE1\u0CF1\u0CF2\u0D05-\u0D0C\u0D0E-\u0D10\u0D12-\u0D3A\u0D3D\u0D4E\u0D5F-\u0D61\u0D7A-\u0D7F\u0D85-\u0D96\u0D9A-\u0DB1\u0DB3-\u0DBB\u0DBD\u0DC0-\u0DC6\u0E01-\u0E30\u0E32\u0E33\u0E40-\u0E46\u0E81\u0E82\u0E84\u0E87\u0E88\u0E8A\u0E8D\u0E94-\u0E97\u0E99-\u0E9F\u0EA1-\u0EA3\u0EA5\u0EA7\u0EAA\u0EAB\u0EAD-\u0EB0\u0EB2\u0EB3\u0EBD\u0EC0-\u0EC4\u0EC6\u0EDC-\u0EDF\u0F00\u0F40-\u0F47\u0F49-\u0F6C\u0F88-\u0F8C\u1000-\u102A\u103F\u1050-\u1055\u105A-\u105D\u1061\u1065\u1066\u106E-\u1070\u1075-\u1081\u108E\u10A0-\u10C5\u10C7\u10CD\u10D0-\u10FA\u10FC-\u1248\u124A-\u124D\u1250-\u1256\u1258\u125A-\u125D\u1260-\u1288\u128A-\u128D\u1290-\u12B0\u12B2-\u12B5\u12B8-\u12BE\u12C0\u12C2-\u12C5\u12C8-\u12D6\u12D8-\u1310\u1312-\u1315\u1318-\u135A\u1380-\u138F\u13A0-\u13F5\u13F8-\u13FD\u1401-\u166C\u166F-\u167F\u1681-\u169A\u16A0-\u16EA\u16F1-\u16F8\u1700-\u170C\u170E-\u1711\u1720-\u1731\u1740-\u1751\u1760-\u176C\u176E-\u1770\u1780-\u17B3\u17D7\u17DC\u1820-\u1877\u1880-\u18A8\u18AA\u18B0-\u18F5\u1900-\u191E\u1950-\u196D\u1970-\u1974\u1980-\u19AB\u19B0-\u19C9\u1A00-\u1A16\u1A20-\u1A54\u1AA7\u1B05-\u1B33\u1B45-\u1B4B\u1B83-\u1BA0\u1BAE\u1BAF\u1BBA-\u1BE5\u1C00-\u1C23\u1C4D-\u1C4F\u1C5A-\u1C7D\u1CE9-\u1CEC\u1CEE-\u1CF1\u1CF5\u1CF6\u1D00-\u1DBF\u1E00-\u1F15\u1F18-\u1F1D\u1F20-\u1F45\u1F48-\u1F4D\u1F50-\u1F57\u1F59\u1F5B\u1F5D\u1F5F-\u1F7D\u1F80-\u1FB4\u1FB6-\u1FBC\u1FBE\u1FC2-\u1FC4\u1FC6-\u1FCC\u1FD0-\u1FD3\u1FD6-\u1FDB\u1FE0-\u1FEC\u1FF2-\u1FF4\u1FF6-\u1FFC\u2071\u207F\u2090-\u209C\u2102\u2107\u210A-\u2113\u2115\u2119-\u211D\u2124\u2126\u2128\u212A-\u212D\u212F-\u2139\u213C-\u213F\u2145-\u2149\u214E\u2183\u2184\u2C00-\u2C2E\u2C30-\u2C5E\u2C60-\u2CE4\u2CEB-\u2CEE\u2CF2\u2CF3\u2D00-\u2D25\u2D27\u2D2D\u2D30-\u2D67\u2D6F\u2D80-\u2D96\u2DA0-\u2DA6\u2DA8-\u2DAE\u2DB0-\u2DB6\u2DB8-\u2DBE\u2DC0-\u2DC6\u2DC8-\u2DCE\u2DD0-\u2DD6\u2DD8-\u2DDE\u2E2F\u3005\u3006\u3031-\u3035\u303B\u303C\u3041-\u3096\u309D-\u309F\u30A1-\u30FA\u30FC-\u30FF\u3105-\u312D\u3131-\u318E\u31A0-\u31BA\u31F0-\u31FF\u3400-\u4DB5\u4E00-\u9FD5\uA000-\uA48C\uA4D0-\uA4FD\uA500-\uA60C\uA610-\uA61F\uA62A\uA62B\uA640-\uA66E\uA67F-\uA69D\uA6A0-\uA6E5\uA717-\uA71F\uA722-\uA788\uA78B-\uA7AD\uA7B0-\uA7B7\uA7F7-\uA801\uA803-\uA805\uA807-\uA80A\uA80C-\uA822\uA840-\uA873\uA882-\uA8B3\uA8F2-\uA8F7\uA8FB\uA8FD\uA90A-\uA925\uA930-\uA946\uA960-\uA97C\uA984-\uA9B2\uA9CF\uA9E0-\uA9E4\uA9E6-\uA9EF\uA9FA-\uA9FE\uAA00-\uAA28\uAA40-\uAA42\uAA44-\uAA4B\uAA60-\uAA76\uAA7A\uAA7E-\uAAAF\uAAB1\uAAB5\uAAB6\uAAB9-\uAABD\uAAC0\uAAC2\uAADB-\uAADD\uAAE0-\uAAEA\uAAF2-\uAAF4\uAB01-\uAB06\uAB09-\uAB0E\uAB11-\uAB16\uAB20-\uAB26\uAB28-\uAB2E\uAB30-\uAB5A\uAB5C-\uAB65\uAB70-\uABE2\uAC00-\uD7A3\uD7B0-\uD7C6\uD7CB-\uD7FB\uF900-\uFA6D\uFA70-\uFAD9\uFB00-\uFB06\uFB13-\uFB17\uFB1D\uFB1F-\uFB28\uFB2A-\uFB36\uFB38-\uFB3C\uFB3E\uFB40\uFB41\uFB43\uFB44\uFB46-\uFBB1\uFBD3-\uFD3D\uFD50-\uFD8F\uFD92-\uFDC7\uFDF0-\uFDFB\uFE70-\uFE74\uFE76-\uFEFC\uFF21-\uFF3A\uFF41-\uFF5A\uFF66-\uFFBE\uFFC2-\uFFC7\uFFCA-\uFFCF\uFFD2-\uFFD7\uFFDA-\uFFDC])+$/u;
    if (control.value && !invalidCharsRegex.test(control.value.toString().trim())) {
      return { badCharacters: true };
    }
    return null;
  }

  static emailValidator(control: AbstractControl): { [key: string]: boolean } | null {
    const emailRegex =
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.(com|org|net|edu|gov|mil|io|ai|dev|info|biz|me|us|uk|ca|es|ar|com.br|mx|cl|xyz|online|store|tech|co|ph|com.ph|.net.ph|org.ph)$/;
    const value = control.value?.toString().trim();
    if (control.value && !emailRegex.test(value)) {
      return { invalidEmail: true };
    }
    let isGmail: boolean;
    let isHotmail: boolean;
    let isOutlook: boolean;
    let isIcloud: boolean;
    if (value) {
      isGmail =
        value.toLowerCase().includes('@g') &&
        !value.toLowerCase().includes('@gov') &&
        !value.toLowerCase().includes('@growie');
      isHotmail = value.toLowerCase().includes('@h');
      isOutlook = value.toLowerCase().includes('@out');
      isIcloud = value.toLowerCase().includes('@icl');
    }
    if (isGmail && !value.toLowerCase().endsWith('@gmail.com')) {
      return { invalidEmail: true };
    }
    if (isHotmail && !value.toLowerCase().endsWith('@hotmail.com')) {
      return { invalidEmail: true };
    }
    if (isOutlook && !value.toLowerCase().endsWith('@outlook.com')) {
      return { invalidEmail: true };
    }
    if (isIcloud && !value.toLowerCase().endsWith('@icloud.com')) {
      return { invalidEmail: true };
    }
    return null;
  }

  static requiredDate(control: AbstractControl): { [key: string]: boolean } | null {
    if (!control.value || control.value === null) {
      return { required: true };
    }
    return null;
  }

  static minLengthPostalCode(control: AbstractControl): { [key: string]: boolean } | null {
    if (control.value && control.value.length < 4) {
      return { minlengthfour: true };
    }
    return null;
  }

  static minLengthPostalCodeChl(control: AbstractControl): { [key: string]: boolean } | null {
    if (control.value && control.value.length < 7) {
      return { minlengthseven: true };
    }
    return null;
  }

  static minLengthPostalCodeCol(control: AbstractControl): { [key: string]: boolean } | null {
    if (control.value && control.value.length < 6) {
      return { minlengthsix: true };
    }
    return null;
  }

  static minLengthPostalCodeBra(control: AbstractControl): { [key: string]: boolean } | null {
    if (control.value && control.value.length < 8) {
      return { minlengtheight: true };
    }
    return null;
  }

  static minLengthPostalCodeGTM(control: AbstractControl): { [key: string]: boolean } | null {
    if (control.value && control.value.length < 5) {
      return { minlengthfive: true };
    }
    return null;
  }

  static minLengthPostalCodeMEX(control: AbstractControl): { [key: string]: boolean } | null {
    if (control.value && control.value.length < 5) {
      return { minlengthfive: true };
    }
    return null;
  }

  static minLengthStreet(control: AbstractControl): { [key: string]: boolean } | null {
    if (control.value && control.value.length < 3) {
      return { minlengththree: true };
    }
    return null;
  }

  static CBUAliasValidator(control: AbstractControl): { [key: string]: boolean } | null {
    const invalidCharsRegex = /^([ A-Za-z0-9\u002D\u002E])+$/u;
    if (control.value) {
      if (control.value.length < 6) {
        return { minlength: true };
      }
      if (control.value.length > 22) {
        return { maxlength: true };
      }
      if (control.value && !invalidCharsRegex.test(control.value.toString().trim())) {
        return { badCharacters: true };
      }
    }
    return null;
  }

  static CBUAliasValidatorChl(control: AbstractControl): { [key: string]: boolean } | null {
    const invalidCharsRegex = /^([ A-Za-z0-9\u002D\u002E])+$/u;
    if (control.value) {
      if (control.value.length < 7) {
        return { minlength: true };
      }
      if (control.value.length > 18) {
        return { maxlength: true };
      }
      if (control.value && !invalidCharsRegex.test(control.value.toString().trim())) {
        return { badCharacters: true };
      }
    }
    return null;
  }

  static CBUAliasValidatorCol(control: AbstractControl): { [key: string]: boolean } | null {
    const invalidCharsRegex = /^([ A-Za-z0-9\u002D\u002E])+$/u;
    if (control.value) {
      if (control.value.length < 4) {
        return { minlength: true };
      }
      if (control.value.length > 20) {
        return { maxlength: true };
      }
      if (control.value && !invalidCharsRegex.test(control.value.toString().trim())) {
        return { badCharacters: true };
      }
    }
    return null;
  }

  static CBUAliasValidatorBra(control: AbstractControl): { [key: string]: boolean } | null {
    if (control.value) {
      if (control.value.length < 6) {
        return { minlength: true };
      }
      if (control.value.length > 50) {
        return { maxlength: true };
      }
    }
    return null;
  }

  static CBUAliasValidatorCri(control: AbstractControl): { [key: string]: boolean } | null {
    const invalidCharsRegex = /^([ A-Za-z0-9\u002D\u002E])+$/u;
    if (control.value) {
      const length = control.value.length;
      if (length < 18) {
        return { minlength: true };
      }
      if (length > 20) {
        return { maxlength: true };
      }
      if (!invalidCharsRegex.test(control.value.toString().trim())) {
        return { badCharacters: true };
      }
    }
    return null;
  }

  static CBUAliasValidatorPan(control: AbstractControl): { [key: string]: boolean } | null {
    const invalidCharsRegex = /^([ A-Za-z0-9\u002D\u002E])+$/u;
    if (control.value) {
      const length = control.value.length;
      if (length < 5) {
        return { minlength: true };
      }
      if (length > 20) {
        return { maxlength: true };
      }
      if (!invalidCharsRegex.test(control.value.toString().trim())) {
        return { badCharacters: true };
      }
    }
    return null;
  }

  static minLengthName(control: AbstractControl): { [key: string]: boolean } | null {
    if (control.value) {
      if (control.value.length < 3) {
        return { minlengthChl: true };
      }
      if (control.value.length > 22) {
        return { maxlength: true };
      }
    }
    return null;
  }

  static requiredFalse(control: AbstractControl): { [key: string]: boolean } | null {
    if (control.value !== false) {
      return { required: true };
    }
    return null;
  }

  static bankCodeValidator(control: AbstractControl): { [key: string]: boolean } | null {
    const inputValue = control.value?.trim();

    // Verifica si el valor ingresado es uno de los códigos válidos
    const isValidCode = BANKS_CODE_COL.some((bank) => bank.value === inputValue);

    if (inputValue && !isValidCode) {
      return { invalidBankCode: true };
    }

    return null;
  }

  static bankCodeValidatorMex(control: AbstractControl): { [key: string]: boolean } | null {
    const value = control.value as string;

    if (!value || value.length < 3) {
      return null;
    }

    const bankCode = value.substring(0, 3);
    const isValid = BANKS_CODE_MEX.some((bank) => bank.value === bankCode);

    return isValid ? null : { notMatchBankCode: true };
  }

  static addressValidatorCol(control: AbstractControl): { [key: string]: boolean } | null {
    if (control.value) {
      const letters = control.value.match(/[A-Za-z]/g) || [];
      const numbers = control.value.match(/\d/g) || [];
      if (letters.length < 2 || numbers.length < 2) {
        return { invalidAddress: true };
      }
    }

    return null;
  }

  static phoneNumberValidatorCol(control: AbstractControl): { [key: string]: boolean } | null {
    const value = control.value;
    if (!value) {
      return null;
    }
    const colombiaRegex = /^3\d{9}$/;

    const isValid = colombiaRegex.test(value);
    return isValid ? null : { invalidColombianPhone: true };
  }
}

export interface OnboardingState {
  coin?: 'USDC' | 'WLD' | 'USDCB';
  cryptoAmount?: number;
  successRedirectUrl?: string;
  userId?: string;
  address?: string;
  exchangeCountry?: EXCHANGE_COUNTRY;
  flow?: ONBOARDING_FLOW;
  ramp?: RAMP_MODE;
  fiatAmount?: number;
  companyId?: WHITELABEL_COMPANY;
  hideChat?: boolean;
  network?: NETWORKS;
  externalId?: string;
  signature?: string;
  paymentMethod?: string;
  appOrigin?: string;
  flowEvent?: boolean;
}

export enum DOCUMENT_TYPE_PHL {
  DRIVER_LICENSE = 'DRIVER_LICENSE',
  PASSPORT = 'PASSPORT',
  ID_CARD = 'ID_CARD',
  RESIDENT_CARD = 'RESIDENT_CARD',
}

export const ACCOUNT_TYPE_BANK_CHILE = [
  {
    value: 'Cuenta corriente',
    label: 'Cuenta corriente',
  },
  {
    value: 'Cuenta de ahorros',
    label: 'Cuenta de ahorros',
  },
  {
    value: 'Cuenta Vista',
    label: 'Cuenta vista',
  },
];

export const ACCOUNT_TYPE_BANK_DEFAULT = [
  {
    value: 'checking',
    label: 'Cuenta corriente',
  },
  {
    value: 'savings',
    label: 'Cuenta de ahorros',
  },
];

export const ACCOUNT_TYPE_MEX = [
  {
    value: 'savings',
    label: 'CLABE',
  },
  {
    value: 'debit card account',
    label: 'Tarjeta de débito',
  },
  {
    value: 'phone account',
    label: 'Número de teléfono',
  },
];

export const ACCOUNT_TYPE_PHL = [
  {
    value: 'savings',
    label: 'Savings Account',
  },
  {
    value: 'checking',
    label: 'Current/Checking Account',
  },
  {
    value: 'phone account',
    label: 'Mobile Number Account',
  },
];

export const BANK_CODE_CHILE = [
  {
    value: '317',
    label: 'Banco estado',
  },
  {
    value: '323',
    label: 'Banco falabella',
  },
  {
    value: '318',
    label: 'Banco de chile',
  },
  {
    value: '314',
    label: 'Banco santander',
  },
  {
    value: '962',
    label: 'Mercado pago',
  },
  {
    value: '310',
    label: 'Bci',
  },
  {
    value: '976',
    label: 'Mach',
  },
  {
    value: '325',
    label: 'Tenpo prepago',
  },
  {
    value: '309',
    label: 'Scotiabank chile',
  },
  {
    value: '322',
    label: 'Banco ripley',
  },
  {
    value: '959',
    label: 'Tapp caja los andes',
  },
  {
    value: '319',
    label: 'Coopeuch',
  },
  {
    value: '315',
    label: 'Banco itau',
  },
  {
    value: '312',
    label: 'Bice',
  },
  {
    value: '321',
    label: 'Banco consorcio',
  },
  {
    value: '960',
    label: 'Copec pay',
  },
  {
    value: '324',
    label: 'Banco security',
  },
  {
    value: '961',
    label: 'Prepago los héroes',
  },
  {
    value: '980',
    label: 'Los andes tarjetas de prepago',
  },
  {
    value: '308',
    label: 'Banco internacional',
  },
  {
    value: '311',
    label: 'Corpbanca',
  },
];

export const BANK_CODE_NEQUI = [
  {
    value: '1507',
    type: 'Bank',
    label: 'Banca Digital Nequi',
    checking: null,
    savings: '^(3[0-9]{2}\\d{7}|(030|031|032|870)\\d{8})$',
    savingsError:
      'Si empieza con un número de 300 al 399, debe contener 10 dígitos en total. Sino deben ser 11 dígitos.',
  },
];

export const BANKS_CODE_COL = [
  {
    value: '1507',
    type: 'Bank',
    label: 'Banca Digital Nequi',
    checking: null,
    savings: '^(3[0-9]{2}\\d{7}|(030|031|032|870)\\d{8})$',
    savingsError:
      'Si empieza con un número de 300 al 399, debe contener 10 dígitos en total. Sino deben ser 11 dígitos.',
  },
  {
    value: '1007',
    type: 'Bank',
    label: 'Bancolombia',
    checking: '^\\d{10,11}$',
    savings: '^\\d{10,11}$',
    checkingError: 'Debe contener entre 10 y 11 dígitos.',
    savingsError: 'Debe contener entre 10 y 11 dígitos.',
  },
  {
    value: '1809',
    type: 'Bank',
    label: 'NU',
    checking: '^\\d{8,12}$',
    savings: '^\\d{8,12}$',
    checkingError: 'Debe contener entre 8 y 12 dígitos.',
    savingsError: 'Debe contener entre 8 y 12 dígitos.',
  },
  {
    value: '1551',
    type: 'Bank',
    label: 'Daviplata',
    checking: '^(3[0-9]{2}\\d{7}|(030|031|032|870)\\d{8})$',
    savings: '^(3[0-9]{2}\\d{7}|(030|031|032|870)\\d{8})$',
    checkingError:
      'Si empieza con un número de 300 al 399, debe contener 10 dígitos en total. Sino deben ser 11 dígitos.',
    savingsError:
      'Si empieza con un número de 300 al 399, debe contener 10 dígitos en total. Sino deben ser 11 dígitos.',
  },
  {
    value: '1051',
    type: 'Bank',
    label: 'Banco Davivienda SA',
    checking: '^[0-8]\\d{7,15}$',
    savings: '^\\d{8,17}$',
    checkingError: 'Debe comenzar con un número entre 0 y 8 y contener entre 8 y 16 dígitos.',
    savingsError: 'Debe contener entre 8 y 18 dígitos.',
  },
  {
    value: '1001',
    type: 'Bank',
    label: 'Banco de Bogotá',
    checking: '^[0-9]{9}$',
    savings: '^[0-9]{9}$',
    checkingError: 'Debe contener exactamente 9 dígitos.',
    savingsError: 'Debe contener exactamente 9 dígitos.',
  },
  {
    value: '1032',
    type: 'Bank',
    label: 'Banco Caja Social BCSC SA',
    checking: '^[2]\\d{10}$',
    savings: '^[024]\\d{10}$',
    checkingError: 'Debe comenzar con 2 y contener exactamente 11 dígitos.',
    savingsError: 'Debe comenzar con 0, 2 o 4 y contener 11 dígitos.',
  },
  {
    value: '1013',
    type: 'Bank',
    label: 'BBVA Colombia',
    checking: '^(\\d{9,10}|\\d{14}|\\d{16}|\\d{18})$',
    savings: '^(\\d{9,10}|\\d{14}|\\d{16}|\\d{18})$',
    checkingError: 'Debe contener 10, 15, 17 o 19 dígitos.',
    savingsError: 'Debe contener 10, 15, 17 o 19 dígitos.',
  },
  {
    value: '1052',
    type: 'Bank',
    label: 'Banco AV Villas',
    checking: '^[0124678]\\d{8}$',
    savings: '^\\d{9}$',
    checkingError: 'Debe comenzar con 0, 1, 2, 4, 6, 7 o 8 y contener entre 8 y 9 dígitos.',
    savingsError: 'Debe contener entre 7 y 9 dígitos.',
  },
  {
    value: '1801',
    type: 'Bank',
    label: 'MOVii',
    checking: '^\\d{10}$',
    savings: '^\\d{10}$',
    checkingError: 'Debe contener exactamente 10 dígitos.',
    savingsError: 'Debe contener exactamente 10 dígitos.',
  },
  {
    value: '1804',
    type: 'Bank',
    label: 'Ualá',
    checking: '^\\d{4,15}$',
    savings: '^\\d{4,15}$',
    checkingError: 'Debe contener entre 4 y 15 dígitos.',
    savingsError: 'Debe contener entre 4 y 15 dígitos.',
  },
  {
    value: '1019',
    type: 'Bank',
    label: 'Banco Colpatria (SCOTIABANK COLPATRIA)',
    checking: '^[0-24-8]\\d{7,10}$',
    savings: '^\\d{8,12}$',
    checkingError: 'Debe comenzar con 0, 2, 4 u 8 y contener entre 8 y 11 dígitos.',
    savingsError: 'Debe contener entre 8 y 12 dígitos.',
  },
  {
    value: '1062',
    type: 'Bank',
    label: 'Banco Falabella S.A',
    checking: '^[15]\\d{11}$',
    savings: '^[15]\\d{11}$',
    checkingError: 'Debe comenzar con 1 o 5 y contener exactamente 12 dígitos.',
    savingsError: 'Debe comenzar con 1 o 5 y contener exactamente 12 dígitos.',
  },
  {
    value: '1070',
    type: 'Bank',
    label: 'Lulo Bank S.A.',
    checking: null,
    savings: null,
  },
  {
    value: '1002',
    type: 'Bank',
    label: 'Banco Popular',
    checking: '^[1](\\d{8}|\\d{11}|\\d{12})$',
    savings: '^[025](\\d{8}|\\d{11}|\\d{12})$',
    checkingError: 'Debe comenzar con 1 y contener 9, 12 o 13 dígitos.',
    savingsError: 'Debe comenzar con 0, 2 o 5 y contener 9, 12 o 13 dígitos.',
  },
  {
    value: '1292',
    type: 'Bank',
    label: 'Confiar Cooperativa Financiera',
    checking: null,
    savings: null,
  },
];

export const BANKS_CODE_GTM = [
  {
    value: '001',
    type: 'Bank',
    label: 'Banco de Guatemala',
    checking: '^(1|2|3|5|6|7|8|9)[0-9]{6}$',
    savings: null, // El banco no maneja cuentas de ahorro
    checkingError: 'Debe empezar con un número entre 1 y 9 y contener 7 dígitos.',
    savingsError: 'Este banco no maneja cuentas de ahorro.',
  },
  {
    value: '004',
    type: 'Bank',
    label: 'El Crédito Hipotecario Nacional de Guatemala',
    checking: '^(10|11)[0-9]{10}$',
    savings: '^(01|02|03)[0-9]{10}$',
    checkingError: 'Debe empezar con 10 o 11 y contener 12 dígitos.',
    savingsError: 'Debe empezar con 01, 02 o 03 y contener 12 dígitos.',
  },
  {
    value: '012',
    type: 'Bank',
    label: 'Banco de los Trabajadores',
    checking: '^(1|2|3|5|6|7|8|9)[0-9]{7,9}$',
    savings: '^(1|2|3|4|5|6|7|8|9)[0-9]{3,9}$',
    checkingError: 'Debe empezar con un número entre 1 y 9 y contener entre 8 y 10 dígitos.',
    savingsError: 'Debe empezar con un número entre 1 y 9 y contener entre 4 y 10 dígitos.',
  },
  {
    value: '013',
    type: 'Bank',
    label: 'Banco Inmobiliario S.A.',
    checking: '^(1|2|5)[0-9]{10,11}$',
    savings: '^(1)[0-9]{10,11}$',
    checkingError: 'Debe empezar con 1, 2 o 5 y contener entre 11 y 12 dígitos.',
    savingsError: 'Debe empezar con 1 y contener entre 11 y 12 dígitos.',
  },
  {
    value: '015',
    type: 'Bank',
    label: 'Banco Industrial S.A.',
    checking: '^[0-9]{10}$',
    savings: '^[0-9]{7}$',
    checkingError: 'Debe contener 10 dígitos.',
    savingsError: 'Debe contener 7 dígitos.',
  },
  {
    value: '016',
    type: 'Bank',
    label: 'Banco de Desarrollo Rural S.A.',
    checking: '^(3)[0-9]{9}|(0)[0-9]{13}$',
    savings: '^(4)[0-9]{9}|(0)[0-9]{13}$',
    checkingError: 'Debe empezar con 3 y contener 10 dígitos, o con 0 y contener 14.',
    savingsError: 'Debe empezar con 4 y contener 10 dígitos, o con 0 y contener 14.',
  },
  {
    value: '019',
    type: 'Bank',
    label: 'Banco Internacional S.A.',
    checking: '^[0-9]{10}$',
    savings: '^[0-9]{10}$',
    checkingError: 'Debe contener exactamente 10 dígitos.',
    savingsError: 'Debe contener exactamente 10 dígitos.',
  },
  {
    value: '030',
    type: 'Bank',
    label: 'Citibank, N.A. Sucursal Guatemala',
    checking: '^(000)[0-9]{7}$',
    savings: null, // El banco no maneja cuentas de ahorro
    checkingError: 'Debe empezar con 000 y contener exactamente 10 dígitos.',
    savingsError: 'Este banco no maneja cuentas de ahorro.',
  },
  {
    value: '036',
    type: 'Bank',
    label: 'Vivibanco, S.A.',
    checking: '^[0-9]{12}$',
    savings: '^[0-9]{12}$',
    checkingError: 'Debe contener exactamente 12 dígitos.',
    savingsError: 'Debe contener exactamente 12 dígitos.',
  },
  {
    value: '039',
    type: 'Bank',
    label: 'Banco Ficohsa Guatemala, S.A',
    checking: '^(5)[0-9]{8,10}$',
    savings: '^(5)[0-9]{8,10}$',
    checkingError: 'Debe empezar con 5 y contener entre 9 y 11 dígitos.',
    savingsError: 'Debe empezar con 5 y contener entre 9 y 11 dígitos.',
  },
  {
    value: '040',
    type: 'Bank',
    label: 'Banco Proamerica, S.A.',
    checking: '^[0-9]{14}$',
    savings: '^[0-9]{14}$',
    checkingError: 'Debe contener exactamente 14 dígitos.',
    savingsError: 'Debe contener exactamente 14 dígitos.',
  },
  {
    value: '041',
    type: 'Bank',
    label: 'Banco de Antigua, S.A.',
    checking: null, // El banco no maneja cuentas corrientes
    savings: '^(4127)[0-9]{9}$',
    checkingError: 'Este banco no maneja cuentas corrientes.',
    savingsError: 'Debe empezar con 4127 y contener exactamente 13 dígitos.',
  },
  {
    value: '042',
    type: 'Bank',
    label: 'Banco de América Central, S.A.',
    checking: '^[0-9]{9,11}$',
    savings: '^(96)[0-9]{7}|(1)[0-9]{9}|[0-9]{9,11}$',
    checkingError: 'Debe contener entre 9 y 11 dígitos.',
    savingsError:
      'Debe empezar con 96 (8 dígitos), 1 (10 dígitos), o contener entre 9 y 11 dígitos.',
  },
  {
    value: '044',
    type: 'Bank',
    label: 'Banco Agromercantil de Guatemala, S.A.',
    checking: '^(0)?[0-9]{9,10}$',
    savings: '^(0)?[0-9]{9,10}$',
    checkingError: 'Debe contener entre 10 y 11 dígitos (con o sin 0 inicial).',
    savingsError: 'Debe contener entre 10 y 11 dígitos (con o sin 0 inicial).',
  },
  {
    value: '045',
    type: 'Bank',
    label: 'Banco G&T Continental, S.A.',
    checking: '^[0-9]{11}$',
    savings: '^[0-9]{11}$',
    checkingError: 'Debe contener exactamente 11 dígitos.',
    savingsError: 'Debe contener exactamente 11 dígitos.',
  },
  {
    value: '047',
    type: 'Bank',
    label: 'Banco Azteca de Guatemala, S.A.',
    checking: '^[0-9]{14}$',
    savings: null, // El banco no maneja cuentas de ahorro
    checkingError: 'Debe contener exactamente 14 dígitos.',
    savingsError: 'Este banco no maneja cuentas de ahorro.',
  },
  {
    value: '048',
    type: 'Bank',
    label: 'Banco Inv, S.A.',
    checking: '^(0)[0-9]{11}$',
    savings: '^(2)[0-9]{11}$',
    checkingError: 'Debe empezar con 0 y contener exactamente 12 dígitos.',
    savingsError: 'Debe empezar con 2 y contener exactamente 12 dígitos.',
  },
  {
    value: '049',
    type: 'Bank',
    label: 'Banco Credicorp, S.A.',
    checking: '^[0-9]{12}$',
    savings: '^[0-9]{12}$',
    checkingError: 'Debe contener exactamente 12 dígitos.',
    savingsError: 'Debe contener exactamente 12 dígitos.',
  },
];

export const BANKS_CODE_PAN = [
  { value: '0013', label: 'Banco Nacional de Panamá' },
  { value: '0026', label: 'Banistmo S.A.' },
  { value: '0039', label: 'Citibank' },
  { value: '0071', label: 'Banco General' },
  { value: '0181', label: 'Banco Davivienda' },
  { value: '0372', label: 'Multibank' },
  { value: '0408', label: 'Banco Towerbank' },
  { value: '0424', label: 'Banco Scotiabank' },
  { value: '0518', label: 'Banco Bicsa' },
  { value: '0712', label: 'Cooperativa de Profesionales' },
  { value: '0767', label: 'Banco Banvivienda' },
  { value: '0770', label: 'Banco Caja de Ahorros' },
  { value: '0916', label: 'Banco del Pacífico (Panamá) S.A.' },
  { value: '1067', label: 'Metrobank S.A.' },
  { value: '1083', label: 'Banco Aliado' },
  { value: '1106', label: 'Credicorp Bank' },
  { value: '1151', label: 'Global Bank' },
  { value: '1164', label: 'Bank of China' },
  { value: '1258', label: 'Canal Bank' },
  { value: '1384', label: 'BAC International Bank' },
  { value: '1397', label: 'BCT Bank' },
  { value: '1478', label: 'MMG Bank' },
  { value: '1494', label: 'Banco St. George' },
  { value: '1504', label: 'Banco Azteca' },
  { value: '1517', label: 'Banco Pichincha Panamá' },
  { value: '1533', label: 'PRODUBank' },
  { value: '1562', label: 'Banco Delta' },
  { value: '1575', label: 'Banco Lafise Panamá S.A.' },
  { value: '1588', label: 'Banco Banesco' },
  { value: '1591', label: 'Capital Bank' },
  { value: '1614', label: 'Banco Banisi S.A.' },
  { value: '1627', label: 'Banco G&T Continental' },
  { value: '1630', label: 'Banco Mercantil Bank' },
  { value: '1656', label: 'BBP Bank S.A.' },
  { value: '1672', label: 'Prival Bank' },
  { value: '1685', label: 'Balboa Bank & Trust' },
  { value: '1708', label: 'Uni Bank & Trust Inc' },
  { value: '1724', label: 'Banco Ficohsa Panamá' },
  { value: '1737', label: 'FPB Bank' },
  { value: '1753', label: 'Bancolombia' },
  { value: '1782', label: 'BI-Bank S.A.' },
  { value: '1805', label: 'B2 Atlas Bank' },
  { value: '2503', label: 'Banco Coopeduc' },
  { value: '2516', label: 'Banco Cooesan' },
  { value: '2529', label: 'Banco Cacechi' },
  { value: '2532', label: 'Banco Coeduco' },
  { value: '2545', label: 'Coopeve' },
  { value: '5005', label: 'Cooperativa Cristóbal' },
  { value: '5018', label: 'Banco Edioacc R.L' },
  { value: '5021', label: 'Banco Ecaseso' },
  { value: '5034', label: 'Banco Cooprac R.L.' },
  { value: '9900', label: 'Banco Itaú Panamá S.A.' },
];

export const BANKS_CODE_CRI = [
  { value: '0102', label: 'Banco Bac San José' },
  { value: '0104', label: 'Banco Davivienda Costa Rica S.A.' },
  { value: '0107', label: 'Banco BCT' },
  { value: '0114', label: 'Banco Lafise' },
  { value: '0116', label: 'Banco Promérica' },
  { value: '0120', label: 'Banco Improsa' },
  { value: '0123', label: 'Banco Scotiabank' },
  { value: '0125', label: 'Banco Cathay' },
  { value: '0126', label: 'Banco General de Costa Rica S.A.' },
  { value: '0127', label: 'Banco CMB (Costa Rica) S.A.' },
  { value: '0151', label: 'Banco Nacional de Costa Rica' },
  { value: '0152', label: 'Banco de Costa Rica' },
  { value: '0161', label: 'Banco Popular de Desarrollo Comunal' },
  { value: '0162', label: 'Banco Hipotecario de la Vivienda' },
  { value: '0231', label: 'Banco Internacional de Costa Rica' },
  { value: '0312', label: 'Prival Bank (Costa Rica) S.A.' },
];

export const BANKS_CODE_MEX = [
  { value: '012', label: 'BBVA Bancomer' },
  { value: '638', label: 'NU' },
  { value: '728', label: 'SPIN BY OXXO' },
  { value: '722', label: 'Mercado Pago W' },
  { value: '137', label: 'BanCoppel' },
  { value: '014', label: 'Banco Santander (México)' },
  { value: '646', label: 'Mercado Pago' },
  { value: '002', label: 'Banco Nacional de México' },
  { value: '072', label: 'Banco Mercantil del Norte' },
  { value: '021', label: 'HSBC México' },
  { value: '127', label: 'Banco Azteca' },
  { value: '138', label: 'ABC Capital' },
  { value: '661', label: 'Klar' },
  { value: '036', label: 'Banco Inbursa' },
  { value: '042', label: 'Banca Mifel' },
  { value: '044', label: 'Scotiabank Inverlat' },
  { value: '058', label: 'Banco Regional de Monterrey' },
  { value: '062', label: 'Banca Afirme' },
  { value: '140', label: 'Consubanco' },
  { value: '030', label: 'Banco del Bajío' },
  { value: '147', label: 'Bankaool' },
  { value: '130', label: 'Banco Compartamos' },
  { value: '699', label: 'Fondeadora' },
  { value: '706', label: 'Arcus' },
  { value: '059', label: 'Banco Invex' },
  { value: '710', label: 'NVIO' },
  { value: '684', label: 'Transfer' },
  { value: '723', label: 'Cuenca' },
  { value: '151', label: 'Donde' },
  { value: '166', label: 'Banco del Ahorro Nacional y Servicios Financieros' },
  { value: '133', label: 'Banco Actinver' },
  { value: '019', label: 'Banco Nacional del Ejército' },
];

export const BANKS_CODE_PH = [
  { value: 'gcash', type: 'e-wallet', label: 'GCash' },
  { value: 'grabpay', type: 'e-wallet', label: 'GrabPay' },
  { value: 'paymaya', type: 'e-wallet', label: 'PayMaya/ Maya Wallet' },
  {
    value: 'allbank',
    type: 'bank',
    label: 'AllBank (A Thrift Bank), Inc.',
  },
  {
    value: 'guinobatan',
    type: 'bank',
    label: 'ASENSO/Rural Bank of Guinobatan',
  },
  { value: 'aub', type: 'bank', label: 'Asia United Bank' },
  { value: 'bananapay', type: 'e-wallet', label: 'BananaPay' },
  { value: 'bangkomabuhay', type: 'bank', label: 'Bangko Mabuhay' },
  { value: 'bankofchina', type: 'bank', label: 'bank of China' },
  { value: 'bankofcommerce', type: 'bank', label: 'Bank of Commerce' },
  {
    value: 'bpibanko',
    type: 'bank',
    label: 'BanKo, A subsidiary of BPI',
  },
  { value: 'bdouni', type: 'bank', label: 'BDO Bank' },
  { value: 'bdo', type: 'bank', label: 'BDO Network Bank' },
  {
    value: 'binangonan',
    type: 'bank',
    label: 'Binangonan Rural Bank (BRBDigital)',
  },
  { value: 'bpi', type: 'bank', label: 'BPI' },
  { value: 'camalig', type: 'bank', label: 'Camalig Bank' },
  { value: 'cardbank', type: 'bank', label: 'CARD Bank' },
  { value: 'sme', type: 'bank', label: 'CARD SME Bank' },
  {
    value: 'cebuanal',
    type: 'bank',
    label: 'Cebuana Lhuillier Bank/ Cebuana Xpress',
  },
  { value: 'chinabank', type: 'bank', label: 'China Bank' },
  {
    value: 'chinabanksavings',
    type: 'bank',
    label: 'China Bank Savings',
  },
  { value: 'cimb', type: 'bank', label: 'CIMB Bank Philippines' },
  {
    value: 'cisbayad',
    type: 'e-wallet',
    label: 'CIS Bayad Center/ Bayad',
  },
  { value: 'ctbc', type: 'bank', label: 'CTBC Bank (Philippines) Corp.' },
  {
    value: 'coins.ph',
    type: 'e-wallet',
    label: 'DCPAY PHILIPPINES, INC.',
  },
  {
    value: 'dbp',
    type: 'bank',
    label: 'Development Bank of the Philippines',
  },
  { value: 'dumaguetebankx', type: 'bank', label: 'Dumaguete Bank' },
  { value: 'dungganon', type: 'bank', label: 'Dungganon Bank' },
  { value: 'eastwest', type: 'bank', label: 'EastWest Bank' },
  {
    value: 'easypay',
    type: 'e-wallet',
    label: 'Easy Pay Global EMI Corp',
  },
  { value: 'ecash', type: 'e-wallet', label: 'ecash' },
  { value: 'equicom', type: 'bank', label: 'Equicom Savings Bank' },
  { value: 'gotyme', type: 'bank', label: 'GoTyme Bank' },
  { value: 'iremit', type: 'bank', label: 'I-Remit/iCASH' },
  {
    value: 'isla',
    type: 'bank',
    label: 'ISLA Bank (A Thrift Bank), Inc.',
  },
  { value: 'zybi', type: 'e-wallet', label: 'JuanCash' },
  {
    value: 'eastwest_rural',
    type: 'bank',
    label: 'Komo/ EastWest Rural Bank',
  },
  { value: 'landbank', type: 'bank', label: 'LANDBANK / OFBank' },
  {
    value: 'lazada',
    type: 'e-wallet',
    label: 'Lazada Wallet (Alipay Ph.)',
  },
  { value: 'legazpi', type: 'bank', label: 'Legazpi Savings Bank' },
  { value: 'LuLuMoney', type: 'bank', label: 'LuLuMoney' },
  { value: 'malayan', type: 'bank', label: 'Malayan Bank Savings' },
  { value: 'marcopay', type: 'e-wallet', label: 'MarCoPay' },
  { value: 'mayabank', type: 'bank', label: 'Maya Bank' },
  { value: 'maybank', type: 'bank', label: 'Maybank' },
  { value: 'metrobank', type: 'bank', label: 'Metrobank' },
  { value: 'mcb', type: 'bank', label: 'Mindanao Consolidated CoopBank' },
  { value: 'netbank', type: 'bank', label: 'NetBank' },
  { value: 'omnipay', type: 'bank', label: 'Omnipay, Inc.' },
  { value: 'ownb', type: 'bank', label: 'Own Bank' },
  { value: 'palawanpay', type: 'e-wallet', label: 'PalawanPay' },
  {
    value: 'partnerrb',
    type: 'bank',
    label: 'Partner Rural Bank (Cotabato), Inc.',
  },
  { value: 'PayMongo', type: 'e-wallet', label: 'PayMongo' },
  { value: 'pbcom', type: 'bank', label: 'PBCOM' },
  { value: 'pdax', type: 'e-wallet', label: 'PDAX' },
  { value: 'veterans', type: 'bank', label: 'Philippine Veterans Bank' },
  { value: 'philtrust', type: 'bank', label: 'PhilTrust Bank' },
  { value: 'pnb', type: 'bank', label: 'PNBMPHMMTOD' },
  {
    value: 'producers',
    type: 'bank',
    label: 'Producers Savings Bank Corporation',
  },
  { value: 'psbank', type: 'bank', label: 'PSBank' },
  { value: 'queenbank', type: 'bank', label: 'Queenbank' },
  {
    value: 'quezonbank',
    type: 'bank',
    label: 'Quezon Capital Rural Bank',
  },
  { value: 'rcbc', type: 'bank', label: 'RCBC / Diskartech' },
  { value: 'robinsons', type: 'bank', label: 'Robinsons Bank' },
  { value: 'seabank', type: 'bank', label: 'Seabank' },
  { value: 'security', type: 'bank', label: 'Security Bank' },
  { value: 'security2', type: 'bank', label: 'Security Bank 2' },
  { value: 'shopeepay', type: 'e-wallet', label: 'ShopeePay' },
  { value: 'speedypay', type: 'e-wallet', label: 'SpeedyPay' },
  {
    value: 'standard_chartered',
    type: 'bank',
    label: 'Standard Chartered Bank',
  },
  { value: 'starpay', type: 'e-wallet', label: 'Starpay' },
  { value: 'sterling', type: 'bank', label: 'Sterling Bank of Asia' },
  { value: 'sunsavings', type: 'bank', label: 'Sun Savings Bank' },
  { value: 'tayocash', type: 'e-wallet', label: 'TayoCash' },
  { value: 'tonik', type: 'bank', label: 'Tonik Bank' },
  {
    value: 'traxionpay',
    type: 'e-wallet',
    label: 'TraxionPay/ DigiCOOP/ COOPNET',
  },
  { value: 'ucpb', type: 'bank', label: 'UCPB Savings Bank' },
  {
    value: 'unionbank',
    type: 'bank',
    label: 'Union Bank of the Philippines',
  },
  { value: 'uniondigital', type: 'bank', label: 'UnionDigital Bank' },
  { value: 'ussc', type: 'e-wallet', label: 'USSC' },
  { value: 'wealth', type: 'bank', label: 'Wealth Bank' },
];

export const BANKS_ONLY_SAVINGS = {
  '1001': 'Banco de Bogotá',
  '1007': 'Bancolombia',
  '1032': 'Banco Caja Social BCSC SA',
  '1052': 'Banco AV Villas',
  '1070': 'LULOBANK',
  '1507': 'NEQUI',
  '1097': 'DALE',
  '1560': 'PIBANK',
  '1801': 'MOVii',
  '1811': 'RAPPIPAY',
  '041': 'Banco de Antigua, S.A.',
};

export const BANKS_ONLY_CHECKING = {
  '047': 'Banco Azteca de Guatemala, S.A.',
  '030': 'Citibank, N.A. Sucursal Guatemala',
  '001': 'Banco de Guatemala',
};

export enum BANK_STATE_CODE {
  STATE_BANK = '317',
}

export const REGIONS = [
  {
    name: 'Arica y Parinacota',
    communes: ['Arica', 'Camarones', 'Putre', 'General Lagos'],
  },
  {
    name: 'Tarapacá',
    communes: ['Iquique', 'Alto Hospicio', 'Pozo Almonte', 'Camiña', 'Colchane', 'Huara', 'Pica'],
  },
  {
    name: 'Antofagasta',
    communes: [
      'Antofagasta',
      'Mejillón',
      'Sierra Gorda',
      'Taltal',
      'Calama',
      'Ollagüe',
      'San Pedro de Atacama',
      'Tocopilla',
      'María Elena',
    ],
  },
  {
    name: 'Atacama',
    communes: [
      'Copiapó',
      'Caldera',
      'Tierra Amarilla',
      'Chañaral',
      'Diego de Almagro',
      'Vallenar',
      'Alto del Carmen',
      'Freirina',
      'Huasco',
    ],
  },
  {
    name: 'Coquimbo',
    communes: [
      'La Serena',
      'Coquimbo',
      'Andacollo',
      'La Higuera',
      'Paihuano',
      'Vicuña',
      'Illapel',
      'Canela',
      'Los Vilos',
      'Salamanca',
      'Ovalle',
      'Combarbalá',
      'Monte Patria',
      'Punitaqui',
      'Río Hurtado',
    ],
  },
  {
    name: 'Valparaíso',
    communes: [
      'Valparaíso',
      'Casablanca',
      'Concón',
      'Juan Fernández',
      'Puchuncaví',
      'Quintero',
      'Viña del Mar',
      'Isla de Pascua',
      'Los Andes',
      'Calle Larga',
      'Rinconada',
      'San Esteban',
      'La Ligua',
      'Cabildo',
      'Papudo',
      'Petorca',
      'Zapallar',
      'Quillota',
      'La Calera',
      'Hijuelas',
      'La Cruz',
      'Nogales',
      'San Antonio',
      'Algarrobo',
      'Cartagena',
      'El Quisco',
      'El Tabo',
      'Santo Domingo',
      'San Felipe',
      'Catemu',
      'Llay-Llay',
      'Panquehue',
      'Putaendo',
      'Santa María',
      'Quilpué',
      'Limache',
      'Olmué',
      'Villa Alemana',
    ],
  },
  {
    name: "O'Higgins",
    communes: [
      'Rancagua',
      'Codegua',
      'Coinco',
      'Coltauco',
      'Doñihue',
      'Graneros',
      'Las Cabras',
      'Machalí',
      'Malloa',
      'Mostazal',
      'Olivar',
      'Peumo',
      'Pichidegua',
      'Quinta de Tilcoco',
      'Rengo',
      'Requínoa',
      'San Vicente',
      'Pichilemu',
      'La Estrella',
      'Litueche',
      'Marchigüe',
      'Navidad',
      'Paredones',
      'San Fernando',
      'Chépica',
      'Chimbarongo',
      'Lolol',
      'Nancagua',
      'Palmilla',
      'Peralillo',
      'Placilla',
      'Pumanque',
      'Santa Cruz',
    ],
  },
  {
    name: 'Maule',
    communes: [
      'Talca',
      'Constitución',
      'Curepto',
      'Empedrado',
      'Maule',
      'Pelarco',
      'Pencahue',
      'Río Claro',
      'San Clemente',
      'San Rafael',
      'Cauquenes',
      'Chanco',
      'Pelluhue',
      'Curicó',
      'Hualañé',
      'Licantén',
      'Molina',
      'Rauco',
      'Romeral',
      'Sagrada Familia',
      'Teno',
      'Vichuquén',
      'Linares',
      'Colbún',
      'Longaví',
      'Parral',
      'Retiro',
      'San Javier',
      'Villa Alegre',
      'Yerbas Buenas',
    ],
  },
  {
    name: 'Ñuble',
    communes: [
      'Chillán',
      'Bulnes',
      'Chillán Viejo',
      'El Carmen',
      'Pemuco',
      'Pinto',
      'Quillón',
      'San Ignacio',
      'Yungay',
      'Quirihue',
      'Cobquecura',
      'Coelemu',
      'Ninhue',
      'Portezuelo',
      'Ránquil',
      'Treguaco',
      'San Carlos',
      'Coihueco',
      'Ñiquén',
      'San Fabián',
      'San Nicolás',
    ],
  },
  {
    name: 'Biobío',
    communes: [
      'Concepción',
      'Coronel',
      'Chiguayante',
      'Florida',
      'Hualqui',
      'Lota',
      'Penco',
      'San Pedro de La Paz',
      'Santa Juana',
      'Talcahuano',
      'Tomé',
      'Hualpén',
      'Lebu',
      'Arauco',
      'Cañete',
      'Contulmo',
      'Curanilahue',
      'Los Álamos',
      'Tirúa',
      'Los Ángeles',
      'Antuco',
      'Cabrero',
      'Laja',
      'Mulchén',
      'Nacimiento',
      'Negrete',
      'Quilaco',
      'Quilleco',
      'San Rosendo',
      'Santa Bárbara',
      'Tucapel',
      'Yumbel',
      'Alto Biobío',
    ],
  },
  {
    name: 'La Araucanía',
    communes: [
      'Temuco',
      'Carahue',
      'Cunco',
      'Curarrehue',
      'Freire',
      'Galvarino',
      'Gorbea',
      'Lautaro',
      'Loncoche',
      'Melipeuco',
      'Nueva Imperial',
      'Padre Las Casas',
      'Perquenco',
      'Pitrufquén',
      'Pucón',
      'Saavedra',
      'Teodoro Schmidt',
      'Toltén',
      'Vilcún',
      'Villarrica',
      'Cholchol',
      'Angol',
      'Collipulli',
      'Curacautín',
      'Ercilla',
      'Lonquimay',
      'Los Sauces',
      'Lumaco',
      'Purén',
      'Renaico',
      'Traiguén',
      'Victoria',
    ],
  },
  {
    name: 'Los Ríos',
    communes: [
      'Valdivia',
      'Corral',
      'Lanco',
      'Los Lagos',
      'Máfil',
      'Mariquina',
      'Paillaco',
      'Panguipulli',
      'La Unión',
      'Futrono',
      'Lago Ranco',
      'Río Bueno',
    ],
  },
  {
    name: 'Los Lagos',
    communes: [
      'Puerto Montt',
      'Calbuco',
      'Cochamó',
      'Fresia',
      'Frutillar',
      'Los Muermos',
      'Llanquihue',
      'Maullín',
      'Puerto Varas',
      'Castro',
      'Ancud',
      'Chonchi',
      'Curaco de Vélez',
      'Dalcahue',
      'Puqueldón',
      'Queilén',
      'Quellón',
      'Quemchi',
      'Quinchao',
      'Osorno',
      'Puerto Octay',
      'Purranque',
      'Puyehue',
      'Río Negro',
      'San Juan de la Costa',
      'San Pablo',
      'Chaitén',
      'Futaleufú',
      'Hualaihué',
      'Palena',
    ],
  },
  {
    name: 'Aysén',
    communes: [
      'Coyhaique',
      'Lago Verde',
      'Aysén',
      'Cisnes',
      'Guaitecas',
      'Cochrane',
      "O'Higgins",
      'Tortel',
      'Chile Chico',
      'Río Ibáñez',
    ],
  },
  {
    name: 'Magallanes y Antártica Chilena',
    communes: [
      'Punta Arenas',
      'Laguna Blanca',
      'Río Verde',
      'San Gregorio',
      'Cabo de Hornos',
      'Antártica',
      'Porvenir',
      'Primavera',
      'Timaukel',
      'Natales',
      'Torres del Paine',
    ],
  },
  {
    name: 'Metropolitana de Santiago',
    communes: [
      'Santiago',
      'Cerrillos',
      'Cerro Navia',
      'Conchalí',
      'El Bosque',
      'Estación Central',
      'Huechuraba',
      'Independencia',
      'La Cisterna',
      'La Florida',
      'La Granja',
      'La Pintana',
      'La Reina',
      'Las Condes',
      'Lo Barnechea',
      'Lo Espejo',
      'Lo Prado',
      'Macul',
      'Maipú',
      'Ñuñoa',
      'Pedro Aguirre Cerda',
      'Peñalolén',
      'Providencia',
      'Pudahuel',
      'Quilicura',
      'Quinta Normal',
      'Recoleta',
      'Renca',
      'San Joaquín',
      'San Miguel',
      'San Ramón',
      'Vitacura',
      'Puente Alto',
      'Pirque',
      'San José de Maipo',
      'Colina',
      'Lampa',
      'Til Til',
      'San Bernardo',
      'Buin',
      'Calera de Tango',
      'Paine',
      'Melipilla',
      'Alhué',
      'Curacaví',
      'María Pinto',
      'San Pedro',
      'Talagante',
      'El Monte',
      'Isla de Maipo',
      'Padre Hurtado',
      'Peñaflor',
    ],
  },
];

export const DEPARTMENTS = [
  {
    name: 'Amazonas',
    municipalities: [
      'El Encanto',
      'La Chorrera',
      'La Pedrera',
      'La Victoria',
      'Miriti - Parana',
      'Leticia',
      'Puerto Alegría',
      'Puerto Arica',
      'Puerto Nariño',
      'Puerto Santander',
      'Tarapaca',
    ],
  },
  {
    name: 'Antioquia',
    municipalities: [
      'Abejorral',
      'Abriaqui',
      'Alejandría',
      'Amagá',
      'Amalfi',
      'Andes',
      'Angelópolis',
      'Angostura',
      'Anorí',
      'Anzá',
      'Apartadó',
      'Arboletes',
      'Argelia',
      'Armenia',
      'Barbosa',
      'Bello',
      'Belmira',
      'Betania',
      'Betulia',
      'Briceño',
      'Buriticá',
      'Cáceres',
      'Caicedo',
      'Caldas',
      'Campamento',
      'Cañasgordas',
      'Caracolí',
      'Caramanta',
      'Carepa',
      'Carolina',
      'Caucasia',
      'Chigorodó',
      'Cisneros',
      'Ciudad Bolívar',
      'Cocorná',
      'Concepción',
      'Concordia',
      'Copacabana',
      'Dabeiba',
      'Don Matías',
      'Ebéjico',
      'El Bagre',
      'El Carmen de Viboral',
      'El Retiro',
      'El Santuario',
      'Entrerríos',
      'Envigado',
      'Fredonia',
      'Frontino',
      'Giraldo',
      'Girardota',
      'Gómez Plata',
      'Granada',
      'Guadalupe',
      'Guarne',
      'Guatapé',
      'Heliconia',
      'Hispania',
      'Itagüí',
      'Ituango',
      'Jardín',
      'Jericó',
      'La Ceja',
      'La Estrella',
      'La Pintada',
      'La Unión',
      'Liborina',
      'Maceo',
      'Marinilla',
      'Medellín',
      'Montebello',
      'Murindó',
      'Mutatá',
      'Nariño',
      'Necoclí',
      'Nechí',
      'Olaya',
      'Peñol',
      'Peque',
      'Pueblorrico',
      'Puerto Berrío',
      'Puerto Nare',
      'Puerto Triunfo',
      'Remedios',
      'Retiro',
      'Rionegro',
      'Sabanalarga',
      'Sabaneta',
      'Salgar',
      'San Andrés de Cuerquia',
      'San Carlos',
      'San Francisco',
      'San Jerónimo',
      'San José de la Montaña',
      'San Juan de Urabá',
      'San Luis',
      'San Pedro de Urabá',
      'San Pedro de los Milagros',
      'San Rafael',
      'San Roque',
      'San Vicente Ferrer',
      'Santa Bárbara',
      'Santa Fe de Antioquia',
      'Santa Rosa de Osos',
      'Santo Domingo',
      'Segovia',
      'Sonsón',
      'Sopetrán',
      'Támesis',
      'Tarazá',
      'Tarso',
      'Titiribí',
      'Toledo',
      'Turbo',
      'Uramita',
      'Urrao',
      'Valdivia',
      'Valparaíso',
      'Vegachí',
      'Venecia',
      'Vigía del Fuerte',
      'Yalí',
      'Yarumal',
      'Yolombó',
      'Yondó',
      'Zaragoza',
    ],
  },
  {
    name: 'Arauca',
    municipalities: [
      'Arauca',
      'Arauquita',
      'Cravo Norte',
      'Fortul',
      'Puerto Rondón',
      'Saravena',
      'Tame',
    ],
  },
  {
    name: 'Atlántico',
    municipalities: [
      'Baranoa',
      'Barranquilla',
      'Campo de la Cruz',
      'Candelaria',
      'Galapa',
      'Juan de Acosta',
      'Luruaco',
      'Malambo',
      'Manatí',
      'Palmar de Varela',
      'Piojó',
      'Polonuevo',
      'Ponedera',
      'Puerto Colombia',
      'Repelón',
      'Sabanagrande',
      'Sabanalarga',
      'Santa Lucía',
      'Santo Tomás',
      'Soledad',
      'Suan',
      'Tubará',
      'Usiacurí',
    ],
  },
  {
    name: 'Bogotá, D.C.',
    municipalities: [
      'Antonio Nariño',
      'Barrios Unidos',
      'Bogotá',
      'Bosa',
      'Candelaria',
      'Chapinero',
      'Ciudad Bolívar',
      'Engativá',
      'Fontibón',
      'Kennedy',
      'Los Mártires',
      'Puente Aranda',
      'Rafael Uribe Uribe',
      'San Cristóbal',
      'Santa Fe',
      'Suba',
      'Sumapaz',
      'Teusaquillo',
      'Tunjuelito',
      'Usaquén',
      'Usme',
    ],
  },
  {
    name: 'Bolívar',
    municipalities: [
      'Achi',
      'Altos del Rosario',
      'Arenal',
      'Arjona',
      'Arroyohondo',
      'Barranco de Loba',
      'Calamar',
      'Cantagallo',
      'Cartagena',
      'Cicuco',
      'Clemencia',
      'Córdoba',
      'El Carmen de Bolívar',
      'El Guamo',
      'El Peñón',
      'Hatillo de Loba',
      'Magangué',
      'Mahates',
      'Margarita',
      'María La Baja',
      'Mompox',
      'Montecristo',
      'Morales',
      'Norosí',
      'Pinillos',
      'Regidor',
      'Río Viejo',
      'San Cristóbal',
      'San Estanislao',
      'San Fernando',
      'San Jacinto',
      'San Jacinto del Cauca',
      'San Juan Nepomuceno',
      'San Martín de Loba',
      'San Pablo',
      'Santa Catalina',
      'Santa Rosa',
      'Santa Rosa del Sur',
      'Simití',
      'Soplaviento',
      'Talaigua Nuevo',
      'Tiquisio',
      'Turbaco',
      'Turbana',
      'Villanueva',
      'Zambrano',
    ],
  },
  {
    name: 'Boyacá',
    municipalities: [
      'Almeida',
      'Aquitania',
      'Arcabuco',
      'Belén',
      'Berbeo',
      'Betéitiva',
      'Boavita',
      'Boyacá',
      'Briceño',
      'Buenavista',
      'Busbanzá',
      'Caldas',
      'Campohermoso',
      'Cerinza',
      'Chinavita',
      'Chiquinquirá',
      'Chíquiza',
      'Chiscas',
      'Chita',
      'Chitaraque',
      'Chivatá',
      'Chivor',
      'Ciénega',
      'Cómbita',
      'Coper',
      'Corrales',
      'Covarachía',
      'Cubará',
      'Cucaita',
      'Cuítiva',
      'Duitama',
      'El Cocuy',
      'El Espino',
      'Firavitoba',
      'Floresta',
      'Gachantivá',
      'Gámeza',
      'Garagoa',
      'Guacamayas',
      'Guateque',
      'Guayatá',
      'Güicán',
      'Iza',
      'Jenesano',
      'Jericó',
      'La Capilla',
      'La Uvita',
      'La Victoria',
      'Labranzagrande',
      'Macanal',
      'Maripí',
      'Miraflores',
      'Mongua',
      'Monguí',
      'Moniquirá',
      'Motavita',
      'Muzo',
      'Nobsa',
      'Nuevo Colón',
      'Oicatá',
      'Otanche',
      'Pachavita',
      'Páez',
      'Paipa',
      'Pajarito',
      'Panqueba',
      'Pauna',
      'Paya',
      'Paz de Río',
      'Pesca',
      'Pisba',
      'Puerto Boyacá',
      'Quípama',
      'Ramiriquí',
      'Ráquira',
      'Rondón',
      'Saboyá',
      'Sáchica',
      'Samacá',
      'San Eduardo',
      'San José de Pare',
      'San Luis de Gaceno',
      'San Mateo',
      'San Miguel de Sema',
      'San Pablo de Borbur',
      'Santa María',
      'Santa Rosa de Viterbo',
      'Santa Sofía',
      'Santana',
      'Sativanorte',
      'Sativasur',
      'Siachoque',
      'Soatá',
      'Socha',
      'Socotá',
      'Sogamoso',
      'Somondoco',
      'Sora',
      'Soracá',
      'Sotaquirá',
      'Susacón',
      'Sutamarchán',
      'Sutatenza',
      'Tasco',
      'Tenza',
      'Tibaná',
      'Tibasosa',
      'Tinjacá',
      'Tipacoque',
      'Toca',
      'Togüí',
      'Tópaga',
      'Tota',
      'Tunja',
      'Tununguá',
      'Turmequé',
      'Tuta',
      'Tutazá',
      'Úmbita',
      'Ventaquemada',
      'Villa de Leyva',
      'Viracachá',
      'Zetaquira',
    ],
  },
  {
    name: 'Caldas',
    municipalities: [
      'Aguadas',
      'Anserma',
      'Aranzazu',
      'Belalcázar',
      'Chinchiná',
      'Filadelfia',
      'La Dorada',
      'La Merced',
      'Manizales',
      'Manzanares',
      'Marmato',
      'Marquetalia',
      'Marulanda',
      'Neira',
      'Norcasia',
      'Pácora',
      'Palestina',
      'Pensilvania',
      'Riosucio',
      'Risaralda',
      'Salamina',
      'Samaná',
      'San José',
      'Supía',
      'Victoria',
      'Villamaría',
      'Viterbo',
    ],
  },
  {
    name: 'Caquetá',
    municipalities: [
      'Albania',
      'Belén de los Andaquíes',
      'Cartagena del Chairá',
      'Curillo',
      'El Doncello',
      'El Paujil',
      'Florencia',
      'La Montañita',
      'Milán',
      'Morelia',
      'Puerto Rico',
      'San José del Fragua',
      'San Vicente del Caguán',
      'Solano',
      'Solita',
      'Valparaíso',
    ],
  },
  {
    name: 'Casanare',
    municipalities: [
      'Aguazul',
      'Chámeza',
      'Hato Corozal',
      'La Salina',
      'Maní',
      'Monterrey',
      'Nunchía',
      'Orocué',
      'Paz de Ariporo',
      'Pore',
      'Recetor',
      'Sabanalarga',
      'Sácama',
      'San Luis de Palenque',
      'Támara',
      'Tauramena',
      'Trinidad',
      'Villanueva',
      'Yopal',
    ],
  },
  {
    name: 'Cauca',
    municipalities: [
      'Almaguer',
      'Argelia',
      'Balboa',
      'Bolívar',
      'Buenos Aires',
      'Cajibío',
      'Caldono',
      'Caloto',
      'Corinto',
      'El Tambo',
      'Florencia',
      'Guapi',
      'Inzá',
      'Jambaló',
      'La Sierra',
      'La Vega',
      'López',
      'Mercaderes',
      'Miranda',
      'Morales',
      'Padilla',
      'Páez',
      'Patía',
      'Piamonte',
      'Piendamó',
      'Popayán',
      'Puerto Tejada',
      'Puracé',
      'Rosas',
      'San Sebastián',
      'Santa Rosa',
      'Santander de Quilichao',
      'Silvia',
      'Sotará',
      'Suárez',
      'Sucre',
      'Timbío',
      'Timbiquí',
      'Toribío',
      'Totoró',
      'Villa Rica',
    ],
  },
  {
    name: 'Cesar',
    municipalities: [
      'Aguachica',
      'Agustín Codazzi',
      'Astrea',
      'Becerril',
      'Bosconia',
      'Chimichagua',
      'Chiriguaná',
      'Curumaní',
      'El Copey',
      'El Paso',
      'Gamarra',
      'González',
      'La Gloria',
      'La Jagua de Ibirico',
      'La Paz',
      'Manaure',
      'Pailitas',
      'Pelaya',
      'Pueblo Bello',
      'Río de Oro',
      'San Alberto',
      'San Diego',
      'San Martín',
      'Tamalameque',
      'Valledupar',
    ],
  },
  {
    name: 'Chocó',
    municipalities: [
      'Acandí',
      'Alto Baudó',
      'Atrato',
      'Bagadó',
      'Bahía Solano',
      'Bajo Baudó',
      'Bojayá',
      'Carmen del Darién',
      'Cértegui',
      'Condoto',
      'El Cantón del San Pablo',
      'El Carmen de Atrato',
      'El Litoral del San Juan',
      'Istmina',
      'Juradó',
      'Lloró',
      'Medio Atrato',
      'Medio Baudó',
      'Medio San Juan',
      'Nóvita',
      'Nuquí',
      'Quibdó',
      'Río Iró',
      'Río Quito',
      'Riosucio',
      'San José del Palmar',
      'Sipí',
      'Tadó',
      'Unguía',
      'Unión Panamericana',
    ],
  },
  {
    name: 'Córdoba',
    municipalities: [
      'Ayapel',
      'Buenavista',
      'Canalete',
      'Cereté',
      'Chimá',
      'Chinú',
      'Ciénaga de Oro',
      'Cotorra',
      'La Apartada',
      'Lorica',
      'Los Córdobas',
      'Momil',
      'Moñitos',
      'Montelíbano',
      'Montería',
      'Planeta Rica',
      'Pueblo Nuevo',
      'Puerto Escondido',
      'Puerto Libertador',
      'Purísima',
      'Sahagún',
      'San Andrés Sotavento',
      'San Antero',
      'San Bernardo del Viento',
      'San Carlos',
      'San José de Uré',
      'San Pelayo',
      'Tierralta',
      'Tuchín',
      'Valencia',
    ],
  },
  {
    name: 'Cundinamarca',
    municipalities: [
      'Agua de Dios',
      'Albán',
      'Anapoima',
      'Anolaima',
      'Apulo',
      'Arbeláez',
      'Beltrán',
      'Bituima',
      'Bojacá',
      'Cabrera',
      'Cachipay',
      'Cajicá',
      'Caparrapí',
      'Caqueza',
      'Carmen de Carupa',
      'Chaguaní',
      'Chía',
      'Chipaque',
      'Choachí',
      'Chocontá',
      'Cogua',
      'Cota',
      'Cucunubá',
      'El Colegio',
      'El Peñón',
      'El Rosal',
      'Facatativá',
      'Fómeque',
      'Fosca',
      'Funza',
      'Fúquene',
      'Fusagasugá',
      'Gachalá',
      'Gachancipá',
      'Gachetá',
      'Gama',
      'Girardot',
      'Granada',
      'Guachetá',
      'Guaduas',
      'Guasca',
      'Guataquí',
      'Guatavita',
      'Guayabal de Síquima',
      'Guayabetal',
      'Gutiérrez',
      'Jerusalén',
      'Junín',
      'La Calera',
      'La Mesa',
      'La Palma',
      'La Peña',
      'La Vega',
      'Lenguazaque',
      'Machetá',
      'Madrid',
      'Manta',
      'Medina',
      'Mosquera',
      'Nariño',
      'Nemocón',
      'Nilo',
      'Nimaima',
      'Nocaima',
      'Pacho',
      'Paime',
      'Pandi',
      'Paratebueno',
      'Pasca',
      'Puerto Salgar',
      'Pulí',
      'Quebradanegra',
      'Quetame',
      'Quipile',
      'Ricaurte',
      'San Antonio del Tequendama',
      'San Bernardo',
      'San Cayetano',
      'San Francisco',
      'San Juan de Río Seco',
      'Sasaima',
      'Sesquilé',
      'Sibaté',
      'Silvania',
      'Simijaca',
      'Soacha',
      'Sopó',
      'Subachoque',
      'Suesca',
      'Supatá',
      'Susa',
      'Sutatausa',
      'Tabio',
      'Tausa',
      'Tena',
      'Tenjo',
      'Tibacuy',
      'Tibirita',
      'Tocaima',
      'Tocancipá',
      'Topaipí',
      'Úbalá',
      'Ubaque',
      'Une',
      'Útica',
      'Venecia',
      'Vergara',
      'Viani',
      'Villa de San Diego de Ubaté',
      'Villagómez',
      'Villapinzón',
      'Villeta',
      'Viotá',
      'Yacopí',
      'Zipacón',
      'Zipaquirá',
    ],
  },
  {
    name: 'Guainía',
    municipalities: [
      'Barranco Minas',
      'Cacahual',
      'Guadalupe',
      'Inírida',
      'Mapiripana',
      'Morichal',
      'Pana Pana',
      'Puerto Colombia',
      'San Felipe',
    ],
  },
  {
    name: 'Guaviare',
    municipalities: ['Calamar', 'El Retorno', 'Miraflores', 'San José del Guaviare'],
  },
  {
    name: 'Huila',
    municipalities: [
      'Acevedo',
      'Agrado',
      'Aipe',
      'Algeciras',
      'Altamira',
      'Baraya',
      'Campoalegre',
      'Colombia',
      'Elías',
      'Garzón',
      'Gigante',
      'Guadalupe',
      'Hobo',
      'Íquira',
      'Isnos',
      'La Argentina',
      'La Plata',
      'Nátaga',
      'Neiva',
      'Oporapa',
      'Paicol',
      'Palermo',
      'Palestina',
      'Pital',
      'Pitalito',
      'Rivera',
      'Saladoblanco',
      'San Agustín',
      'Santa María',
      'Suaza',
      'Tarqui',
      'Tello',
      'Teruel',
      'Tesalia',
      'Timaná',
      'Villavieja',
      'Yaguará',
    ],
  },
  {
    name: 'La Guajira',
    municipalities: [
      'Albania',
      'Barrancas',
      'Dibulla',
      'Distracción',
      'El Molino',
      'Fonseca',
      'Hatonuevo',
      'La Jagua del Pilar',
      'Maicao',
      'Manaure',
      'Riohacha',
      'San Juan del Cesar',
      'Uribia',
      'Urumita',
      'Villanueva',
    ],
  },
  {
    name: 'Magdalena',
    municipalities: [
      'Algarrobo',
      'Aracataca',
      'Ariguani',
      'Cerro San Antonio',
      'Chibolo',
      'Cienaga',
      'Concordia',
      'El Banco',
      'El Piñon',
      'El Reten',
      'Fundacion',
      'Guamal',
      'Nueva Granada',
      'Pedraza',
      'Pijiño del Carmen',
      'Pivijay',
      'Plato',
      'Puebloviejo',
      'Remolino',
      'Sabanas de San Angel',
      'Salamina',
      'San Sebastian de Buenavista',
      'San Zenon',
      'Santa Ana',
      'Santa Barbara de Pinto',
      'Santa Marta',
      'Sitionuevo',
      'Tenerife',
      'Zapayan',
      'Zona Bananera',
    ],
  },
  {
    name: 'Meta',
    municipalities: [
      'Acacias',
      'Barranca de Upia',
      'Cabuyaro',
      'Castilla la Nueva',
      'Cubarral',
      'Cumaral',
      'El Calvario',
      'El Castillo',
      'El Dorado',
      'Fuente de Oro',
      'Granada',
      'Guamal',
      'La Macarena',
      'Lejanias',
      'Mapiripan',
      'Mesetas',
      'Puerto Concordia',
      'Puerto Gaitan',
      'Puerto Lleras',
      'Puerto Lopez',
      'Puerto Rico',
      'Restrepo',
      'San Carlos de Guaroa',
      'San Juan de Arama',
      'San Juanito',
      'San Martin',
      'Uribe',
      'Villavicencio',
      'Vistahermosa',
    ],
  },
  {
    name: 'Nariño',
    municipalities: [
      'Aldana',
      'Ancuya',
      'Arboleda - Berruecos',
      'Barbacoas',
      'Belen',
      'Buesaco',
      'Chachagüi',
      'Colon',
      'Consaca',
      'Contadero',
      'Cordoba',
      'Cuaspud',
      'Cumbal',
      'Cumbitara',
      'El Charco',
      'El Peñol',
      'El Rosario',
      'El Tablon de Gomez',
      'El Tambo',
      'Francisco Pizarro',
      'Funes',
      'Guachucal',
      'Guaitarilla',
      'Gualmatan',
      'Iles',
      'Imues',
      'Ipiales',
      'La Cruz',
      'La Florida',
      'La Llanada',
      'La Tola',
      'La Union',
      'Leiva',
      'Linares',
      'Los Andes',
      'Magüi',
      'Mallama',
      'Mosquera',
      'Nariño',
      'Olaya Herrera',
      'Ospina',
      'Pasto',
      'Policarpa',
      'Potosi',
      'Providencia',
      'Puerres',
      'Pupiales',
      'Ricaurte',
      'Roberto Payan',
      'Samaniego',
      'San Andres de Tumaco',
      'San Bernardo',
      'San Jose de Alban',
      'San Lorenzo',
      'San Pablo',
      'San Pedro de Cartago',
      'Sandona',
      'Santa Barbara',
      'Santacruz',
      'Sapuyes',
      'Taminango',
      'Tangua',
      'Tuquerres',
      'Yacuanquer',
    ],
  },
  {
    name: 'Norte de Santander',
    municipalities: [
      'Abrego',
      'Arboledas',
      'Bochalema',
      'Bucarasica',
      'Cachira',
      'Cacota',
      'Chinacota',
      'Chitaga',
      'Convencion',
      'Cucuta',
      'Cucutilla',
      'Durania',
      'El Carmen',
      'El Tarra',
      'El Zulia',
      'Gramalote',
      'Hacari',
      'Herran',
      'La Esperanza',
      'La Playa',
      'Labateca',
      'Los Patios',
      'Lourdes',
      'Mutiscua',
      'Ocaña',
      'Pamplona',
      'Pamplonita',
      'Puerto Santander',
      'Ragonvalia',
      'Salazar',
      'San Calixto',
      'San Cayetano',
      'Santiago',
      'Sardinata',
      'Silos',
      'Teorama',
      'Tibu',
      'Toledo',
      'Villa Caro',
      'Villa del Rosario',
    ],
  },
  {
    name: 'Putumayo',
    municipalities: [
      'Colon',
      'Leguizamo',
      'Mocoa',
      'Orito',
      'Puerto Asis',
      'Puerto Caicedo',
      'Puerto Guzman',
      'San Francisco',
      'San Miguel',
      'Santiago',
      'Sibundoy',
      'Valle del Guamuez',
      'Villagarzon',
    ],
  },
  {
    name: 'Quindío',
    municipalities: [
      'Armenia',
      'Buenavista',
      'Calarca',
      'Circasia',
      'Cordoba',
      'Filandia',
      'Genova',
      'La Tebaida',
      'Montenegro',
      'Pijao',
      'Quimbaya',
      'Salento',
    ],
  },
  {
    name: 'Risaralda',
    municipalities: [
      'Apia',
      'Balboa',
      'Belen de Umbria',
      'Dosquebradas',
      'Guatica',
      'La Celia',
      'La Virginia',
      'Marsella',
      'Mistrato',
      'Pereira',
      'Pueblo Rico',
      'Quinchia',
      'Santa Rosa de Cabal',
      'Santuario',
    ],
  },
  {
    name: 'San Andrés y Providencia',
    municipalities: ['San Andrés', 'Providencia'],
  },
  {
    name: 'Santander',
    municipalities: [
      'Aguada',
      'Albania',
      'Aratoca',
      'Barbosa',
      'Barichara',
      'Barrancabermeja',
      'Betulia',
      'Bolivar',
      'Bucaramanga',
      'Cabrera',
      'California',
      'Capitanejo',
      'Carcasi',
      'Cepita',
      'Cerrito',
      'Charala',
      'Charta',
      'Chima',
      'Chipata',
      'Cimitarra',
      'Concepcion',
      'Confines',
      'Contratacion',
      'Coromoro',
      'Curiti',
      'El Carmen de Chucuri',
      'El Guacamayo',
      'El Peñon',
      'El Playon',
      'Encino',
      'Enciso',
      'Florian',
      'Floridablanca',
      'Galan',
      'Gambita',
      'Giron',
      'Guaca',
      'Guadalupe',
      'Guapota',
      'Guavata',
      'Güepsa',
      'Hato',
      'Jesus Maria',
      'Jordan',
      'La Belleza',
      'La Paz',
      'Landazuri',
      'Lebrija',
      'Los Santos',
      'Macaravita',
      'Malaga',
      'Matanza',
      'Mogotes',
      'Molagavita',
      'Ocamonte',
      'Oiba',
      'Onzaga',
      'Palmar',
      'Palmas del Socorro',
      'Paramo',
      'Piedecuesta',
      'Pinchote',
      'Puente Nacional',
      'Puerto Parra',
      'Puerto Wilches',
      'Rionegro',
      'Sabana de Torres',
      'San Andres',
      'San Benito',
      'San Gil',
      'San Joaquin',
      'San Jose de Miranda',
      'San Miguel',
      'San Vicente de Chucuri',
      'Santa Barbara',
      'Santa Helena del Opon',
      'Simacota',
      'Socorro',
      'Suaita',
      'Sucre',
      'Surata',
      'Tona',
      'Valle de San Jose',
      'Velez',
      'Vetas',
      'Villanueva',
      'Zapatoca',
    ],
  },
  {
    name: 'Sucre',
    municipalities: [
      'Buenavista',
      'Caimito',
      'Chalan',
      'Coloso',
      'Corozal',
      'Coveñas',
      'El Roble',
      'Galeras',
      'Guaranda',
      'La Union',
      'Los Palmitos',
      'Majagual',
      'Morroa',
      'Ovejas',
      'Palmito',
      'Sampues',
      'San Benito Abad',
      'San Juan de Betulia',
      'San Luis de Since',
      'San Marcos',
      'San Onofre',
      'San Pedro',
      'Santiago de Tolu',
      'Sincelejo',
      'Sucre',
      'Tolu Viejo',
    ],
  },
  {
    name: 'Tolima',
    municipalities: [
      'Alpujarra',
      'Alvarado',
      'Ambalema',
      'Anzoategui',
      'Armero',
      'Ataco',
      'Cajamarca',
      'Carmen de Apicala',
      'Casabianca',
      'Chaparral',
      'Coello',
      'Coyaima',
      'Cunday',
      'Dolores',
      'Espinal',
      'Falan',
      'Flandes',
      'Fresno',
      'Guamo',
      'Herveo',
      'Honda',
      'Ibague',
      'Icononzo',
      'Lerida',
      'Libano',
      'Mariquita',
      'Melgar',
      'Murillo',
      'Natagaima',
      'Ortega',
      'Palocabildo',
      'Piedras',
      'Planadas',
      'Prado',
      'Purificacion',
      'Rioblanco',
      'Roncesvalles',
      'Rovira',
      'Saldaña',
      'San Antonio',
      'San Luis',
      'Santa Isabel',
      'Suarez',
      'Valle de San Juan',
      'Venadillo',
      'Villahermosa',
      'Villarrica',
    ],
  },
  {
    name: 'Valle del Cauca',
    municipalities: [
      'Alcala',
      'Andalucia',
      'Ansermanuevo',
      'Argelia',
      'Bolivar',
      'Buenaventura',
      'Bugalagrande',
      'Caicedonia',
      'Cali',
      'Calima',
      'Candelaria',
      'Cartago',
      'Dagua',
      'El Aguila',
      'El Cairo',
      'El Cerrito',
      'El Dovio',
      'Florida',
      'Ginebra',
      'Guacari',
      'Guadalajara de Buga',
      'Jamundi',
      'La Cumbre',
      'La Union',
      'La Victoria',
      'Obando',
      'Palmira',
      'Pradera',
      'Restrepo',
      'Riofrio',
      'Roldanillo',
      'San Pedro',
      'Sevilla',
      'Toro',
      'Trujillo',
      'Tulua',
      'Ulloa',
      'Versalles',
      'Vijes',
      'Yotoco',
      'Yumbo',
      'Zarzal',
    ],
  },
  {
    name: 'Vaupés',
    municipalities: ['Caruru', 'Mitu', 'Pacoa', 'Papunaua', 'Taraira', 'Yavarate'],
  },
  {
    name: 'Vichada',
    municipalities: ['Cumaribo', 'La Primavera', 'Puerto Carreño', 'Santa Rosalia'],
  },
];

export const PROVINCES_CRI = [
  {
    name: 'San José',
    cantons: [
      'San José',
      'Escazú',
      'Desamparados',
      'Puriscal',
      'Tarrazú',
      'Aserrí',
      'Mora',
      'Goicoechea',
      'Santa Ana',
      'Alajuelita',
      'Vásquez de Coronado',
      'Acosta',
      'Tibás',
      'Moravia',
      'Montes de Oca',
      'Turrubares',
      'Dota',
      'Curridabat',
      'Pérez Zeledón',
      'León Cortés',
    ],
  },
  {
    name: 'Alajuela',
    cantons: [
      'Alajuela',
      'San Ramón',
      'Grecia',
      'San Mateo',
      'Atenas',
      'Naranjo',
      'Palmares',
      'Poás',
      'Orotina',
      'Zarcero',
      'Valverde Vega',
      'Upala',
      'Los Chiles',
      'Guatuso',
      'Río Cuarto',
    ],
  },
  {
    name: 'Cartago',
    cantons: [
      'Cartago',
      'Paraíso',
      'La Unión',
      'Jiménez',
      'Turrialba',
      'Alvarado',
      'Oreamuno',
      'El Guarco',
    ],
  },
  {
    name: 'Heredia',
    cantons: [
      'Heredia',
      'Barva',
      'Santo Domingo',
      'Santa Bárbara',
      'San Rafael',
      'San Isidro',
      'Belén',
      'Flores',
      'San Pablo',
      'Sarapiquí',
    ],
  },
  {
    name: 'Guanacaste',
    cantons: [
      'Liberia',
      'Nicoya',
      'Santa Cruz',
      'Bagaces',
      'Carrillo',
      'Cañas',
      'Abangares',
      'Tilarán',
      'Nandayure',
      'La Cruz',
      'Hojancha',
    ],
  },
  {
    name: 'Puntarenas',
    cantons: [
      'Puntarenas',
      'Esparza',
      'Buenos Aires',
      'Montes de Oro',
      'Osa',
      'Quepos',
      'Golfito',
      'Coto Brus',
      'Parrita',
      'Corredores',
      'Garabito',
    ],
  },
  {
    name: 'Limón',
    cantons: ['Limón', 'Pococí', 'Siquirres', 'Talamanca', 'Matina', 'Guácimo'],
  },
];

export const PROVINCES_PAN = [
  {
    name: 'Bocas del Toro',
    districts: ['Bocas del Toro', 'Changuinola', 'Chiriquí Grande'],
  },
  {
    name: 'Coclé',
    districts: ['Aguadulce', 'Antón', 'La Pintada', 'Natá', 'Olá', 'Penonomé'],
  },
  {
    name: 'Colón',
    districts: ['Colón', 'Chagres', 'Donoso', 'Portobelo', 'Santa Isabel'],
  },
  {
    name: 'Chiriquí',
    districts: [
      'Alanje',
      'Barú',
      'Boquerón',
      'Boquete',
      'Bugaba',
      'David',
      'Dolega',
      'Gualaca',
      'Remedios',
      'Renacimiento',
      'San Félix',
      'San Lorenzo',
      'Tolé',
    ],
  },
  {
    name: 'Darien',
    districts: ['Chepigana', 'Pinogana'],
  },
  {
    name: 'Herrera',
    districts: ['Chitré', 'Las Minas', 'Los Pozos', 'Ocú', 'Parita', 'Pesé', 'Santa María'],
  },
  {
    name: 'Los Santos',
    districts: ['Guararé', 'Las Tablas', 'Los Santos', 'Macaracas', 'Pedasí', 'Pocrí', 'Tonosí'],
  },
  {
    name: 'Panamá',
    districts: ['Balboa', 'Chepo', 'Chimán', 'Panamá', 'San Miguelito', 'Taboga'],
  },
  {
    name: 'Panamá Oeste',
    districts: ['Arraiján', 'Capira', 'Chame', 'La Chorrera', 'San Carlos'],
  },
  {
    name: 'Veraguas',
    districts: [
      'Atalaya',
      'Calobre',
      'Cañazas',
      'La Mesa',
      'Las Palmas',
      'Mariato',
      'Montijo',
      'Río de Jesús',
      'San Francisco',
      'Santa Fé',
      'Santiago',
      'Soná',
    ],
  },
  {
    name: 'Comarca Guna Yala',
    districts: ['Ailigandí', 'Cartí Sugdup', 'Narganá'],
  },
  {
    name: 'Comarca Emberá-Wounaan',
    districts: ['Cémaco', 'Sambú'],
  },
  {
    name: 'Comarca Ngäbe-Buglé',
    districts: ['Besikó', 'Kankintú', 'Kusapín', 'Mironó', 'Müna', 'Nole Duima'],
  },
];

export const DEPARTMENTS_GTM = [
  {
    name: 'Alta Verapaz',
    municipalities: [
      'Cobán',
      'Santa Cruz Verapaz',
      'San Cristóbal Verapaz',
      'Tactic',
      'Tamahú',
      'San Miguel Tucurú',
      'Panzós',
      'Senahú',
      'San Pedro Carchá',
      'San Juan Chamelco',
      'Lanquín',
      'Santa María Cahabón',
      'Chisec',
      'Chahal',
      'Fray Bartolomé de las Casas',
      'Santa Catalina La Tinta',
      'Raxruhá',
    ],
  },
  {
    name: 'Baja Verapaz',
    municipalities: [
      'Salamá',
      'San Miguel Chicaj',
      'Rabinal',
      'Cubulco',
      'Granados',
      'Santa Cruz El Chol',
      'San Jerónimo',
      'Purulhá',
    ],
  },
  {
    name: 'Chimaltenango',
    municipalities: [
      'Chimaltenango',
      'San José Poaquil',
      'San Martín Jilotepeque',
      'San Juan Comalapa',
      'Santa Apolonia',
      'Tecpán Guatemala',
      'Patzún',
      'San Miguel Pochuta',
      'Patzicía',
      'Santa Cruz Balanyá',
      'Acatenango',
      'San Pedro Yepocapa',
      'San Andrés Itzapa',
      'Parramos',
      'Zaragoza',
      'El Tejar',
    ],
  },
  {
    name: 'Chiquimula',
    municipalities: [
      'Chiquimula',
      'San José La Arada',
      'San Juan Ermita',
      'Jocotán',
      'Camotán',
      'Olopa',
      'Esquipulas',
      'Concepción Las Minas',
      'Quezaltepeque',
      'San Jacinto',
      'Ipala',
    ],
  },
  {
    name: 'El Progreso',
    municipalities: [
      'Guastatoya',
      'Morazán',
      'San Agustín Acasaguastlán',
      'San Cristóbal Acasaguastlán',
      'El Jícaro',
      'Sansare',
      'Sanarate',
      'San Antonio La Paz',
    ],
  },
  {
    name: 'Escuintla',
    municipalities: [
      'Escuintla',
      'Santa Lucía Cotzumalguapa',
      'La Democracia',
      'Siquinalá',
      'Masagua',
      'Tiquisate',
      'La Gomera',
      'Guanagazapa',
      'San José',
      'Iztapa',
      'Palín',
      'San Vicente Pacaya',
      'Nueva Concepción',
    ],
  },
  {
    name: 'Guatemala',
    municipalities: [
      'Guatemala',
      'Santa Catarina Pinula',
      'San José Pinula',
      'San José del Golfo',
      'Palencia',
      'Chinautla',
      'San Pedro Ayampuc',
      'Mixco',
      'San Pedro Sacatepéquez',
      'San Juan Sacatepéquez',
      'San Raymundo',
      'Chuarrancho',
      'Fraijanes',
      'Amatitlán',
      'Villa Nueva',
      'Villa Canales',
      'San Miguel Petapa',
    ],
  },
  {
    name: 'Huehuetenango',
    municipalities: [
      'Huehuetenango',
      'Chiantla',
      'Malacatancito',
      'Cuilco',
      'Nentón',
      'San Pedro Necta',
      'Jacaltenango',
      'San Pedro Soloma',
      'San Ildelfonso Ixtahuacán',
      'Santa Bárbara',
      'La Libertad',
      'La Democracia',
      'San Miguel Acatán',
      'San Rafael La Independencia',
      'Todos Santos Cuchumatán',
      'San Juan Atitán',
      'Santa Eulalia',
      'San Mateo Ixtatán',
      'Colotenango',
      'San Sebastián Huehuetenango',
      'Tectitán',
      'Concepción Huista',
      'San Juan Ixcoy',
      'San Antonio Huista',
      'San Sebastián Coatán',
      'Santa Cruz Barillas',
      'Aguacatán',
      'San Rafael Petzal',
      'San Gaspar Ixchil',
      'Santiago Chimaltenango',
      'Santa Ana Huista',
    ],
  },
  {
    name: 'Izabal',
    municipalities: ['Puerto Barrios', 'Livingston', 'El Estor', 'Morales', 'Los Amates'],
  },
  {
    name: 'Jalapa',
    municipalities: [
      'Jalapa',
      'San Pedro Pinula',
      'San Luis Jilotepeque',
      'San Manuel Chaparrón',
      'San Carlos Alzatate',
      'Monjas',
      'Mataquescuintla',
    ],
  },
  {
    name: 'Jutiapa',
    municipalities: [
      'Jutiapa',
      'El Progreso',
      'Santa Catarina Mita',
      'Agua Blanca',
      'Asunción Mita',
      'Yupiltepeque',
      'Atescatempa',
      'Jerez',
      'El Adelanto',
      'Zapotitlán',
      'Comapa',
      'Jalpatagua',
      'Conguaco',
      'Moyuta',
      'Pasaco',
      'San José Acatempa',
      'Quesada',
    ],
  },
  {
    name: 'Petén',
    municipalities: [
      'Flores',
      'San José',
      'San Benito',
      'San Andrés',
      'La Libertad',
      'San Francisco',
      'Santa Ana',
      'Dolores',
      'San Luis',
      'Sayaxché',
      'Melchor de Mencos',
      'Poptún',
      'El Chal',
      'Las Cruces',
    ],
  },
  {
    name: 'Quetzaltenango',
    municipalities: [
      'Quetzaltenango',
      'Salcajá',
      'Olintepeque',
      'San Carlos Sija',
      'Sibilia',
      'Cabricán',
      'Cajolá',
      'San Miguel Siguilá',
      'San Juan Ostuncalco',
      'San Mateo',
      'Concepción Chiquirichapa',
      'San Martín Sacatepéquez',
      'Almolonga',
      'Cantel',
      'Huitán',
      'Zunil',
      'Colomba',
      'San Francisco La Unión',
      'El Palmar',
      'Coatepeque',
      'Génova',
      'Flores Costa Cuca',
      'La Esperanza',
      'Palestina de Los Altos',
    ],
  },
  {
    name: 'Quiché',
    municipalities: [
      'Santa Cruz del Quiché',
      'Chiché',
      'Chinique',
      'Zacualpa',
      'Chajul',
      'Chichicastenango',
      'Patzité',
      'San Antonio Ilotenango',
      'San Pedro Jocopilas',
      'Cunén',
      'San Juan Cotzal',
      'Joyabaj',
      'Nebaj',
      'San Andrés Sajcabajá',
      'Uspantán',
      'Sacapulas',
      'San Bartolomé Jocotenango',
      'Canillá',
      'Chicamán',
      'Ixcán',
      'Pachalum',
    ],
  },
  {
    name: 'Retalhuleu',
    municipalities: [
      'Retalhuleu',
      'San Sebastián',
      'Santa Cruz Muluá',
      'San Martín Zapotitlán',
      'San Felipe',
      'San Andrés Villa Seca',
      'Champerico',
      'Nuevo San Carlos',
      'El Asintal',
    ],
  },
  {
    name: 'Sacatepéquez',
    municipalities: [
      'Antigua Guatemala',
      'Jocotenango',
      'Pastores',
      'Sumpango',
      'Santo Domingo Xenacoj',
      'Santiago Sacatepéquez',
      'San Bartolomé Milpas Altas',
      'San Lucas Sacatepéquez',
      'Santa Lucía Milpas Altas',
      'Magdalena Milpas Altas',
      'Santa María de Jesús',
      'Ciudad Vieja',
      'San Miguel Dueñas',
      'San Juan Alotenango',
      'San Antonio Aguas Calientes',
      'Santa Catarina Barahona',
    ],
  },
  {
    name: 'San Marcos',
    municipalities: [
      'San Marcos',
      'San Pedro Sacatepéquez',
      'San Antonio Sacatepéquez',
      'Comitancillo',
      'San Miguel Ixtahuacán',
      'Concepción Tutuapa',
      'Tacaná',
      'Sibinal',
      'Tajumulco',
      'Tejutla',
      'San Rafael Pie de la Cuesta',
      'Nuevo Progreso',
      'El Tumbador',
      'El Rodeo',
      'Malacatán',
      'Catarina',
      'Ayutla',
      'Ocós',
      'San Pablo',
      'El Quetzal',
      'La Reforma',
      'Pajapita',
      'Ixchiguán',
      'San José Ojetenam',
      'San Cristóbal Cucho',
      'Sipacapa',
      'Esquipulas Palo Gordo',
      'Río Blanco',
      'San Lorenzo',
    ],
  },
  {
    name: 'Santa Rosa',
    municipalities: [
      'Cuilapa',
      'Barberena',
      'Santa Rosa de Lima',
      'Casillas',
      'San Rafael Las Flores',
      'Oratorio',
      'San Juan Tecuaco',
      'Chiquimulilla',
      'Taxisco',
      'Santa María Ixhuatán',
      'Guazacapán',
      'Santa Cruz Naranjo',
      'Pueblo Nuevo Viñas',
      'Nueva Santa Rosa',
    ],
  },
  {
    name: 'Sololá',
    municipalities: [
      'Sololá',
      'San José Chacayá',
      'Santa María Visitación',
      'Santa Lucía Utatlán',
      'Nahualá',
      'Santa Catarina Ixtahuacán',
      'Santa Clara La Laguna',
      'Concepción',
      'San Andrés Semetabaj',
      'Panajachel',
      'Santa Catarina Palopó',
      'San Antonio Palopó',
      'San Lucas Tolimán',
      'Santa Cruz La Laguna',
      'San Pablo La Laguna',
      'San Marcos La Laguna',
      'San Juan La Laguna',
      'San Pedro La Laguna',
      'Santiago Atitlán',
    ],
  },
  {
    name: 'Suchitepéquez',
    municipalities: [
      'Mazatenango',
      'Cuyotenango',
      'San Francisco Zapotitlán',
      'San Bernardino',
      'San José El Idolo',
      'Santo Domingo Suchitepéquez',
      'San Lorenzo',
      'Samayac',
      'San Pablo Jocopilas',
      'San Antonio Suchitepéquez',
      'San Miguel Panán',
      'San Gabriel',
      'Chicacao',
      'Patulul',
      'Santa Bárbara',
      'San Juan Bautista',
      'Santo Tomás La Unión',
      'Zunilito',
      'Pueblo Nuevo',
      'Río Bravo',
    ],
  },
  {
    name: 'Totonicapán',
    municipalities: [
      'Totonicapán',
      'San Cristóbal Totonicapán',
      'San Francisco El Alto',
      'San Andrés Xecul',
      'Momostenango',
      'Santa María Chiquimula',
      'Santa Lucía La Reforma',
      'San Bartolo',
    ],
  },
  {
    name: 'Zacapa',
    municipalities: [
      'Zacapa',
      'Estanzuela',
      'Río Hondo',
      'Gualán',
      'Teculután',
      'Usumatlán',
      'Cabañas',
      'San Diego',
      'La Unión',
      'Huité',
    ],
  },
];

export const STATES = [
  {
    name: 'Acre',
    municipalities: [
      'Rio Branco',
      'Cruzeiro do Sul',
      'Sena Madureira',
      'Tarauacá',
      'Feijó',
      'Brasileia',
      'Epitaciolândia',
      'Xapuri',
      'Mâncio Lima',
      'Plácido de Castro',
      'Senador Guiomard',
      'Marechal Thaumaturgo',
      'Porto Walter',
      'Assis Brasil',
      'Rodrigues Alves',
      'Acrelândia',
      'Bujari',
      'Capixaba',
      'Jordão',
      'Manoel Urbano',
      'Porto Acre',
      'Santa Rosa do Purus',
    ].sort(),
  },
  {
    name: 'Alagoas',
    municipalities: [
      'Maceió',
      'Arapiraca',
      'Rio Largo',
      'Palmeira dos Índios',
      'União dos Palmares',
      'Penedo',
      'São Miguel dos Campos',
      'Coruripe',
      'Delmiro Gouveia',
      'Campo Alegre',
      'Marechal Deodoro',
      'Santana do Ipanema',
      'Atalaia',
      'Girau do Ponciano',
      'São Luís do Quitunde',
      'Teotônio Vilela',
      'Pilar',
      'Igaci',
      'São Sebastião',
      'Matriz de Camaragibe',
      'Joaquim Gomes',
      'Viçosa',
      'Porto Calvo',
      'Murici',
      'Boca da Mata',
    ].sort(),
  },
  {
    name: 'Amapá',
    municipalities: [
      'Macapá',
      'Santana',
      'Laranjal do Jari',
      'Oiapoque',
      'Mazagão',
      'Porto Grande',
      'Tartarugalzinho',
      'Pedra Branca do Amapari',
      'Vitória do Jari',
      'Calçoene',
      'Amapá',
      'Ferreira Gomes',
      'Cutias',
      'Itaubal',
      'Pracuúba',
      'Serra do Navio',
    ].sort(),
  },
  {
    name: 'Amazonas',
    municipalities: [
      'Manaus',
      'Parintins',
      'Itacoatiara',
      'Manacapuru',
      'Coari',
      'Tefé',
      'Tabatinga',
      'Maués',
      'Iranduba',
      'Humaitá',
      'São Gabriel da Cachoeira',
      'Lábrea',
      'Manicoré',
      'Borba',
      'Careiro',
      'Eirunepé',
      'Nova Olinda do Norte',
      'Boca do Acre',
      'Carauari',
      'Barreirinha',
      'Presidente Figueiredo',
    ].sort(),
  },
  {
    name: 'Bahia',
    municipalities: [
      'Salvador',
      'Feira de Santana',
      'Vitória da Conquista',
      'Camaçari',
      'Itabuna',
      'Juazeiro',
      'Lauro de Freitas',
      'Ilhéus',
      'Jequié',
      'Teixeira de Freitas',
      'Barreiras',
      'Alagoinhas',
      'Porto Seguro',
      'Simões Filho',
      'Paulo Afonso',
      'Eunápolis',
      'Santo Antônio de Jesus',
      'Valença',
      'Candeias',
      'Guanambi',
      'Jacobina',
    ].sort(),
  },
  {
    name: 'Ceará',
    municipalities: [
      'Fortaleza',
      'Caucaia',
      'Juazeiro do Norte',
      'Maracanaú',
      'Sobral',
      'Crato',
      'Itapipoca',
      'Maranguape',
      'Iguatu',
      'Quixadá',
      'Pacatuba',
      'Quixeramobim',
      'Aquiraz',
      'Canindé',
      'Tianguá',
      'Pacajus',
      'Russas',
      'Aracati',
      'Cascavel',
      'Icó',
    ].sort(),
  },
  {
    name: 'Distrito Federal',
    municipalities: [
      'Brasília',
      'Ceilândia',
      'Taguatinga',
      'Samambaia',
      'Plano Piloto',
      'Planaltina',
      'Águas Claras',
      'Recanto das Emas',
      'Gama',
      'Guará',
      'Santa Maria',
      'Sobradinho II',
      'São Sebastião',
      'Vicente Pires',
      'Itapoã',
      'Sobradinho',
      'Sudoeste/Octogonal',
      'Riacho Fundo II',
      'Brazlândia',
      'Paranoá',
    ].sort(),
  },
  {
    name: 'Espírito Santo',
    municipalities: [
      'Vila Velha',
      'Serra',
      'Cariacica',
      'Vitória',
      'Cachoeiro de Itapemirim',
      'Linhares',
      'São Mateus',
      'Guarapari',
      'Colatina',
      'Aracruz',
      'Nova Venécia',
      'Barra de São Francisco',
      'Santa Maria de Jetibá',
      'Castelo',
      'Marataízes',
      'Itapemirim',
      'São Gabriel da Palha',
      'Viana',
      'Domingos Martins',
      'Afonso Cláudio',
    ].sort(),
  },
  {
    name: 'Goiás',
    municipalities: [
      'Goiânia',
      'Aparecida de Goiânia',
      'Anápolis',
      'Rio Verde',
      'Luziânia',
      'Águas Lindas de Goiás',
      'Valparaíso de Goiás',
      'Trindade',
      'Formosa',
      'Novo Gama',
      'Senador Canedo',
      'Catalão',
      'Itumbiara',
      'Jataí',
      'Planaltina',
      'Caldas Novas',
      'Santo Antônio do Descoberto',
      'Goianésia',
      'Cidade Ocidental',
      'Mineiros',
    ].sort(),
  },
  {
    name: 'Maranhão',
    municipalities: [
      'São Luís',
      'Imperatriz',
      'São José de Ribamar',
      'Timon',
      'Caxias',
      'Codó',
      'Paço do Lumiar',
      'Açailândia',
      'Bacabal',
      'Balsas',
      'Santa Inês',
      'Barra do Corda',
      'Pinheiro',
      'Chapadinha',
      'Santa Luzia',
      'Buriticupu',
      'Itapecuru Mirim',
      'Grajaú',
      'Coroatá',
      'Tutóia',
    ].sort(),
  },
  {
    name: 'Mato Grosso',
    municipalities: [
      'Cuiabá',
      'Várzea Grande',
      'Rondonópolis',
      'Sinop',
      'Tangará da Serra',
      'Cáceres',
      'Sorriso',
      'Lucas do Rio Verde',
      'Primavera do Leste',
      'Barra do Garças',
      'Alta Floresta',
      'Nova Mutum',
      'Pontes e Lacerda',
      'Campo Verde',
      'Juína',
      'Guarantã do Norte',
      'Colíder',
      'Campo Novo do Parecis',
      'Juara',
      'Poxoréu',
    ].sort(),
  },
  {
    name: 'Mato Grosso do Sul',
    municipalities: [
      'Campo Grande',
      'Dourados',
      'Três Lagoas',
      'Corumbá',
      'Ponta Porã',
      'Naviraí',
      'Nova Andradina',
      'Sidrolândia',
      'Aquidauana',
      'Maracaju',
      'Paranaíba',
      'Coxim',
      'Amambai',
      'Rio Brilhante',
      'Ivinhema',
      'Cassilândia',
      'Jardim',
      'Anastácio',
      'Caarapó',
      'Aparecida do Taboado',
    ].sort(),
  },
  {
    name: 'Minas Gerais',
    municipalities: [
      'Belo Horizonte',
      'Uberlândia',
      'Contagem',
      'Juiz de Fora',
      'Betim',
      'Montes Claros',
      'Ribeirão das Neves',
      'Uberaba',
      'Governador Valadares',
      'Ipatinga',
      'Sete Lagoas',
      'Divinópolis',
      'Santa Luzia',
      'Ibirité',
      'Poços de Caldas',
      'Patos de Minas',
      'Pouso Alegre',
      'Teófilo Otoni',
      'Barbacena',
      'Sabará',
    ].sort(),
  },
  {
    name: 'Pará',
    municipalities: [
      'Belém',
      'Ananindeua',
      'Santarém',
      'Marabá',
      'Parauapebas',
      'Castanhal',
      'Altamira',
      'Tucuruí',
      'Bragança',
      'Capanema',
      'Brevibrasil',
      'Itaituba',
      'Cametá',
      'Soure',
      'Redenção',
      'Paragominas',
      'Tailândia',
      'Melgaço',
      'São Félix do Xingu',
      'Ulianópolis',
    ].sort(),
  },
  {
    name: 'Paraíba',
    municipalities: [
      'João Pessoa',
      'Campina Grande',
      'Santa Rita',
      'Patos',
      'Bayeux',
      'Cabo Branco',
      'São Bento',
      'Sousa',
      'Mamanguape',
      'Peloz',
      'Itabaiana',
      'Monteiro',
      'São João do Rio do Peixe',
      'Picuí',
      'Aparecida',
      'Cruz do Espírito Santo',
      'Esperança',
      'Queimadas',
      'Alagoa Grande',
      'Conde',
    ].sort(),
  },
  {
    name: 'Paraná',
    municipalities: [
      'Curitiba',
      'Londrina',
      'Maringá',
      'Ponta Grossa',
      'Cascavel',
      'Foz do Iguaçu',
      'São José dos Pinhais',
      'Guarapuava',
      'Umuarama',
      'Toledo',
      'Colombo',
      'Aracaju',
      'Castro',
      'Apucarana',
      'Paranaguá',
      'Rio Branco do Sul',
      'Lapa',
      'Campo Largo',
      'Prudentópolis',
      'Telêmaco Borba',
    ].sort(),
  },
  {
    name: 'Pernambuco',
    municipalities: [
      'Recife',
      'Olinda',
      'Jaboatão dos Guararapes',
      'Caruaru',
      'Petrolina',
      'Garanhuns',
      'Igarassu',
      'Camaragibe',
      'São Lourenço da Mata',
      'Arcoverde',
      'Goiana',
      'Santa Cruz do Capibaribe',
      'Paulista',
      'Cabrobó',
      'Belo Jardim',
      'Surubim',
      'Salgueiro',
      'Limoeiro',
      'Toritama',
      'Buarque de Macedo',
    ].sort(),
  },
  {
    name: 'Piauí',
    municipalities: [
      'Teresina',
      'Parnaíba',
      'Picos',
      'Floriano',
      'Piripiri',
      'Campo Maior',
      'Barras',
      'Oeiras',
      'José de Freitas',
      'Cocal',
      'Luzilândia',
      'São João do Piauí',
      'Uruçui',
      'Bertolínia',
      'Buriti dos Lopes',
      'Cajueiro da Praia',
      'Inhuma',
      'Pavussu',
      'Regeneração',
      'São Pedro do Piauí',
    ].sort(),
  },
  {
    name: 'Rio de Janeiro',
    municipalities: [
      'Rio de Janeiro',
      'Niterói',
      'Duque de Caxias',
      'Nova Iguaçu',
      'São Gonçalo',
      'Petrópolis',
      'Volta Redonda',
      'Macaé',
      'Belford Roxo',
      'Campo Grande',
      'Magé',
      'Teresópolis',
      'Itaboraí',
      'Angra dos Reis',
      'Resende',
      'Queimados',
      'Barra Mansa',
      'São João de Meriti',
      'Rio das Ostras',
      'Cabo Frio',
      'Araruama',
      'Saquarema',
      'Maricá',
      'Nilópolis',
      'Guapimirim',
      'Mangaratiba',
      'Paraty',
      'Três Rios',
      'Valença',
      'Itaguai',
      'Seropédica',
      'Santa Maria Madalena',
      'Bom Jardim',
      'Comendador Levy Gasparian',
      'Paty do Alferes',
      'Santo Antônio de Pádua',
      'Carmo',
      'Laje do Muriaé',
      'Mendes',
      'Sapucaia',
      'São José do Vale do Rio Preto',
      'Areal',
      'Rio das Flores',
      'Porciúncula',
      'Petrópolis',
      'Cabo Frio',
      'Conceição de Macabu',
      'Duas Barras',
      'Itaocara',
      'Itaperuna',
      'Laje do Muriaé',
      'Macuco',
      'Miracema',
      'Pindamonhangaba',
      'Santo Antônio de Pádua',
      'São Sebastião do Alto',
    ].sort(),
  },
  {
    name: 'Rio Grande do Norte',
    municipalities: [
      'Natal',
      'Mossoró',
      'Parnamirim',
      'São Gonçalo do Amarante',
      'Macau',
      'Caicó',
      'Currais Novos',
      'São Miguel',
      'Cerro Corá',
      'Açu',
      'Pau dos Ferros',
      'Nísia Floresta',
      'João Câmara',
      'Santa Cruz',
      'Santo Antônio',
      'Touros',
      'São Paulo do Potengi',
      'Piparanhas',
      'Jandaíra',
      'Canguaretama',
    ].sort(),
  },
  {
    name: 'Rio Grande do Sul',
    municipalities: [
      'Porto Alegre',
      'Canoas',
      'Pelotas',
      'Gravataí',
      'Santa Maria',
      'Uruguaiana',
      'Bagé',
      'São Leopoldo',
      'Novo Hamburgo',
      'Sapucaia do Sul',
      'Santana do Livramento',
      'Ijuí',
      'Caxias do Sul',
      'Lajeado',
      'Viamão',
      'Esteio',
      'Alvorada',
      'Frederico Westphalen',
      'Passo Fundo',
      'Tapes',
    ].sort(),
  },
  {
    name: 'Rondônia',
    municipalities: [
      'Porto Velho',
      'Ji-Paraná',
      'Vilhena',
      'Cacoal',
      'Rolim de Moura',
      'Ariquemes',
      'Guajará-Mirim',
      'Pimenta Bueno',
      'Buritis',
      'Jaru',
      'Ouro Preto do Oeste',
      'Nova Mamoré',
      'São Miguel do Guaporé',
      'Cerejeiras',
      'Alto Paraíso',
      'Mirante da Serra',
      'Castanheiras',
      'Nova União',
      'Chupinguaia',
      'Vale do Anari',
    ].sort(),
  },
  {
    name: 'Roraima',
    municipalities: [
      'Boa Vista',
      'Rorainópolis',
      'Caracaraí',
      'Iracema',
      'Mucajaí',
      'Cantá',
      'Normandia',
      'Uiramutã',
      'Bonfim',
      'São João da Baliza',
      'Pacaraima',
      'Amajari',
      'Caroebe',
      'São Luiz',
      'Bom Fim',
    ].sort(),
  },
  {
    name: 'Santa Catarina',
    municipalities: [
      'Florianópolis',
      'Joinville',
      'Blumenau',
      'Chapecó',
      'Criciúma',
      'Lages',
      'Itajaí',
      'São José',
      'Balneário Camboriú',
      'Jaraguá do Sul',
      'Palhoça',
      'Tubarão',
      'Camboriú',
      'São Bento do Sul',
      'Pomerode',
      'Rio do Sul',
      'Brusque',
      'Xanxerê',
      'Caçador',
      'Concórdia',
      'Lages',
      'Araranguá',
      'Sao Miguel do Oeste',
      'Balneário Piçarras',
      'Pouso Redondo',
      'Massaranduba',
      'Navegantes',
      'Nova Trento',
      'Guaramirim',
      'Tijucas',
      'Pinhalzinho',
      'Rio dos Cedros',
      'São João Batista',
      'Entre Rios',
      'Santo Amaro da Imperatriz',
      'Fração',
      'Santa Rosa do Sul',
      'Bela Vista do Toldo',
      'Mafra',
      'Indaial',
      'São João do Sul',
      'Urussanga',
      'Alfredo Wagner',
      'Trombudo Central',
      'Apiúna',
      'Braço do Norte',
      'Cerro Negro',
      'Cunha Porã',
      'Paial',
      'Passo de Torres',
      'São Francisco do Sul',
      'Tijucas do Sul',
      'Treze Tílias',
      'Vargem Bonita',
      'Zortéa',
    ].sort(),
  },
  {
    name: 'São Paulo',
    municipalities: [
      'São Paulo',
      'Guarulhos',
      'Campinas',
      'São Bernardo do Campo',
      'Santo André',
      'Osasco',
      'Sorocaba',
      'Ribeirão Preto',
      'São José dos Campos',
      'Diadema',
      'Mauá',
      'Jundiaí',
      'Carapicuíba',
      'Franca',
      'São Vicente',
      'Bauru',
      'Itapevi',
      'Barueri',
      'Maringá',
      'Botucatu',
      'São Carlos',
      'Itu',
      'Rio Claro',
      'Atibaia',
      'Pindamonhangaba',
      'Indaiatuba',
      'Taubaté',
      'Jaguariúna',
      'Jacareí',
      'Limeira',
      'Mogi das Cruzes',
      'Piracicaba',
      'São Roque',
      'Tatuí',
      'Votuporanga',
      'Santo Antônio de Posse',
      'Araraquara',
      'Itatiba',
      'Tatuí',
      'Vargem Grande do Sul',
      'Jundiaí',
      'Sertãozinho',
      'Capivari',
      'Botucatu',
      'Santa Bárbara Oeste',
      'Ibitinga',
      'Lins',
      'Piedade',
      'Itapeva',
      'Tremembé',
      'Mauá',
      'São Sebastião',
      'Paraguaçu Paulista',
      'Barretos',
      'Mirassol',
      'Pouso Alegre',
      'Avaré',
      'Bocaina',
      'Andradina',
      'Fernandópolis',
      'Itu',
      'Marília',
      'São João da Boa Vista',
      'Clementina',
      'Tupã',
      'Matão',
      'Mira Estrela',
      'Palmital',
      'Paranapanema',
      'Ribeirão Pires',
      'Santa Rita do Sapucaí',
      'Sorocaba',
      'Louveira',
      'Itapira',
      'Rafael Jambeiro',
      'Iperó',
      'Alumínio',
      'Tatuí',
      'Cunha',
    ].sort(),
  },
  {
    name: 'Sergipe',
    municipalities: [
      'Aracaju',
      'Nossa Senhora do Socorro',
      'Lagarto',
      'Itabaiana',
      'São Cristóvão',
      'Tobias Barreto',
      'Estância',
      'Simão Dias',
      'Propriá',
      'Carmópolis',
      'Barra dos Coqueiros',
      'Itabi',
      'Japoatã',
      'Telha',
      'Riachuelo',
      'Salgado',
      'Umbaúba',
      'Boquim',
      'Indiaroba',
      'Arauá',
    ].sort(),
  },
  {
    name: 'Tocantins',
    municipalities: [
      'Palmas',
      'Araguaína',
      'Gurupi',
      'Paraíso do Tocantins',
      'Miracema do Tocantins',
      'Porto Nacional',
      'Colinas do Tocantins',
      'Tocantinópolis',
      'Augustinópolis',
      'Palmeirante',
      'Silvanópolis',
      'Axixá do Tocantins',
      'São Sebastião do Tocantins',
      'Formoso do Araguaia',
      'Filadélfia',
      'Rio da Conceição',
      'Divinópolis do Tocantins',
      'Mateiros',
      'Lajeado',
      'Peixe',
    ].sort(),
  },
];

export const STATES_MEX = [
  {
    name: 'Aguascalientes',
    municipalities: [
      'Aguascalientes',
      'Asientos',
      'Calvillo',
      'Rincón de Romos',
      'Pabellón de Arteaga',
      'Jesús María',
    ].sort(),
  },
  {
    name: 'Baja California',
    municipalities: [
      'Mexicali',
      'Tijuana',
      'Ensenada',
      'Tecate',
      'Playas de Rosarito',
      'San Quintín',
    ].sort(),
  },
  {
    name: 'Baja California Sur',
    municipalities: ['La Paz', 'Los Cabos', 'Comondú', 'Loreto', 'Mulegé'],
  },
  {
    name: 'Campeche',
    municipalities: [
      'Campeche',
      'Ciudad del Carmen',
      'Champotón',
      'Escárcega',
      'Calkiní',
      'Hecelchakán',
    ].sort(),
  },
  {
    name: 'Chiapas',
    municipalities: [
      'Tuxtla Gutiérrez',
      'San Cristóbal de las Casas',
      'Tapachula',
      'Comitán',
      'Ocosingo',
      'Palenque',
    ].sort(),
  },
  {
    name: 'Chihuahua',
    municipalities: [
      'Chihuahua',
      'Ciudad Juárez',
      'Delicias',
      'Parral',
      'Camargo',
      'Meoqui',
    ].sort(),
  },
  {
    name: 'Ciudad de México',
    municipalities: [
      'Álvaro Obregón',
      'Azcapotzalco',
      'Benito Juárez',
      'Coyoacán',
      'Cuajimalpa de Morelos',
      'Cuauhtémoc',
      'Gustavo A. Madero',
      'Iztacalco',
      'Iztapalapa',
      'La Magdalena Contreras',
      'Miguel Hidalgo',
      'Milpa Alta',
      'Tláhuac',
      'Tlalpan',
      'Venustiano Carranza',
      'Xochimilco',
    ].sort(),
  },
  {
    name: 'Coahuila',
    municipalities: [
      'Saltillo',
      'Torreón',
      'Monclova',
      'Piedras Negras',
      'Ramos Arizpe',
      'Frontera',
    ].sort(),
  },
  {
    name: 'Colima',
    municipalities: ['Colima', 'Manzanillo', 'Tecomán', 'Villa de Álvarez', 'Coquimatlán'].sort(),
  },
  {
    name: 'Durango',
    municipalities: ['Durango', 'Gómez Palacio', 'Lerdo', 'Victoria de Durango', 'Canatlán'].sort(),
  },
  {
    name: 'Estado de México',
    municipalities: [
      'Toluca',
      'Naucalpan',
      'Ecatepec',
      'Nezahualcóyotl',
      'Chimalhuacán',
      'Tultitlán',
    ].sort(),
  },
  {
    name: 'Guanajuato',
    municipalities: [
      'Guanajuato',
      'León',
      'Irapuato',
      'Silao',
      'Celaya',
      'San Miguel de Allende',
    ].sort(),
  },
  {
    name: 'Guerrero',
    municipalities: [
      'Chilpancingo',
      'Acapulco',
      'Iguala',
      'Taxco',
      'Zihuatanejo',
      'Coyuca de Benítez',
    ].sort(),
  },
  {
    name: 'Hidalgo',
    municipalities: [
      'Pachuca',
      'Tulancingo',
      'Tula',
      'Tepeji del Río',
      'Huejutla',
      'Ixmiquilpan',
    ].sort(),
  },
  {
    name: 'Jalisco',
    municipalities: [
      'Guadalajara',
      'Zapopan',
      'Tlaquepaque',
      'Puerto Vallarta',
      'Tlajomulco',
      'Tepatitlán',
    ].sort(),
  },
  {
    name: 'Michoacán',
    municipalities: [
      'Morelia',
      'Uruapan',
      'Zamora',
      'Lázaro Cárdenas',
      'Apatzingán',
      'Pátzcuaro',
    ].sort(),
  },
  {
    name: 'Morelos',
    municipalities: [
      'Cuernavaca',
      'Jiutepec',
      'Temixco',
      'Cuautla',
      'Zacatepec',
      'Yautepec',
    ].sort(),
  },
  {
    name: 'Nayarit',
    municipalities: ['Tepic', 'Xalisco', 'Bahía de Banderas', 'Compostela', 'San Blas'].sort(),
  },
  {
    name: 'Nuevo León',
    municipalities: [
      'Monterrey',
      'Guadalupe',
      'San Nicolás',
      'Escobedo',
      'Apodaca',
      'San Pedro Garza García',
    ].sort(),
  },
  {
    name: 'Oaxaca',
    municipalities: ['Oaxaca de Juárez', 'Salina Cruz', 'Istmo', 'Juchitán', 'Tehuantepec'].sort(),
  },
  {
    name: 'Puebla',
    municipalities: [
      'Puebla',
      'Tehuacán',
      'Atlixco',
      'San Martín Texmelucan',
      'Amozoc',
      'Cholula',
    ].sort(),
  },
  {
    name: 'Querétaro',
    municipalities: [
      'Querétaro',
      'San Juan del Río',
      'El Marqués',
      'Amealco',
      'Corregidora',
    ].sort(),
  },
  {
    name: 'Quintana Roo',
    municipalities: ['Chetumal', 'Cancún', 'Playa del Carmen', 'Tulum', 'Isla Mujeres'].sort(),
  },
  {
    name: 'San Luis Potosí',
    municipalities: [
      'San Luis Potosí',
      'Ciudad Valles',
      'Matehuala',
      'Soledad de Graciano Sánchez',
      'Rioverde',
    ].sort(),
  },
  {
    name: 'Sinaloa',
    municipalities: ['Culiacán', 'Mazatlán', 'Los Mochis', 'Guasave', 'Navolato'].sort(),
  },
  {
    name: 'Sonora',
    municipalities: ['Hermosillo', 'Nogales', 'Cajeme', 'Guaymas', 'San Luis Río Colorado'].sort(),
  },
  {
    name: 'Tabasco',
    municipalities: ['Villahermosa', 'Cárdenas', 'Comalcalco', 'Balancán', 'Macuspana'].sort(),
  },
  {
    name: 'Tamaulipas',
    municipalities: ['Ciudad Victoria', 'Tampico', 'Reynosa', 'Matamoros', 'Altamira'].sort(),
  },
  {
    name: 'Tlaxcala',
    municipalities: ['Tlaxcala', 'Apizaco', 'Chiautempan', 'Huamantla', 'Calpulalpan'].sort(),
  },
  {
    name: 'Veracruz',
    municipalities: ['Veracruz', 'Xalapa', 'Coatzacoalcos', 'Poza Rica', 'Minatitlán'].sort(),
  },
  {
    name: 'Yucatán',
    municipalities: ['Mérida', 'Valladolid', 'Progreso', 'Tizimín', 'Kanasín'].sort(),
  },
  {
    name: 'Zacatecas',
    municipalities: ['Zacatecas', 'Guadalupe', 'Fresnillo', 'Río Grande', 'Jerez'].sort(),
  },
];

export const STATES_PH = [
  {
    name: 'Abra',
    municipalities: [
      'Bangued',
      'Boliney',
      'Bucay',
      'Bucloc',
      'Daguioman',
      'Danglas',
      'Dolores',
      'La Paz',
      'Lacub',
      'Lagangilang',
      'Lagayan',
      'Langiden',
      'Licuan-Baay',
      'Luba',
      'Malibcong',
      'Manabo',
      'Peñarrubia',
      'Pidigan',
      'Pilar',
      'Sallapadan',
      'San Isidro',
      'San Juan',
      'San Quintin',
      'Tayum',
      'Tineg',
      'Tubo',
      'Villaviciosa',
    ],
  },
  {
    name: 'Agusan del Norte',
    municipalities: [
      'Cabadbaran City',
      'Buenavista',
      'Carmen',
      'Jabonga',
      'Kitcharao',
      'Las Nieves',
      'Magallanes',
      'Nasipit',
      'Remedios T. Romualdez',
      'Santiago',
      'Tubay',
    ],
  },
  {
    name: 'Agusan del Sur',
    municipalities: [
      'Prosperidad',
      'Bayugan City',
      'Bunawan',
      'Esperanza',
      'La Paz',
      'Loreto',
      'Rosario',
      'San Francisco',
      'San Luis',
      'Santa Josefa',
      'Sibagat',
      'Talacogon',
      'Trento',
      'Veruela',
    ],
  },
  {
    name: 'Aklan',
    municipalities: [
      'Kalibo',
      'Altavas',
      'Balete',
      'Banga',
      'Batan',
      'Buruanga',
      'Ibajay',
      'Lezo',
      'Libacao',
      'Madalag',
      'Makato',
      'Malay',
      'Malinao',
      'Nabas',
      'New Washington',
      'Numancia',
      'Tangalan',
    ],
  },
  {
    name: 'Albay',
    municipalities: [
      'Legazpi City',
      'Ligao City',
      'Tabaco City',
      'Bacacay',
      'Camalig',
      'Daraga',
      'Guinobatan',
      'Jovellar',
      'Libon',
      'Malilipot',
      'Malinao',
      'Manito',
      'Oas',
      'Pio Duran',
      'Polangui',
      'Rapu-Rapu',
      'Santo Domingo',
      'Tiwi',
    ],
  },
  {
    name: 'Antique',
    municipalities: [
      'San Jose de Buenavista',
      'Anini-y',
      'Barbaza',
      'Belison',
      'Bugasong',
      'Caluya',
      'Culasi',
      'Hamtic',
      'Laua-an',
      'Libertad',
      'Pandan',
      'Patnongon',
      'San Remigio',
      'Sebaste',
      'Sibalom',
      'Tibiao',
      'Tobias Fornier',
      'Valderrama',
    ],
  },
  {
    name: 'Apayao',
    municipalities: ['Kabugao', 'Calanasan', 'Conner', 'Flora', 'Luna', 'Pudtol', 'Santa Marcela'],
  },
  {
    name: 'Aurora',
    municipalities: [
      'Baler',
      'Casiguran',
      'Dilasag',
      'Dinalungan',
      'Dingalan',
      'Dipaculao',
      'Maria Aurora',
      'San Luis',
    ],
  },
  {
    name: 'Basilan',
    municipalities: [
      'Lamitan City',
      'Isabela City',
      'Akbar',
      'Al-Barka',
      'Hadji Mohammad Ajul',
      'Lantawan',
      'Maluso',
      'Sumisip',
      'Tabuan-Lasa',
      'Tipo-Tipo',
      'Tuburan',
      'Ungkaya Pukan',
    ],
  },
  {
    name: 'Bataan',
    municipalities: [
      'Balanga City',
      'Abucay',
      'Bagac',
      'Dinalupihan',
      'Hermosa',
      'Limay',
      'Mariveles',
      'Morong',
      'Orani',
      'Orion',
      'Pilar',
      'Samal',
    ],
  },
];

export const ARG_ACTIVITIES = [
  { label: 'Relación de dependencia', value: 'Relación de dependencia' },
  { label: 'Independiente', value: 'Independiente' },
  { label: 'Estudiante', value: 'Estudiante' },
  { label: 'Desocupado', value: 'Desocupado' },
  { label: 'Jubilado', value: 'Jubilado' },
  { label: 'Pensionado', value: 'Pensionado' },
  { label: 'Labores de hogar', value: 'Labores de hogar' },
  { label: 'Monotributista', value: 'Monotributista' },
];

export const ARG_INDUSTRIES = [
  'Actividades administrativas y servicios de apoyo',
  'Administración pública, defensa y seguridad social obligatoria',
  'Agricultura, ganadería, caza, silvicultura y pesca',
  'Comercio al por mayor y al por menor; reparación de vehículos',
  'Construcción',
  'Enseñanza',
  'Explotación de minas y canteras',
  'Industria manufacturera',
  'Información y comunicaciones',
  'Intermediación financiera y servicios de seguros',
  'Salud humana y servicios sociales',
  'Servicios artísticos, culturales, deportivos y de esparcimiento',
  'Servicios de alojamiento y servicios de comida',
  'Servicios de asociaciones y servicios personales',
  'Servicios de hogares privados que contratan servicio doméstico',
  'Servicios de organizaciones y órganos extraterritoriales',
  'Servicios de transporte y almacenamiento',
  'Servicios inmobiliarios',
  'Servicios profesionales, científicos y técnicos',
  'Suministro de agua, cloacas, gestión de residuos y saneamiento público',
  'Suministro de electricidad, gas, vapor y aire acondicionado',
  'Otra',
];

export const CHL_ACTIVITIES = [
  { label: 'Relación de dependencia', value: 'Relación de dependencia' },
  { label: 'Independiente', value: 'Independiente' },
  { label: 'Estudiante', value: 'Estudiante' },
  { label: 'Desocupado', value: 'Desocupado' },
  { label: 'Jubilado', value: 'Jubilado' },
  { label: 'Pensionado', value: 'Pensionado' },
  { label: 'Labores de hogar', value: 'Labores de hogar' },
];

export const CHL_INDUSTRIES = [
  'AGRICULTURA, GANADERÍA, SILVICULTURA Y PESCA',
  'EXPLOTACIÓN DE MINAS Y CANTERAS',
  'INDUSTRIA MANUFACTURERA',
  'SUMINISTRO DE ELECTRICIDAD, GAS, VAPOR Y AIRE ACONDICIONADO',
  'SUMINISTRO DE AGUA; EVACUACIÓN DE AGUAS RESIDUALES, GESTIÓN DE DESECHOS Y DESCONTAMINACIÓN',
  'CONSTRUCCIÓN',
  'COMERCIO AL POR MAYOR Y AL POR MENOR; REPARACIÓN DE VEHICULOS AUTOMOTORES Y MOTOCICLETAS',
  'TRANSPORTE Y ALMACENAMIENTO',
  'ACTIVIDADES DE ALOJAMIENTO Y DE SERVICIO DE COMIDAS',
  'INFORMACIÓN Y COMUNICACIONES',
  'ACTIVIDADES FINANCIERAS Y DE SEGUROS',
  'ACTIVIDADES INMOBILIARIAS',
  'ACTIVIDADES PROFESIONALES, CIENTIFICAS Y TÉCNICAS',
  'ACTIVIDADES DE SERVICIOS ADMINISTRATIVOS Y DE APOYO',
  'ADMINISTRACIÓN PÚBLICA Y DEFENSA; PLANES DE SEGURIDAD SOCIAL DE AFILIACIÓN OBLIGATORIA',
  'ENSEÑANZA',
  'ACTIVIDADES DE ATENCIÓN DE LA SALUD HUMANA Y DE ASISTENCIA SOCIAL',
  'ACTIVIDADES ARTÍSTICAS, DE ENTRETENIMIENTO Y RECREATIVAS',
  'OTRAS ACTIVIDADES DE SERVICIOS',
  'ACTIVIDADES DE LOS HOGARES COMO EMPLEADORES; ACTIVIDADES NO DIFERENCIADAS DE LOS HOGARES',
  'ACTIVIDADES DE ORGANIZACIONES Y ÓRGANOS EXTRATERRITORIALES',
  'Otra',
].map((value) => {
  return value.charAt(0).toUpperCase() + value.substring(1).toLowerCase();
});

export const COL_ACTIVITIES = [
  { label: 'Independiente', value: 'Independiente' },
  { label: 'Asalariado', value: 'Asalariado' },
  { label: 'Pensionado', value: 'Pensionado' },
  { label: 'Desocupado', value: 'Desocupado' },
];

export const PH_ACTIVITIES = [
  { label: 'Nagtatrabaho', value: 'Asalariado' },
  { label: 'Walang trabaho', value: 'Desocupado' },
];

export const COL_INDUSTRIES = [
  'Agricultura, Ganadería, Caza, Silvicultura y Pesca',
  'Explotación de Minas y Canteras',
  'Industrias Manufactureras',
  'Suministro de Electricidad, Gas, Vapor y Aire Acondicionado',
  'Distribución de Agua; Evacuación y Tratamiento de Aguas Residuales',
  'Construcción',
  'Comercio al por Mayor y al por Menor; Reparación de Vehículos Automotores y Motocicletas',
  'Transporte y Almacenamiento',
  'Alojamiento y Servicios de Comida',
  'Información y Comunicaciones',
  'Actividades Financieras y de Seguros',
  'Actividades Inmobiliarias',
  'Actividades Profesionales, Científicas y Técnicas',
  'Actividades de Servicios Administrativos y de Apoyo',
  'Administración Pública y Defensa; Planes de Seguridad Social de Afiliación Obligatoria',
  'Educación',
  'Actividades de Atención de la Salud Humana y de Asistencia Social',
  'Actividades Artísticas, de Entretenimiento y Recreación',
  'Otras Actividades de Servicios',
  'Actividades de los Hogares Individuales en Calidad de Empleadores',
  'Actividades de Organizaciones y Entidades Extraterritoriales',
];

export const BRA_ACTIVITIES = [
  { label: 'Independente', value: 'Independiente' },
  { label: 'Assalariado', value: 'Asalariado' },
  { label: 'Aposentado', value: 'Pensionado' },
  { label: 'Desempregado', value: 'Desocupado' },
  { label: 'Estudante', value: 'Estudiante' },
];

export const BRA_INDUSTRIES = [
  'Agricultura, Pecuária, Caça, Silvicultura e Pesca',
  'Extração de Minas e Rocha',
  'Indústrias Manufatureiras',
  'Fornecimento de Eletricidade, Gás, Vapor e Ar Condicionado',
  'Distribuição de Água; Coleta e Tratamento de Esgotos',
  'Construção',
  'Comércio Atacadista e Varejista; Reparação de Veículos Automotores e Motocicletas',
  'Transporte e Armazenamento',
  'Alojamento e Serviços de Alimentação',
  'Informação e Comunicações',
  'Atividades Financeiras e de Seguros',
  'Atividades Imobiliárias',
  'Atividades Profissionais, Científicas e Técnicas',
  'Atividades de Serviços Administrativos e de Apoio',
  'Administração Pública e Defesa; Planos de Seguridade Social de Afiliados Obrigatórios',
  'Educação',
  'Atividades de Cuidado da Saúde Humana e Assistência Social',
  'Atividades Artísticas, de Entretenimento e Recreação',
  'Outras Atividades de Serviços',
  'Atividades de Domicílios Individuais como Empregadores',
  'Atividades de Organizações e Entidades Extraterritoriais',
];

export const CONFIG_BY_COUNTRY = {
  [EXCHANGE_COUNTRY.ARG]: {
    LEGAL_ID: FIELD.BANK_ACCOUNT_CODE_ARG,
    LEGAL_ID_NAME: 'CUIT/CUIL',
    SET_NATIONALITY: 'Argentina',
    BANK_ACCOUNT: {
      LABEL: 'CBU/CVU/ALIAS',
      HEADER: 'pesos argentinos',
      CURRENCY: ACCOUNT_CURRENCY.ARS,
    },
    DOC_NAME: 'DNI',
    TEXT_BACK: 'Parte de atrás',
  },
  [EXCHANGE_COUNTRY.CHL]: {
    LEGAL_ID: FIELD.RUT,
    LEGAL_ID_NAME: 'RUT',
    SET_NATIONALITY: 'Chile',
    BANK_ACCOUNT: {
      LABEL: 'Número de cuenta',
      HEADER: 'pesos chilenos',
      ACCOUNT_TYPE: ACCOUNT_TYPE_BANK_CHILE,
      BANK_CODE: BANK_CODE_CHILE,
      CURRENCY: ACCOUNT_CURRENCY.CLP,
    },
    DOC_NAME: 'CARNET',
    TEXT_BACK: 'Parte de atrás',
  },
  [EXCHANGE_COUNTRY.COL]: {
    LEGAL_ID: FIELD.CC,
    LEGAL_ID_NAME: 'Cédula de ciudadanía',
    SET_NATIONALITY: 'Colombia',
    BANK_ACCOUNT: {
      LABEL: 'Número de cuenta',
      HEADER: 'pesos colombianos',
      ACCOUNT_TYPE: ACCOUNT_TYPE_BANK_DEFAULT,
      BANK_CODE: BANKS_CODE_COL,
      CURRENCY: ACCOUNT_CURRENCY.COP,
    },
    DOC_NAME: 'carnet de identificación',
    TEXT_BACK: 'Parte de atrás',
  },
  [EXCHANGE_COUNTRY.BRA]: {
    LEGAL_ID: FIELD.CPF,
    LEGAL_ID_NAME: 'CPF',
    SET_NATIONALITY: 'Brasil',
    BANK_ACCOUNT: {
      LABEL: 'Celular',
      HEADER: 'reais',
      CURRENCY: ACCOUNT_CURRENCY.BRL,
    },
    DOC_NAME: 'carteira de identidade',
    TEXT_BACK: 'Parte de atrás',
  },
  [EXCHANGE_COUNTRY.GTM]: {
    LEGAL_ID: FIELD.CUI,
    LEGAL_ID_NAME: 'Código único de identificación',
    SET_NATIONALITY: 'Guatemala',
    BANK_ACCOUNT: {
      LABEL: 'Número de cuenta',
      HEADER: 'quetzales',
      ACCOUNT_TYPE: ACCOUNT_TYPE_BANK_DEFAULT,
      BANK_CODE: BANKS_CODE_GTM,
      CURRENCY: ACCOUNT_CURRENCY.GTQ,
    },
    DOC_NAME: 'documento de identificación',
    TEXT_BACK: 'Parte de atrás',
  },
  [EXCHANGE_COUNTRY.PAN]: {
    LEGAL_ID: FIELD.RUC,
    LEGAL_ID_NAME: 'Registro único de contribuyentes',
    SET_NATIONALITY: 'Panamá',
    BANK_ACCOUNT: {
      LABEL: 'Número de cuenta',
      HEADER: 'Panama usd',
      ACCOUNT_TYPE: ACCOUNT_TYPE_BANK_DEFAULT,
      BANK_CODE: BANKS_CODE_PAN,
      CURRENCY: ACCOUNT_CURRENCY.PUSD,
    },
    DOC_NAME: 'documento de identificación',
    TEXT_BACK: 'Parte de atrás',
  },
  [EXCHANGE_COUNTRY.CRI]: {
    LEGAL_ID: FIELD.CI,
    LEGAL_ID_NAME: 'Cédula de identidad',
    SET_NATIONALITY: 'Costa Rica',
    BANK_ACCOUNT: {
      LABEL: 'Número de cuenta',
      HEADER: 'Colones',
      ACCOUNT_TYPE: ACCOUNT_TYPE_BANK_DEFAULT,
      BANK_CODE: BANKS_CODE_CRI,
      CURRENCY: ACCOUNT_CURRENCY.CRC,
    },
    DOC_NAME: 'documento de identificación',
    TEXT_BACK: 'Parte de atrás',
  },
  [EXCHANGE_COUNTRY.MEX]: {
    LEGAL_ID: FIELD.CURP,
    LEGAL_ID_NAME: 'CURP',
    SET_NATIONALITY: 'México',
    BANK_ACCOUNT: {
      LABEL: 'Número de cuenta',
      HEADER: 'pesos mexicanos',
      ACCOUNT_TYPE: ACCOUNT_TYPE_MEX,
      BANK_CODE: BANKS_CODE_MEX,
      CURRENCY: ACCOUNT_CURRENCY.MXN,
    },
    DOC_NAME: 'documento de identificación',
    TEXT_BACK: 'Parte de atrás',
  },
  [EXCHANGE_COUNTRY.PHL]: {
    LEGAL_ID: FIELD.PCN,
    LEGAL_ID_NAME: 'Philippine National ID',
    SET_NATIONALITY: 'Filipinas',
    BANK_ACCOUNT: {
      LABEL: 'Account number',
      HEADER: 'Philippine Peso',
      ACCOUNT_TYPE: ACCOUNT_TYPE_PHL,
      BANK_CODE: BANKS_CODE_PH,
      CURRENCY: ACCOUNT_CURRENCY.PHP,
    },
    DOC_NAME: 'Identification document',
    TEXT_BACK: 'Back side',
  },
};

export const COUNTRIES = [
  { value: 'Afganistán', label: 'Afganistán' },
  { value: 'Albania', label: 'Albania' },
  { value: 'Alemania', label: 'Alemania' },
  { value: 'Andorra', label: 'Andorra' },
  { value: 'Angola', label: 'Angola' },
  { value: 'Anguila', label: 'Anguila' },
  { value: 'Antártida', label: 'Antártida' },
  { value: 'Antigua y Barbuda', label: 'Antigua y Barbuda' },
  { value: 'Arabia Saudita', label: 'Arabia Saudita' },
  { value: 'Argelia', label: 'Argelia' },
  { value: 'Argentina', label: 'Argentina' },
  { value: 'Armenia', label: 'Armenia' },
  { value: 'Aruba', label: 'Aruba' },
  { value: 'Australia', label: 'Australia' },
  { value: 'Austria', label: 'Austria' },
  { value: 'Azerbaiyán', label: 'Azerbaiyán' },
  { value: 'Bahamas', label: 'Bahamas' },
  { value: 'Baréin', label: 'Baréin' },
  { value: 'Bangladesh', label: 'Bangladesh' },
  { value: 'Barbados', label: 'Barbados' },
  { value: 'Bélgica', label: 'Bélgica' },
  { value: 'Belice', label: 'Belice' },
  { value: 'Benín', label: 'Benín' },
  { value: 'Bermudas', label: 'Bermudas' },
  { value: 'Bielorrusia', label: 'Bielorrusia' },
  { value: 'Birmania', label: 'Birmania' },
  { value: 'Bolivia', label: 'Bolivia' },
  { value: 'Bosnia y Herzegovina', label: 'Bosnia y Herzegovina' },
  { value: 'Botsuana', label: 'Botsuana' },
  { value: 'Brasil', label: 'Brasil' },
  { value: 'Brunéi', label: 'Brunéi' },
  { value: 'Bulgaria', label: 'Bulgaria' },
  { value: 'Burkina Faso', label: 'Burkina Faso' },
  { value: 'Burundi', label: 'Burundi' },
  { value: 'Bután', label: 'Bután' },
  { value: 'Cabo Verde', label: 'Cabo Verde' },
  { value: 'Camboya', label: 'Camboya' },
  { value: 'Camerún', label: 'Camerún' },
  { value: 'Canadá', label: 'Canadá' },
  { value: 'Chad', label: 'Chad' },
  { value: 'Chile', label: 'Chile' },
  { value: 'China', label: 'China' },
  { value: 'Chipre', label: 'Chipre' },
  { value: 'Ciudad del Vaticano', label: 'Ciudad del Vaticano' },
  { value: 'Colombia', label: 'Colombia' },
  { value: 'Comoras', label: 'Comoras' },
  { value: 'Corea del Norte', label: 'Corea del Norte' },
  { value: 'Corea del Sur', label: 'Corea del Sur' },
  { value: 'Costa de Marfil', label: 'Costa de Marfil' },
  { value: 'Costa Rica', label: 'Costa Rica' },
  { value: 'Croacia', label: 'Croacia' },
  { value: 'Cuba', label: 'Cuba' },
  { value: 'Dinamarca', label: 'Dinamarca' },
  { value: 'Dominica', label: 'Dominica' },
  { value: 'Ecuador', label: 'Ecuador' },
  { value: 'Egipto', label: 'Egipto' },
  { value: 'El Salvador', label: 'El Salvador' },
  { value: 'Emiratos Árabes Unidos', label: 'Emiratos Árabes Unidos' },
  { value: 'Eritrea', label: 'Eritrea' },
  { value: 'Eslovaquia', label: 'Eslovaquia' },
  { value: 'Eslovenia', label: 'Eslovenia' },
  { value: 'España', label: 'España' },
  { value: 'Estados Unidos', label: 'Estados Unidos' },
  { value: 'Estonia', label: 'Estonia' },
  { value: 'Etiopía', label: 'Etiopía' },
  { value: 'Filipinas', label: 'Filipinas' },
  { value: 'Finlandia', label: 'Finlandia' },
  { value: 'Fiyi', label: 'Fiyi' },
  { value: 'Francia', label: 'Francia' },
  { value: 'Gabón', label: 'Gabón' },
  { value: 'Gambia', label: 'Gambia' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Ghana', label: 'Ghana' },
  { value: 'Granada', label: 'Granada' },
  { value: 'Grecia', label: 'Grecia' },
  { value: 'Guatemala', label: 'Guatemala' },
  { value: 'Guinea', label: 'Guinea' },
  { value: 'Guinea-Bisáu', label: 'Guinea-Bisáu' },
  { value: 'Guinea Ecuatorial', label: 'Guinea Ecuatorial' },
  { value: 'Guyana', label: 'Guyana' },
  { value: 'Haití', label: 'Haití' },
  { value: 'Honduras', label: 'Honduras' },
  { value: 'Hungría', label: 'Hungría' },
  { value: 'India', label: 'India' },
  { value: 'Indonesia', label: 'Indonesia' },
  { value: 'Irak', label: 'Irak' },
  { value: 'Irán', label: 'Irán' },
  { value: 'Irlanda', label: 'Irlanda' },
  { value: 'Islandia', label: 'Islandia' },
  { value: 'Israel', label: 'Israel' },
  { value: 'Italia', label: 'Italia' },
  { value: 'Jamaica', label: 'Jamaica' },
  { value: 'Japón', label: 'Japón' },
  { value: 'Jordania', label: 'Jordania' },
  { value: 'Kazajistán', label: 'Kazajistán' },
  { value: 'Kenia', label: 'Kenia' },
  { value: 'Kirguistán', label: 'Kirguistán' },
  { value: 'Kiribati', label: 'Kiribati' },
  { value: 'Kosovo', label: 'Kosovo' },
  { value: 'Kuwait', label: 'Kuwait' },
  { value: 'Laos', label: 'Laos' },
  { value: 'Lesoto', label: 'Lesoto' },
  { value: 'Letonia', label: 'Letonia' },
  { value: 'Líbano', label: 'Líbano' },
  { value: 'Liberia', label: 'Liberia' },
  { value: 'Libia', label: 'Libia' },
  { value: 'Liechtenstein', label: 'Liechtenstein' },
  { value: 'Lituania', label: 'Lituania' },
  { value: 'Luxemburgo', label: 'Luxemburgo' },
  { value: 'Macedonia del Norte', label: 'Macedonia del Norte' },
  { value: 'Madagascar', label: 'Madagascar' },
  { value: 'Malasia', label: 'Malasia' },
  { value: 'Malaui', label: 'Malaui' },
  { value: 'Maldivas', label: 'Maldivas' },
  { value: 'Malí', label: 'Malí' },
  { value: 'Malta', label: 'Malta' },
  { value: 'Marruecos', label: 'Marruecos' },
  { value: 'Islas Marshall', label: 'Islas Marshall' },
  { value: 'Mauricio', label: 'Mauricio' },
  { value: 'Mauritania', label: 'Mauritania' },
  { value: 'México', label: 'México' },
  { value: 'Micronesia', label: 'Micronesia' },
  { value: 'Moldavia', label: 'Moldavia' },
  { value: 'Mónaco', label: 'Mónaco' },
  { value: 'Mongolia', label: 'Mongolia' },
  { value: 'Montenegro', label: 'Montenegro' },
  { value: 'Mozambique', label: 'Mozambique' },
  { value: 'Namibia', label: 'Namibia' },
  { value: 'Nauru', label: 'Nauru' },
  { value: 'Nepal', label: 'Nepal' },
  { value: 'Nicaragua', label: 'Nicaragua' },
  { value: 'Níger', label: 'Níger' },
  { value: 'Nigeria', label: 'Nigeria' },
  { value: 'Noruega', label: 'Noruega' },
  { value: 'Nueva Zelanda', label: 'Nueva Zelanda' },
  { value: 'Omán', label: 'Omán' },
  { value: 'Países Bajos', label: 'Países Bajos' },
  { value: 'Pakistán', label: 'Pakistán' },
  { value: 'Palaos', label: 'Palaos' },
  { value: 'Panamá', label: 'Panamá' },
  { value: 'Papúa Nueva Guinea', label: 'Papúa Nueva Guinea' },
  { value: 'Paraguay', label: 'Paraguay' },
  { value: 'Perú', label: 'Perú' },
  { value: 'Polonia', label: 'Polonia' },
  { value: 'Portugal', label: 'Portugal' },
  { value: 'Catar', label: 'Catar' },
  { value: 'Reino Unido', label: 'Reino Unido' },
  { value: 'República Centroafricana', label: 'República Centroafricana' },
  { value: 'República Checa', label: 'República Checa' },
  { value: 'República Democrática del Congo', label: 'República Democrática del Congo' },
  { value: 'República Dominicana', label: 'República Dominicana' },
  { value: 'Ruanda', label: 'Ruanda' },
  { value: 'Rumania', label: 'Rumania' },
  { value: 'Rusia', label: 'Rusia' },
  { value: 'Samoa', label: 'Samoa' },
  { value: 'San Cristóbal y Nieves', label: 'San Cristóbal y Nieves' },
  { value: 'San Marino', label: 'San Marino' },
  { value: 'San Vicente y las Granadinas', label: 'San Vicente y las Granadinas' },
  { value: 'Santa Lucía', label: 'Santa Lucía' },
  { value: 'Santo Tomé y Príncipe', label: 'Santo Tomé y Príncipe' },
  { value: 'Senegal', label: 'Senegal' },
  { value: 'Serbia', label: 'Serbia' },
  { value: 'Seychelles', label: 'Seychelles' },
  { value: 'Sierra Leona', label: 'Sierra Leona' },
  { value: 'Singapur', label: 'Singapur' },
  { value: 'Siria', label: 'Siria' },
  { value: 'Somalia', label: 'Somalia' },
  { value: 'Sri Lanka', label: 'Sri Lanka' },
  { value: 'Suazilandia', label: 'Suazilandia' },
  { value: 'Sudáfrica', label: 'Sudáfrica' },
  { value: 'Sudán', label: 'Sudán' },
  { value: 'Sudán del Sur', label: 'Sudán del Sur' },
  { value: 'Suecia', label: 'Suecia' },
  { value: 'Suiza', label: 'Suiza' },
  { value: 'Surinam', label: 'Surinam' },
  { value: 'Tailandia', label: 'Tailandia' },
  { value: 'Tanzania', label: 'Tanzania' },
  { value: 'Tayikistán', label: 'Tayikistán' },
  { value: 'Timor Oriental', label: 'Timor Oriental' },
  { value: 'Togo', label: 'Togo' },
  { value: 'Tonga', label: 'Tonga' },
  { value: 'Trinidad y Tobago', label: 'Trinidad y Tobago' },
  { value: 'Túnez', label: 'Túnez' },
  { value: 'Turkmenistán', label: 'Turkmenistán' },
  { value: 'Turquía', label: 'Turquía' },
  { value: 'Tuvalu', label: 'Tuvalu' },
  { value: 'Ucrania', label: 'Ucrania' },
  { value: 'Uganda', label: 'Uganda' },
  { value: 'Uruguay', label: 'Uruguay' },
  { value: 'Uzbekistán', label: 'Uzbekistán' },
  { value: 'Vanuatu', label: 'Vanuatu' },
  { value: 'Venezuela', label: 'Venezuela' },
  { value: 'Vietnam', label: 'Vietnam' },
  { value: 'Yemen', label: 'Yemen' },
  { value: 'Yibuti', label: 'Yibuti' },
  { value: 'Zambia', label: 'Zambia' },
  { value: 'Zimbabue', label: 'Zimbabue' },
];

export const COUNTRIES_BR = [
  { label: 'Afeganistão', value: 'Afganistán' },
  { label: 'Albânia', value: 'Albania' },
  { label: 'Alemanha', value: 'Alemania' },
  { label: 'Andorra', value: 'Andorra' },
  { label: 'Angola', value: 'Angola' },
  { label: 'Anguilla', value: 'Anguila' },
  { label: 'Antártida', value: 'Antártida' },
  { label: 'Antígua e Barbuda', value: 'Antigua y Barbuda' },
  { label: 'Arábia Saudita', value: 'Arabia Saudita' },
  { label: 'Argélia', value: 'Argelia' },
  { label: 'Argentina', value: 'Argentina' },
  { label: 'Armênia', value: 'Armenia' },
  { label: 'Aruba', value: 'Aruba' },
  { label: 'Austrália', value: 'Australia' },
  { label: 'Áustria', value: 'Austria' },
  { label: 'Azerbaijão', value: 'Azerbaiyán' },
  { label: 'Bahamas', value: 'Bahamas' },
  { label: 'Bahrein', value: 'Baréin' },
  { label: 'Bangladesh', value: 'Bangladesh' },
  { label: 'Barbados', value: 'Barbados' },
  { label: 'Bélgica', value: 'Bélgica' },
  { label: 'Belize', value: 'Belice' },
  { label: 'Benin', value: 'Benín' },
  { label: 'Bermudas', value: 'Bermudas' },
  { label: 'Bielorrússia', value: 'Bielorrusia' },
  { label: 'Mianmar', value: 'Birmania' },
  { label: 'Bolívia', value: 'Bolivia' },
  { label: 'Bósnia e Herzegovina', value: 'Bosnia y Herzegovina' },
  { label: 'Botsuana', value: 'Botsuana' },
  { label: 'Brasil', value: 'Brasil' },
  { label: 'Brunei', value: 'Brunéi' },
  { label: 'Bulgária', value: 'Bulgaria' },
  { label: 'Burkina Faso', value: 'Burkina Faso' },
  { label: 'Burundi', value: 'Burundi' },
  { label: 'Butão', value: 'Bután' },
  { label: 'Cabo Verde', value: 'Cabo Verde' },
  { label: 'Camboja', value: 'Camboya' },
  { label: 'Camarões', value: 'Camerún' },
  { label: 'Canadá', value: 'Canadá' },
  { label: 'Chade', value: 'Chad' },
  { label: 'Chile', value: 'Chile' },
  { label: 'China', value: 'China' },
  { label: 'Chipre', value: 'Chipre' },
  { label: 'Cidade do Vaticano', value: 'Ciudad del Vaticano' },
  { label: 'Colômbia', value: 'Colombia' },
  { label: 'Comores', value: 'Comoras' },
  { label: 'Coreia do Norte', value: 'Corea del Norte' },
  { label: 'Coreia do Sul', value: 'Corea del Sur' },
  { label: 'Costa do Marfim', value: 'Costa de Marfil' },
  { label: 'Costa Rica', value: 'Costa Rica' },
  { label: 'Croácia', value: 'Croacia' },
  { label: 'Cuba', value: 'Cuba' },
  { label: 'Dinamarca', value: 'Dinamarca' },
  { label: 'Dominica', value: 'Dominica' },
  { label: 'Equador', value: 'Ecuador' },
  { label: 'Egito', value: 'Egipto' },
  { label: 'El Salvador', value: 'El Salvador' },
  { label: 'Emirados Árabes Unidos', value: 'Emiratos Árabes Unidos' },
  { label: 'Eritreia', value: 'Eritrea' },
  { label: 'Eslováquia', value: 'Eslovaquia' },
  { label: 'Eslovênia', value: 'Eslovenia' },
  { label: 'Espanha', value: 'España' },
  { label: 'Estados Unidos', value: 'Estados Unidos' },
  { label: 'Estônia', value: 'Estonia' },
  { label: 'Etiópia', value: 'Etiopía' },
  { label: 'Filipinas', value: 'Filipinas' },
  { label: 'Finlândia', value: 'Finlandia' },
  { label: 'Fiji', value: 'Fiyi' },
  { label: 'França', value: 'Francia' },
  { label: 'Gabão', value: 'Gabón' },
  { label: 'Gâmbia', value: 'Gambia' },
  { label: 'Geórgia', value: 'Georgia' },
  { label: 'Gana', value: 'Ghana' },
  { label: 'Granada', value: 'Granada' },
  { label: 'Grécia', value: 'Grecia' },
  { label: 'Guatemala', value: 'Guatemala' },
  { label: 'Guiné', value: 'Guinea' },
  { label: 'Guiné-Bissau', value: 'Guinea-Bisáu' },
  { label: 'Guiné Equatorial', value: 'Guinea Ecuatorial' },
  { label: 'Guiana', value: 'Guyana' },
  { label: 'Haiti', value: 'Haití' },
  { label: 'Honduras', value: 'Honduras' },
  { label: 'Hungria', value: 'Hungría' },
  { label: 'Índia', value: 'India' },
  { label: 'Indonésia', value: 'Indonesia' },
  { label: 'Iraque', value: 'Irak' },
  { label: 'Irã', value: 'Irán' },
  { label: 'Irlanda', value: 'Irlanda' },
  { label: 'Islândia', value: 'Islandia' },
  { label: 'Israel', value: 'Israel' },
  { label: 'Itália', value: 'Italia' },
  { label: 'Jamaica', value: 'Jamaica' },
  { label: 'Japão', value: 'Japón' },
  { label: 'Jordânia', value: 'Jordania' },
  { label: 'Cazaquistão', value: 'Kazajistán' },
  { label: 'Quênia', value: 'Kenia' },
  { label: 'Quirguistão', value: 'Kirguistán' },
  { label: 'Kiribati', value: 'Kiribati' },
  { label: 'Kosovo', value: 'Kosovo' },
  { label: 'Kuwait', value: 'Kuwait' },
  { label: 'Laos', value: 'Laos' },
  { label: 'Lesoto', value: 'Lesoto' },
  { label: 'Letônia', value: 'Letonia' },
  { label: 'Líbano', value: 'Líbano' },
  { label: 'Libéria', value: 'Liberia' },
  { label: 'Líbia', value: 'Libia' },
  { label: 'Liechtenstein', value: 'Liechtenstein' },
  { label: 'Lituânia', value: 'Lituania' },
  { label: 'Luxemburgo', value: 'Luxemburgo' },
  { label: 'Macedônia do Norte', value: 'Macedonia del Norte' },
  { label: 'Madagascar', value: 'Madagascar' },
  { label: 'Malásia', value: 'Malasia' },
  { label: 'Malawi', value: 'Malaui' },
  { label: 'Maldivas', value: 'Maldivas' },
  { label: 'Mali', value: 'Malí' },
  { label: 'Malta', value: 'Malta' },
  { label: 'Marrocos', value: 'Marruecos' },
  { label: 'Ilhas Marshall', value: 'Islas Marshall' },
  { label: 'Maurício', value: 'Mauricio' },
  { label: 'Mauritânia', value: 'Mauritania' },
  { label: 'México', value: 'México' },
  { label: 'Micronésia', value: 'Micronesia' },
  { label: 'Moldávia', value: 'Moldavia' },
  { label: 'Mônaco', value: 'Mónaco' },
  { label: 'Mongólia', value: 'Mongolia' },
  { label: 'Montenegro', value: 'Montenegro' },
  { label: 'Moçambique', value: 'Mozambique' },
  { label: 'Namíbia', value: 'Namibia' },
  { label: 'Nauru', value: 'Nauru' },
  { label: 'Nepal', value: 'Nepal' },
  { label: 'Nicarágua', value: 'Nicaragua' },
  { label: 'Níger', value: 'Níger' },
  { label: 'Nigéria', value: 'Nigeria' },
  { label: 'Noruega', value: 'Noruega' },
  { label: 'Nova Zelândia', value: 'Nueva Zelanda' },
  { label: 'Omã', value: 'Omán' },
  { label: 'Países Baixos', value: 'Países Bajos' },
  { label: 'Palau', value: 'Palaos' },
  { label: 'Panamá', value: 'Panamá' },
  { label: 'Papua-Nova Guiné', value: 'Papúa Nueva Guinea' },
  { label: 'Paquistão', value: 'Pakistán' },
  { label: 'Paraguai', value: 'Paraguay' },
  { label: 'Peru', value: 'Perú' },
  { label: 'Polônia', value: 'Polonia' },
  { label: 'Portugal', value: 'Portugal' },
  { label: 'Catar', value: 'Catar' },
  { label: 'Reino Unido', value: 'Reino Unido' },
  { label: 'República Centro-Africana', value: 'República Centroafricana' },
  { label: 'República Checa', value: 'Chequia' },
  { label: 'República do Congo', value: 'República del Congo' },
  { label: 'República Democrática do Congo', value: 'República Democrática del Congo' },
  { label: 'República Dominicana', value: 'República Dominicana' },
  { label: 'Romênia', value: 'Rumania' },
  { label: 'Ruanda', value: 'Ruanda' },
  { label: 'Rússia', value: 'Rusia' },
  { label: 'Ilhas Salomão', value: 'Islas Salomón' },
  { label: 'Samoa', value: 'Samoa' },
  { label: 'San Marino', value: 'San Marino' },
  { label: 'Santa Lúcia', value: 'Santa Lucía' },
  { label: 'São Cristóvão e Neves', value: 'San Cristóbal y Nieves' },
  { label: 'São Tomé e Príncipe', value: 'Santo Tomé y Príncipe' },
  { label: 'São Vicente e Granadinas', value: 'San Vicente y las Granadinas' },
  { label: 'Senegal', value: 'Senegal' },
  { label: 'Serra Leoa', value: 'Sierra Leona' },
  { label: 'Sérvia', value: 'Serbia' },
  { label: 'Singapura', value: 'Singapur' },
  { label: 'Síria', value: 'Siria' },
  { label: 'Somália', value: 'Somalia' },
  { label: 'Sri Lanka', value: 'Sri Lanka' },
  { label: 'Suazilândia', value: 'Suazilandia' },
  { label: 'Sudão', value: 'Sudán' },
  { label: 'Sudão do Sul', value: 'Sudán del Sur' },
  { label: 'Suécia', value: 'Suecia' },
  { label: 'Suíça', value: 'Suiza' },
  { label: 'Suriname', value: 'Surinam' },
  { label: 'Tailândia', value: 'Tailandia' },
  { label: 'Tajiquistão', value: 'Tayikistán' },
  { label: 'Tanzânia', value: 'Tanzania' },
  { label: 'Timor-Leste', value: 'Timor Oriental' },
  { label: 'Togo', value: 'Togo' },
  { label: 'Tonga', value: 'Tonga' },
  { label: 'Trinidad e Tobago', value: 'Trinidad y Tobago' },
  { label: 'Tunísia', value: 'Túnez' },
  { label: 'Turcomenistão', value: 'Turkmenistán' },
  { label: 'Turquia', value: 'Turquía' },
  { label: 'Tuvalu', value: 'Tuvalu' },
  { label: 'Ucrânia', value: 'Ucrania' },
  { label: 'Uganda', value: 'Uganda' },
  { label: 'Uruguai', value: 'Uruguay' },
  { label: 'Uzbequistão', value: 'Uzbekistán' },
  { label: 'Vanuatu', value: 'Vanuatu' },
  { label: 'Vaticano', value: 'Ciudad del Vaticano' },
  { label: 'Venezuela', value: 'Venezuela' },
  { label: 'Vietnã', value: 'Vietnam' },
  { label: 'Zâmbia', value: 'Zambia' },
  { label: 'Zimbábue', value: 'Zimbabue' },
];

export const COUNTRIES_PH = [
  { value: 'Afganistán', label: 'Afghanistan' },
  { value: 'Albania', label: 'Albania' },
  { value: 'Alemania', label: 'Germany' },
  { value: 'Andorra', label: 'Andorra' },
  { value: 'Angola', label: 'Angola' },
  { value: 'Anguila', label: 'Anguilla' },
  { value: 'Antártida', label: 'Antarctica' },
  { value: 'Antigua y Barbuda', label: 'Antigua and Barbuda' },
  { value: 'Arabia Saudita', label: 'Saudi Arabia' },
  { value: 'Argelia', label: 'Algeria' },
  { value: 'Argentina', label: 'Argentina' },
  { value: 'Armenia', label: 'Armenia' },
  { value: 'Aruba', label: 'Aruba' },
  { value: 'Australia', label: 'Australia' },
  { value: 'Austria', label: 'Austria' },
  { value: 'Azerbaiyán', label: 'Azerbaijan' },
  { value: 'Bahamas', label: 'Bahamas' },
  { value: 'Baréin', label: 'Bahrain' },
  { value: 'Bangladesh', label: 'Bangladesh' },
  { value: 'Barbados', label: 'Barbados' },
  { value: 'Bélgica', label: 'Belgium' },
  { value: 'Belice', label: 'Belize' },
  { value: 'Benín', label: 'Benin' },
  { value: 'Bermudas', label: 'Bermuda' },
  { value: 'Bielorrusia', label: 'Belarus' },
  { value: 'Birmania', label: 'Myanmar' },
  { value: 'Bolivia', label: 'Bolivia' },
  { value: 'Bosnia y Herzegovina', label: 'Bosnia and Herzegovina' },
  { value: 'Botsuana', label: 'Botswana' },
  { value: 'Brasil', label: 'Brazil' },
  { value: 'Brunéi', label: 'Brunei' },
  { value: 'Bulgaria', label: 'Bulgaria' },
  { value: 'Burkina Faso', label: 'Burkina Faso' },
  { value: 'Burundi', label: 'Burundi' },
  { value: 'Bután', label: 'Bhutan' },
  { value: 'Cabo Verde', label: 'Cape Verde' },
  { value: 'Camboya', label: 'Cambodia' },
  { value: 'Camerún', label: 'Cameroon' },
  { value: 'Canadá', label: 'Canada' },
  { value: 'Chad', label: 'Chad' },
  { value: 'Chile', label: 'Chile' },
  { value: 'China', label: 'China' },
  { value: 'Chipre', label: 'Cyprus' },
  { value: 'Ciudad del Vaticano', label: 'Vatican City' },
  { value: 'Colombia', label: 'Colombia' },
  { value: 'Comoras', label: 'Comoros' },
  { value: 'Corea del Norte', label: 'North Korea' },
  { value: 'Corea del Sur', label: 'South Korea' },
  { value: 'Costa de Marfil', label: 'Ivory Coast' },
  { value: 'Costa Rica', label: 'Costa Rica' },
  { value: 'Croacia', label: 'Croatia' },
  { value: 'Cuba', label: 'Cuba' },
  { value: 'Dinamarca', label: 'Denmark' },
  { value: 'Dominica', label: 'Dominica' },
  { value: 'Ecuador', label: 'Ecuador' },
  { value: 'Egipto', label: 'Egypt' },
  { value: 'El Salvador', label: 'El Salvador' },
  { value: 'Emiratos Árabes Unidos', label: 'United Arab Emirates' },
  { value: 'Eritrea', label: 'Eritrea' },
  { value: 'Eslovaquia', label: 'Slovakia' },
  { value: 'Eslovenia', label: 'Slovenia' },
  { value: 'España', label: 'Spain' },
  { value: 'Estados Unidos', label: 'United States' },
  { value: 'Estonia', label: 'Estonia' },
  { value: 'Etiopía', label: 'Ethiopia' },
  { value: 'Filipinas', label: 'Philippines' },
  { value: 'Finlandia', label: 'Finland' },
  { value: 'Fiyi', label: 'Fiji' },
  { value: 'Francia', label: 'France' },
  { value: 'Gabón', label: 'Gabon' },
  { value: 'Gambia', label: 'Gambia' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Ghana', label: 'Ghana' },
  { value: 'Granada', label: 'Grenada' },
  { value: 'Grecia', label: 'Greece' },
  { value: 'Guatemala', label: 'Guatemala' },
  { value: 'Guinea', label: 'Guinea' },
  { value: 'Guinea-Bisáu', label: 'Guinea-Bissau' },
  { value: 'Guinea Ecuatorial', label: 'Equatorial Guinea' },
  { value: 'Guyana', label: 'Guyana' },
  { value: 'Haití', label: 'Haiti' },
  { value: 'Honduras', label: 'Honduras' },
  { value: 'Hungría', label: 'Hungary' },
  { value: 'India', label: 'India' },
  { value: 'Indonesia', label: 'Indonesia' },
  { value: 'Irak', label: 'Iraq' },
  { value: 'Irán', label: 'Iran' },
  { value: 'Irlanda', label: 'Ireland' },
  { value: 'Islandia', label: 'Iceland' },
  { value: 'Israel', label: 'Israel' },
  { value: 'Italia', label: 'Italy' },
  { value: 'Jamaica', label: 'Jamaica' },
  { value: 'Japón', label: 'Japan' },
  { value: 'Jordania', label: 'Jordan' },
  { value: 'Kazajistán', label: 'Kazakhstan' },
  { value: 'Kenia', label: 'Kenya' },
  { value: 'Kirguistán', label: 'Kyrgyzstan' },
  { value: 'Kiribati', label: 'Kiribati' },
  { value: 'Kosovo', label: 'Kosovo' },
  { value: 'Kuwait', label: 'Kuwait' },
  { value: 'Laos', label: 'Laos' },
  { value: 'Lesoto', label: 'Lesotho' },
  { value: 'Letonia', label: 'Latvia' },
  { value: 'Líbano', label: 'Lebanon' },
  { value: 'Liberia', label: 'Liberia' },
  { value: 'Libia', label: 'Libya' },
  { value: 'Liechtenstein', label: 'Liechtenstein' },
  { value: 'Lituania', label: 'Lithuania' },
  { value: 'Luxemburgo', label: 'Luxembourg' },
  { value: 'Macedonia del Norte', label: 'North Macedonia' },
  { value: 'Madagascar', label: 'Madagascar' },
  { value: 'Malasia', label: 'Malaysia' },
  { value: 'Malaui', label: 'Malawi' },
  { value: 'Maldivas', label: 'Maldives' },
  { value: 'Malí', label: 'Mali' },
  { value: 'Malta', label: 'Malta' },
  { value: 'Marruecos', label: 'Morocco' },
  { value: 'Marshall Islands', label: 'Marshall Islands' },
  { value: 'Mauritius', label: 'Mauritius' },
  { value: 'Mauritania', label: 'Mauritania' },
  { value: 'Mexico', label: 'Mexico' },
  { value: 'Micronesia', label: 'Micronesia' },
  { value: 'Moldova', label: 'Moldova' },
  { value: 'Monaco', label: 'Monaco' },
  { value: 'Mongolia', label: 'Mongolia' },
  { value: 'Montenegro', label: 'Montenegro' },
  { value: 'Mozambique', label: 'Mozambique' },
  { value: 'Namibia', label: 'Namibia' },
  { value: 'Nauru', label: 'Nauru' },
  { value: 'Nepal', label: 'Nepal' },
  { value: 'Nicaragua', label: 'Nicaragua' },
  { value: 'Niger', label: 'Niger' },
  { value: 'Nigeria', label: 'Nigeria' },
  { value: 'Norway', label: 'Norway' },
  { value: 'New Zealand', label: 'New Zealand' },
  { value: 'Oman', label: 'Oman' },
  { value: 'Netherlands', label: 'Netherlands' },
  { value: 'Pakistan', label: 'Pakistan' },
  { value: 'Palau', label: 'Palau' },
  { value: 'Panama', label: 'Panama' },
  { value: 'Papua New Guinea', label: 'Papua New Guinea' },
  { value: 'Paraguay', label: 'Paraguay' },
  { value: 'Peru', label: 'Peru' },
  { value: 'Poland', label: 'Poland' },
  { value: 'Portugal', label: 'Portugal' },
  { value: 'Qatar', label: 'Qatar' },
  { value: 'United Kingdom', label: 'United Kingdom' },
  { value: 'Central African Republic', label: 'Central African Republic' },
  { value: 'Czech Republic', label: 'Czech Republic' },
  { value: 'Democratic Republic of the Congo', label: 'Democratic Republic of the Congo' },
  { value: 'Dominican Republic', label: 'Dominican Republic' },
  { value: 'Rwanda', label: 'Rwanda' },
  { value: 'Romania', label: 'Romania' },
  { value: 'Russia', label: 'Russia' },
  { value: 'Samoa', label: 'Samoa' },
  { value: 'Saint Kitts and Nevis', label: 'Saint Kitts and Nevis' },
  { value: 'San Marino', label: 'San Marino' },
  { value: 'Saint Vincent and the Grenadines', label: 'Saint Vincent and the Grenadines' },
  { value: 'Saint Lucia', label: 'Saint Lucia' },
  { value: 'São Tomé and Príncipe', label: 'São Tomé and Príncipe' },
  { value: 'Senegal', label: 'Senegal' },
  { value: 'Serbia', label: 'Serbia' },
  { value: 'Seychelles', label: 'Seychelles' },
  { value: 'Sierra Leone', label: 'Sierra Leone' },
  { value: 'Singapore', label: 'Singapore' },
  { value: 'Syria', label: 'Syria' },
  { value: 'Somalia', label: 'Somalia' },
  { value: 'Sri Lanka', label: 'Sri Lanka' },
  { value: 'Eswatini', label: 'Eswatini' },
  { value: 'South Africa', label: 'South Africa' },
  { value: 'Sudan', label: 'Sudan' },
  { value: 'South Sudan', label: 'South Sudan' },
  { value: 'Sweden', label: 'Sweden' },
  { value: 'Switzerland', label: 'Switzerland' },
  { value: 'Suriname', label: 'Suriname' },
  { value: 'Thailand', label: 'Thailand' },
  { value: 'Tanzania', label: 'Tanzania' },
  { value: 'Tajikistan', label: 'Tajikistan' },
  { value: 'East Timor', label: 'East Timor' },
  { value: 'Togo', label: 'Togo' },
  { value: 'Tonga', label: 'Tonga' },
  { value: 'Trinidad and Tobago', label: 'Trinidad and Tobago' },
  { value: 'Tunisia', label: 'Tunisia' },
  { value: 'Turkmenistan', label: 'Turkmenistan' },
  { value: 'Turkey', label: 'Turkey' },
  { value: 'Tuvalu', label: 'Tuvalu' },
  { value: 'Ukraine', label: 'Ukraine' },
  { value: 'Uganda', label: 'Uganda' },
  { value: 'Uruguay', label: 'Uruguay' },
  { value: 'Uzbekistan', label: 'Uzbekistan' },
  { value: 'Vanuatu', label: 'Vanuatu' },
  { value: 'Venezuela', label: 'Venezuela' },
  { value: 'Vietnam', label: 'Vietnam' },
  { value: 'Yemen', label: 'Yemen' },
  { value: 'Djibouti', label: 'Djibouti' },
  { value: 'Zambia', label: 'Zambia' },
  { value: 'Zimbabwe', label: 'Zimbabwe' },
];

export const TEXT_LOADING = {
  [EXCHANGE_COUNTRY.ARG]: [
    'El gol del Diego contra Inglaterra en 1986, fue elegido como el mejor gol en la historia de los mundiales por la FIFA.',
    'Boca Juniors es el equipo con más títulos internacionales en Argentina, con 18 trofeos.',
    'River Plate venció a Boca Juniors en la final de la Copa Libertadores 2018, un evento histórico jugado en Madrid.',
    'Lionel Messi rompió el récord de Gabriel Batistuta como máximo goleador de la selección argentina con más de 100 goles.',
  ],
  [EXCHANGE_COUNTRY.BRA]: [
    'Pelé marcou 1.283 gols ao longo de sua carreira, um recorde que o coloca como um dos maiores artilheiros da história.',
    'O gol de Ronaldinho contra a Inglaterra na Copa do Mundo de 2002, em uma cobrança de falta distante, ainda é lembrado por sua genialidade.',
    'O Flamengo tem a maior quantidade de torcedores no Brasil, com mais de 42 milhões de seguidores.',
    'O Brasil de 1970 é considerado por muitos especialistas como a melhor seleção da história do futebol, com jogadores como Pelé, Jairzinho e Tostão.',
  ],
  [EXCHANGE_COUNTRY.COL]: [
    'El golazo de James Rodríguez a Uruguay en el Mundial de 2014 fue elegido como el mejor del torneo.',
    'René Higuita, revolucionó el fútbol con su famoso “escorpión”, una jugada icónica realizada en Wembley en 1995.',
    'El Atlético Nacional ganó la Copa Libertadores en 2016, dedicando el título al Chapecoense tras su tragedia aérea.',
    'Carlos Valderrama, con su icónica cabellera, es el jugador colombiano con más asistencias en la historia de la selección.',
  ],
  [EXCHANGE_COUNTRY.CHL]: [
    'El gol de Alexis Sánchez en la tanda de penales contra Argentina en la final de la Copa América 2015 aseguró el primer título de Chile.',
    'Colo Colo es el único equipo chileno que ha ganado la Copa Libertadores, en 1991.',
    'Marcelo Salas, conocido como "El Matador", fue clave en la clasificación de Chile al Mundial de 1998 después de 16 años de ausencia.',
    'La Roja es el apodo que recibe la selección chilena por el color de su camiseta, símbolo de pasión en el país.',
  ],
  [EXCHANGE_COUNTRY.GTM]: [
    'Antigua Guatemala, con su arquitectura colonial y vistas al Volcán de Agua, es uno de los destinos turísticos más visitados del país.',
    'El Lago de Atitlán, rodeado de volcanes y pintorescos pueblos, es considerado uno de los lagos más bellos del mundo.',
    'El sitio arqueológico de Tikal es Patrimonio de la Humanidad y fue una de las ciudades más importantes de la civilización maya.',
    'Semuc Champey, un paraíso natural con piscinas de agua turquesa, es un lugar ideal para los amantes de la naturaleza y la aventura.',
  ],
  [EXCHANGE_COUNTRY.CRI]: [
    'El Parque Nacional Manuel Antonio combina playas paradisíacas con una abundante biodiversidad, ideal para quienes buscan relajarse y explorar.',
    'El Volcán Arenal es un destino icónico en Costa Rica, con termas naturales y senderos rodeados de selva tropical.',
    'Monteverde es conocido por su bosque nuboso, perfecto para caminatas, tirolesas y avistamiento de vida silvestre.',
    'El Parque Nacional Tortuguero es famoso por ser un lugar clave para el desove de tortugas marinas y sus canales rodeados de naturaleza.',
  ],
  [EXCHANGE_COUNTRY.PAN]: [
    'El Canal de Panamá, una de las maravillas de la ingeniería moderna, es una visita obligada para entender la historia y economía del país.',
    'El Casco Antiguo de la Ciudad de Panamá, con su arquitectura colonial y vibrante vida nocturna, es Patrimonio de la Humanidad.',
    'El Archipiélago de San Blas, hogar de la comunidad indígena Guna, ofrece playas vírgenes y una rica cultura autóctona.',
    'Bocas del Toro es un destino perfecto para practicar snorkel, buceo y disfrutar de sus coloridas casas sobre el agua.',
  ],
  [EXCHANGE_COUNTRY.MEX]: [
    'El gol de Manuel Negrete contra Bulgaria en el Mundial 1986 fue elegido como el mejor gol en la historia de los mundiales en votaciones públicas.',
    'Rafael Márquez es uno de los tres jugadores en la historia que han jugado en cinco Copas del Mundo.',
    'Chivas y América han protagonizado el Clásico Nacional desde 1943, el partido más esperado cada temporada.',
    'El Estadio Azteca es el único en la historia que ha albergado dos finales de la Copa del Mundo, en 1970 y 1986.',
  ],
};

const validMunicipalityCodes = [
  '0101',
  '0102',
  '0103',
  '0104',
  '0105',
  '0106',
  '0107',
  '0108',
  '0109',
  '0110',
  '0111',
  '0112',
  '0113',
  '0114',
  '0115',
  '0116',
  '0117',
  '0201',
  '0202',
  '0203',
  '0204',
  '0205',
  '0206',
  '0207',
  '0208',
  '0301',
  '0302',
  '0303',
  '0304',
  '0305',
  '0306',
  '0307',
  '0308',
  '0309',
  '0310',
  '0311',
  '0312',
  '0313',
  '0314',
  '0315',
  '0316',
  '0317',
  '0318',
  '0401',
  '0402',
  '0403',
  '0404',
  '0405',
  '0406',
  '0407',
  '0408',
  '0409',
  '0410',
  '0411',
  '0412',
  '0413',
  '0414',
  '0415',
  '0416',
  '0417',
  '0418',
  '0419',
  '0501',
  '0502',
  '0503',
  '0504',
  '0505',
  '0506',
  '0507',
  '0508',
  '0509',
  '0510',
  '0511',
  '0512',
  '0513',
  '0514',
  '0515',
  '0516',
  '0517',
  '0518',
  '0519',
  '0520',
  '0601',
  '0602',
  '0603',
  '0604',
  '0605',
  '0606',
  '0607',
  '0608',
  '0609',
  '0610',
  '0611',
  '0612',
  '0613',
  '0614',
  '0615',
  '0616',
  '0617',
  '0618',
  '0619',
  '0701',
  '0702',
  '0703',
  '0704',
  '0705',
  '0706',
  '0707',
  '0708',
  '0709',
  '0710',
  '0711',
  '0712',
  '0713',
  '0714',
  '0715',
  '0716',
  '0717',
  '0718',
  '0719',
  '0720',
  '0721',
  '0801',
  '0802',
  '0803',
  '0804',
  '0805',
  '0806',
  '0807',
  '0808',
  '0809',
  '0810',
  '0811',
  '0812',
  '0813',
  '0814',
  '0815',
  '0816',
  '0817',
  '0818',
  '0819',
  '0901',
  '0902',
  '0903',
  '0904',
  '0905',
  '0906',
  '0907',
  '0908',
  '0909',
  '0910',
  '0911',
  '0912',
  '0913',
  '0914',
  '0915',
  '0916',
  '0917',
  '0918',
  '0919',
  '0920',
  '1001',
  '1002',
  '1003',
  '1004',
  '1005',
  '1006',
  '1007',
  '1008',
  '1009',
  '1010',
  '1011',
  '1012',
  '1013',
  '1014',
  '1015',
  '1016',
  '1017',
  '1018',
  '1019',
  '1020',
  '1101',
  '1102',
  '1103',
  '1104',
  '1105',
  '1106',
  '1107',
  '1108',
  '1109',
  '1110',
  '1111',
  '1112',
  '1113',
  '1114',
  '1115',
  '1116',
  '1117',
  '1118',
  '1119',
  '1120',
  '1201',
  '1202',
  '1203',
  '1204',
  '1205',
  '1206',
  '1207',
  '1208',
  '1209',
  '1210',
  '1211',
  '1212',
  '1213',
  '1214',
  '1215',
  '1216',
  '1217',
  '1218',
  '1219',
  '1220',
  '1301',
  '1302',
  '1303',
  '1304',
  '1305',
  '1306',
  '1307',
  '1308',
  '1309',
  '1310',
  '1311',
  '1312',
  '1313',
  '1314',
  '1315',
  '1316',
  '1317',
  '1318',
  '1319',
  '1320',
  '1401',
  '1402',
  '1403',
  '1404',
  '1405',
  '1406',
  '1407',
  '1408',
  '1409',
  '1410',
  '1411',
  '1412',
  '1413',
  '1414',
  '1415',
  '1416',
  '1417',
  '1418',
  '1419',
  '1420',
  '1501',
  '1502',
  '1503',
  '1504',
  '1505',
  '1506',
  '1507',
  '1508',
  '1509',
  '1510',
  '1511',
  '1512',
  '1513',
  '1514',
  '1515',
  '1516',
  '1517',
  '1518',
  '1519',
  '1520',
  '1601',
  '1602',
  '1603',
  '1604',
  '1605',
  '1606',
  '1607',
  '1608',
  '1609',
  '1610',
  '1611',
  '1612',
  '1613',
  '1614',
  '1615',
  '1616',
  '1617',
  '1618',
  '1619',
  '1620',
  '1701',
  '1702',
  '1703',
  '1704',
  '1705',
  '1706',
  '1707',
  '1708',
  '1709',
  '1710',
  '1711',
  '1712',
  '1713',
  '1714',
  '1715',
  '1716',
  '1717',
  '1718',
  '1719',
  '1720',
  '1801',
  '1802',
  '1803',
  '1804',
  '1805',
  '1901',
  '1902',
  '1903',
  '1904',
  '1905',
  '1906',
  '1907',
  '1908',
  '1909',
  '1910',
  '2001',
  '2002',
  '2003',
  '2004',
  '2005',
  '2006',
  '2007',
  '2008',
  '2009',
  '2010',
  '2011',
  '2101',
  '2102',
  '2103',
  '2104',
  '2105',
  '2106',
  '2107',
  '2201',
  '2202',
  '2203',
  '2204',
  '2205',
  '2206',
  '2207',
  '2208',
  '2209',
  '2210',
  '2211',
  '2212',
  '2213',
  '2214',
  '2215',
  '2216',
  '2217',
];

export enum TYPE_MINIAPP {
  PAY = 'PAY',
  ERROR = 'ERROR',
  REDIRECT = 'REDIRECT',
}
